/**
 * Algolia Collection Page Integration v2.0
 * Hijacks Shopify collection filtering and replaces with Algolia
 * Maintains existing theme UI while powering search with Algolia
 */

class AlgoliaCollectionPage {
  // Default Shopify filter param to Algolia attribute mapping
  // These can be extended/overridden via algoliaConfig.facets.attributeMapping
  static DEFAULT_FILTER_MAPPING = {
    'filter.v.option.color': 'options.color',
    'filter.v.option.size': 'options.size',
    'filter.v.option.material': 'options.material',
    'filter.v.option.style': 'options.style',
    'filter.v.option.length': 'options.length',
    'filter.v.option.width': 'options.width',
    'filter.p.vendor': 'vendor',
    'filter.p.product_type': 'product_type',
    'filter.p.tag': 'tags',
    'filter.v.availability': 'inventory_available',
  };

  constructor() {
    // Only activate on collection pages
    if (!this.isCollectionPage()) {
      this.log('Not a collection page, skipping initialization');
      return;
    }

    // Validate configuration
    if (!window.algoliaConfig || !window.algoliaConfig.enabled) {
      this.log('Algolia not enabled');
      return;
    }

    if (!window.algoliaConfig.features?.collectionFilters) {
      this.log('Collection filters not enabled');
      return;
    }

    // Store configuration
    this.config = window.algoliaConfig;
    this.themeConfig = this.config.theme || {};

    // Current filter state - used to inject filters on each search
    this.currentFilters = [];
    // Numeric range filters - applied via helper.addNumericRefinement for proper Algolia numeric filtering
    this.numericRangeFilters = new Map();

    // Get collection handle
    this.collectionHandle = this.getCollectionHandle();
    if (!this.collectionHandle) {
      console.warn('[Algolia Collection] Could not determine collection handle');
      return;
    }

    // Initialize search client
    const baseClient = window.AlgoliaShared.excludeUnlisted(
      algoliasearch(this.config.appId, this.config.apiKey)
    );
    this.searchClient = window.algoliaLogger
      ? window.algoliaLogger.wrapSearchClient(baseClient, 'CollectionPage')
      : baseClient;

    // Initialize Algolia Insights for click/conversion analytics
    this.initInsights();

    // Track state
    this.currentSort = this.getCurrentSort();
    this.currentLayout = 'grid';
    this.isInitialized = false;
    this.pendingFilters = null;

    // Redirect rules for the search box query, followed only on Enter
    this.searchRedirect = null;
    this.lastResponseQuery = null;
    this.redirectOnResponse = null;

    // Timer references for cleanup
    this._searchDebounceTimer = null;
    this._rangeDebounceTimer = null;
    this._scrollTimer = null;

    // Set by filter/search call sites; consumed in render callback so scroll
    // fires after the active-filter chip strip and grid have re-laid out.
    this._scrollOnNextRender = false;

    // Price slider scale configuration
    // 'linear' = traditional linear scale
    // 'sqrt' = square root scale (compresses high values, good for prices with outliers)
    // 'log' = logarithmic scale (strong compression for extreme ranges)
    // Using linear for now until weighted scale is fully tested
    this.priceSliderScale = 'linear';

    // Cache DOM references
    this.cacheDOM();

    // Initialize
    this.init();
  }

  /**
   * Check if current page is a collection page
   */
  isCollectionPage() {
    const pageContext = window.algoliaConfig?.pageContext;
    return (
      pageContext?.isCollection ||
      document.body.classList.contains('template-collection') ||
      document.querySelector('[data-template="collection"]') !== null
    );
  }

  /**
   * Get collection handle from config or URL
   */
  getCollectionHandle() {
    // Try page context first
    if (this.config?.pageContext?.collectionHandle) {
      return this.config.pageContext.collectionHandle;
    }

    // Extract from URL
    const pathParts = window.location.pathname.split('/');
    const collectionIndex = pathParts.indexOf('collections');
    if (collectionIndex !== -1 && pathParts[collectionIndex + 1]) {
      return pathParts[collectionIndex + 1];
    }

    return null;
  }

  /**
   * Whether discounted sibling variants should be boosted to win the
   * distinct-variant tie, either via collection metafield opt-in or
   * because the collection handle is an on-sale collection.
   *
   * The boost scores at Algolia's `filters` ranking criterion, which orders
   * products as well as picking each product's representative: every product
   * with an on_sale variant ranks above every product without one, under
   * every sort (the sort replicas are virtual, so their sort applies below
   * `filters`). On a sale collection that is a no-op because every product
   * matches. The `boost_sale_variants` metafield is therefore only for sale
   * collections whose handle does not follow the `-on-sale` convention;
   * opting in a mixed collection reorders its whole grid sale-first.
   */
  shouldBoostSaleVariants() {
    const optIn = this.config?.pageContext?.boostSaleVariants;
    return (
      optIn === true ||
      optIn === 'true' ||
      this.collectionHandle === 'on-sale' ||
      Boolean(this.collectionHandle?.endsWith('-on-sale'))
    );
  }

  /**
   * Initialize Algolia Insights for click and conversion tracking
   */
  initInsights() {
    // Always set userToken (needed for InstantSearch insights config even if full analytics disabled)
    this.userToken = window.AlgoliaShared?.getUserToken() || this.getFallbackUserToken();

    // Check if analytics is enabled
    if (!this.config.features?.analytics) {
      this.log('Analytics disabled, skipping Insights initialization');
      return;
    }

    // Check if already initialized by another component
    if (window.AlgoliaShared?.isInsightsInitialized()) {
      this.insightsInitialized = true;
      this.log('Algolia Insights already initialized, reusing. User token:', this.userToken);
      return;
    }

    // Try to initialize via shared utility
    if (window.AlgoliaShared?.initInsights(this.config)) {
      this.insightsInitialized = true;
      this.log('Algolia Insights initialized via AlgoliaShared with user token:', this.userToken);
      return;
    }

    // Fallback: library not loaded yet - wait and retry
    this.log('Algolia Insights not available, will retry...');
    this.retryInsightsInit();
  }

  /**
   * Retry Insights initialization after a delay
   */
  retryInsightsInit(attempts = 0) {
    if (attempts >= 15) {
      this.log('Failed to initialize Algolia Insights after 15 attempts');
      return;
    }

    setTimeout(() => {
      if (window.AlgoliaShared?.initInsights(this.config)) {
        this.insightsInitialized = true;
        this.log('Algolia Insights initialized on retry', attempts + 1);

        // Hits may have already rendered while we were retrying —
        // wire up click tracking now that insights is ready
        if (this.lastHits?.length) {
          this.setupClickTracking(this.lastHits);
        }
      } else {
        this.retryInsightsInit(attempts + 1);
      }
    }, 200);
  }

  /**
   * Fallback user token if AlgoliaShared not available
   */
  getFallbackUserToken() {
    // Priority 1: Customer ID from algoliaConfig (stable across email changes)
    if (window.algoliaConfig?.userToken) {
      return window.algoliaConfig.userToken;
    }
    // Priority 2: Anonymous localStorage token
    const storageKey = 'algolia_user_token';
    let token = localStorage.getItem(storageKey);
    if (!token) {
      token = 'user_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
      localStorage.setItem(storageKey, token);
    }
    return token;
  }

  /**
   * Track product click event
   * @param {Object} product - The product that was clicked
   * @param {number} position - Position in the results (1-indexed)
   * @param {string} queryID - The Algolia queryID from the search response
   */
  trackProductClick(product, position, queryID) {
    const clickedObjectID = product.objectID || product.id?.toString() || '';
    if (clickedObjectID && typeof gtag === 'function') {
      gtag('event', 'search_result_click', {
        object_id: clickedObjectID,
        position: position,
        query: this.search?.helper?.state?.query || '',
        index: this.getCurrentIndexName(),
        surface: 'collection',
      });
    }

    if (!this.insightsInitialized || !this.config.features?.analytics) {
      return;
    }

    // Get the objectID - prefer the original Algolia objectID
    const objectID = product.objectID || product.id?.toString() || '';
    if (!objectID) {
      this.log('Cannot track click: no objectID');
      return;
    }

    try {
      const effectiveQueryID = queryID || this.lastQueryID;

      // Use AlgoliaAnalytics.default directly for reliable event sending
      const insights = window.AlgoliaAnalytics?.default;

      if (effectiveQueryID) {
        // Search-attributed click - requires queryID
        if (insights) {
          insights.clickedObjectIDsAfterSearch({
            index: this.getCurrentIndexName(),
            eventName: 'Product Clicked',
            queryID: effectiveQueryID,
            objectIDs: [objectID],
            positions: [position],
          });
        } else if (typeof aa === 'function') {
          aa('clickedObjectIDsAfterSearch', {
            index: this.getCurrentIndexName(),
            eventName: 'Product Clicked',
            queryID: effectiveQueryID,
            objectIDs: [objectID],
            positions: [position],
            userToken: this.userToken,
          });
        }

        // Store for conversion tracking via Shopify Web Pixel
        this.storeConversionData(effectiveQueryID, objectID);

        // Also store in cart attribution for line item properties (primary method)
        if (window.AlgoliaCartAttribution) {
          const state = this.search?.helper?.state;
          window.AlgoliaCartAttribution.store({
            queryID: effectiveQueryID,
            objectID: objectID,
            handle: product.handle,
            indexName: this.getCurrentIndexName(),
            position: position,
            query: state?.query || '',
            filters: this.getFiltersString(state)
          });
        }

        this.log('Tracked click (search):', { objectID, position, queryID: effectiveQueryID });
      } else {
        // Non-search click - no queryID available
        if (insights) {
          insights.clickedObjectIDs({
            index: this.getCurrentIndexName(),
            eventName: 'Product Clicked',
            objectIDs: [objectID],
          });
        } else if (typeof aa === 'function') {
          aa('clickedObjectIDs', {
            index: this.getCurrentIndexName(),
            eventName: 'Product Clicked',
            objectIDs: [objectID],
            userToken: this.userToken,
          });
        }

        this.log('Tracked click (non-search):', { objectID, position });
      }
    } catch (error) {
      console.warn('[Algolia] Failed to track click:', error);
    }
  }

  /**
   * Store conversion data for Shopify Web Pixel
   * The pixel runs in checkout sandbox — we store in localStorage only
   * (cookie fallback removed to prevent 431 "cookie too large" errors)
   * @param {string} queryID - The Algolia queryID from search
   * @param {string} objectID - The product objectID
   */
  storeConversionData(queryID, objectID) {
    if (!queryID || !objectID) return;

    try {
      const storageKey = '_algolia_conversion';
      let existingData = { items: [] };

      // Get existing data from localStorage
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          existingData = JSON.parse(stored);
        }
      } catch (_e) {
        // localStorage not available, start fresh
      }

      // Add new item (dedupe by objectID, keep last N items)
      const maxItems = window.AlgoliaShared?.MAX_CONVERSION_ITEMS || 20;
      existingData.items = existingData.items.filter(item => item.objectID !== objectID);
      existingData.items.unshift({
        queryID,
        objectID,
        indexName: this.getCurrentIndexName(),
        timestamp: Date.now()
      });
      existingData.items = existingData.items.slice(0, maxItems);

      const dataStr = JSON.stringify(existingData);

      // Store in localStorage (Shopify Web Pixel can access via browser.localStorage.getItem)
      try {
        localStorage.setItem(storageKey, dataStr);
      } catch (_e) {
        // localStorage not available
      }

      // Expire any leftover _algolia_conversion cookie from previous versions
      document.cookie = '_algolia_conversion=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';

      this.log('Stored conversion data:', { queryID, objectID });
    } catch (e) {
      console.warn('[Algolia] Failed to store conversion data:', e);
    }
  }

  /**
   * Track filter click event
   * @param {string} facet - The facet name
   * @param {string} value - The filter value
   */
  trackFilterClick(facet, value) {
    if (typeof gtag === 'function') {
      gtag('event', 'facet_apply', { attribute: facet, value: value, surface: 'collection' });
    }

    if (!this.insightsInitialized || !this.config.features?.analytics) {
      return;
    }

    try {
      aa('clickedFilters', {
        index: this.getCurrentIndexName(),
        eventName: 'Filter Applied',
        filters: [`${facet}:${value}`],
        userToken: this.userToken,
      });
      this.log('Tracked filter click:', { facet, value });
    } catch (error) {
      console.warn('[Algolia] Failed to track filter click:', error);
    }
  }

  /**
   * Get current index name based on sort
   */
  getCurrentIndexName() {
    const sortConfig = this.config.sortIndices?.find((s) => s.value === this.currentSort);
    const suffix = sortConfig?.index || '';
    return this.config.indexName + suffix;
  }

  /**
   * Convert Algolia helper state filters to a compact string
   * Uses shared utility from AlgoliaShared
   */
  getFiltersString(state) {
    return window.AlgoliaShared?.getFiltersString(state) || '';
  }

  /**
   * Set up click tracking on product cards
   * @param {Array} hits - The product hits from Algolia
   */
  setupClickTracking(hits) {
    if (!this.insightsInitialized || !this.config.features?.analytics || !hits?.length) {
      return;
    }

    // Use event delegation on the product grid for better performance
    const productGrid = this.dom.productGrid || document.getElementById('ProductGridContainer');
    if (!productGrid) return;

    // Remove existing listener to prevent duplicates
    if (this._clickTrackingHandler) {
      productGrid.removeEventListener('click', this._clickTrackingHandler);
    }

    // Create handler
    this._clickTrackingHandler = (event) => {
      // Find the closest product card link
      const link = event.target.closest('a[href*="/products/"]');
      if (!link) return;

      // Find the product card container
      const productCard = link.closest('.product-card');
      if (!productCard) return;

      // Get the product handle from the card or link
      const productHandle = productCard.dataset.productHandle ||
                           link.getAttribute('href')?.split('/products/')?.[1]?.split('?')?.[0];
      if (!productHandle) return;

      // Find the hit data for this product
      const hitIndex = hits.findIndex((hit) =>
        hit.handle === productHandle ||
        hit.objectID?.includes(productHandle)
      );

      if (hitIndex === -1) {
        this.log('Could not find hit for product:', productHandle);
        return;
      }

      const hit = hits[hitIndex];
      const position = hitIndex + 1; // 1-indexed position

      // Track the click
      this.trackProductClick(hit, position, this.lastQueryID);
    };

    // Add listener
    productGrid.addEventListener('click', this._clickTrackingHandler);
    this.log('Click tracking set up for', hits.length, 'products');
  }

  /**
   * Get current sort order
   */
  getCurrentSort() {
    // Check URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const urlSort = urlParams.get('sort_by');
    if (urlSort) return urlSort;

    // Check select value
    const sortSelect = document.querySelector('select[is="facet-short"]');
    if (sortSelect?.value) return sortSelect.value;

    // Use configured defaults
    if (this.config?.defaultSort?.collection) {
      return this.config.defaultSort.collection;
    }
    if (this.config?.defaultSort?.global) {
      return this.config.defaultSort.global;
    }

    return 'manual';
  }

  /**
   * Cache DOM element references
   */
  cacheDOM() {
    this.dom = {
      facetForms: document.querySelectorAll('form[is="facet-form"]'),
      productGrid: document.getElementById('ProductGridContainer'),
      productsList: document.getElementById('ProductsList'),
      productCount: document.getElementById('ProductCount'),
      pagination: document.querySelector('.pagination'),
      sortSelect: document.querySelector('select[is="facet-short"]'),
      sortSelectMobile: document.querySelector('#DrawerFacetFiltersContainer select[is="facet-short"]'),
      activeFilters: document.getElementById('FacetFiltersActive'),
      filterContainers: {},
      drawerFilters: {},
    };

    // Cache filter containers by their data-index
    document.querySelectorAll('#FacetFiltersContainer [data-filter][data-index]').forEach((el) => {
      this.dom.filterContainers[el.dataset.index] = el;
    });

    // Cache drawer filter containers
    document.querySelectorAll('#DrawerFacetFiltersContainer [data-filter][data-index]').forEach((el) => {
      this.dom.drawerFilters[el.dataset.index] = el;
    });

    this.log('DOM cached:', {
      facetForms: this.dom.facetForms.length,
      productGrid: !!this.dom.productGrid,
      filterCount: Object.keys(this.dom.filterContainers).length,
    });
  }

  /**
   * Initialize Algolia collection search
   */
  init() {
    this.log('Initializing collection page:', this.collectionHandle);

    window.AlgoliaShared?.manageScrollRestoration();

    // Set up InstantSearch
    this.setupInstantSearch();

    // Set up connectors - pagination FIRST to determine if we use infinite hits
    this.setupPaginationConnector();
    this.setupHitsConnector();
    this.setupStatsConnector();
    this.setupCollectionSearchBox();

    // Hijack the facet form
    this.hijackFacetForm();

    // Hijack filter removal buttons (×, Clear All)
    this.hijackFilterRemoval();

    // Set up event delegation for Algolia facets (handles dynamically rendered elements)
    this.setupFacetEventDelegation();

    // Remember which facet groups the shopper expands/collapses
    this._bindFacetAccordionPersistence();

    // Wire sorting
    this.wireSorting();

    // Detect layout
    this.detectLayout();

    // Parse initial URL filters
    this.parseInitialFilters();

    // Add middleware to expand accordions with active refinements
    this.search.use(this.createAccordionExpansionMiddleware());

    // Add middleware to track pagination state changes for debugging
    this.search.use(this.createPaginationDebugMiddleware());

    // Add middleware to dim the grid the moment state changes (before the
    // network request fires), so the click-to-next-paint window (INP) is
    // bounded by dim-class-apply + paint, not by network RTT + grid re-render.
    // Pairs with hideLoadingState() at the end of renderHits.
    this.search.use(this.createLoadingStateMiddleware());

    // Start InstantSearch
    this.search.start();

    // Fetch unfiltered range bounds (async, in background)
    // This ensures sliders show full range even when page loads with filters
    this.fetchUnfilteredRangeBounds();

    // Mark body as Algolia-active for CSS targeting
    document.body.classList.add('algolia-active');

    // Calculate and set toolbar height for sticky positioning
    this.setToolbarHeightVariable();

    this.isInitialized = true;
    this.log('Collection page initialized');
  }

  /**
   * Calculate toolbar height and set as CSS variable for sticky positioning.
   * Uses ResizeObserver for efficient updates instead of resize event listener.
   */
  setToolbarHeightVariable() {
    const toolbar = document.querySelector('#CollectionToolbar');
    if (!toolbar) return;

    const updateHeight = () => {
      const height = toolbar.offsetHeight;
      this._cachedToolbarHeight = height;
      document.documentElement.style.setProperty('--toolbar-height', `${height}px`);
      this._cacheScrollMeasurements();
    };

    // Set initial height
    updateHeight();

    // Use ResizeObserver for efficient toolbar height tracking
    if (typeof ResizeObserver !== 'undefined') {
      this._toolbarResizeObserver = new ResizeObserver(updateHeight);
      this._toolbarResizeObserver.observe(toolbar);
    } else {
      window.addEventListener('resize', updateHeight);
    }
  }

  /**
   * Cache the layout measurements scrollToProductGrid() / calculateDynamicScrollOffset()
   * need, so the per-tap scroll (facet / sort / pagination) doesn't force a synchronous
   * style+layout read on the input task. Refreshed OFF the input task: on toolbar resize
   * (ResizeObserver) and after each render (renderHits idle block). The header CSS vars
   * are height values that only change on resize/content — the dynamic pin / nav-hidden
   * state is still read live from body classes in calculateDynamicScrollOffset().
   */
  _cacheScrollMeasurements() {
    const chip = document.querySelector('.facets-active-wrapper');
    this._cachedChipStripHeight = chip ? chip.offsetHeight : 0;
    const style = getComputedStyle(document.documentElement);
    this._cachedHeaderHeight = parseFloat(style.getPropertyValue('--header-height')) || 0;
    this._cachedNavHeight = parseFloat(style.getPropertyValue('--header-navigation-height')) || 0;
  }

  /**
   * Fetch unfiltered range bounds for all range facets
   * This is called once at initialization to get the full min/max values
   * without any filters applied, ensuring sliders always show the complete range.
   */
  async fetchUnfilteredRangeBounds() {
    // Only needed if we have range facets and the page has URL filters
    if (!this.rangeFacets || this.rangeFacets.length === 0) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const hasUrlFilters = urlParams.toString().length > 0;

    if (!hasUrlFilters) {
      this.log('No URL filters - will use stats from main search for range bounds');
      return;
    }

    this.log('Page has URL filters - fetching unfiltered range bounds');

    try {
      // Create a temporary search client query with NO filters to get full stats
      const client = window.AlgoliaShared.excludeUnlisted(
        algoliasearch(this.config.appId, this.config.apiKey)
      );
      const index = client.initIndex(this.config.indexName);

      // Build the collection filter (always needed to scope to this collection)
      const collectionFilter = `collections:${this.collectionHandle}`;

      // Query with facets for stats but NO range filters
      const response = await index.search('', {
        filters: collectionFilter,
        facets: this.rangeFacets,
        hitsPerPage: 0, // We don't need hits, just stats
        analytics: false, // Don't count this as a search
      });

      if (response.facets_stats) {
        // Initialize cache if needed
        if (!this.rangeFacetBounds) {
          this.rangeFacetBounds = {};
        }

        // Cache the unfiltered bounds for each range facet
        for (const attribute of this.rangeFacets) {
          const stats = response.facets_stats[attribute];
          if (stats) {
            const min = Math.floor(stats.min || 0);
            const max = Math.ceil(stats.max || 1000);

            // Only update if we don't have bounds OR the new values are wider
            if (!this.rangeFacetBounds[attribute]) {
              this.rangeFacetBounds[attribute] = { min, max };
              this.log(`Pre-fetched range bounds for ${attribute}: ${min} - ${max}`);
            } else {
              // Expand if wider
              if (min < this.rangeFacetBounds[attribute].min) {
                this.rangeFacetBounds[attribute].min = min;
              }
              if (max > this.rangeFacetBounds[attribute].max) {
                this.rangeFacetBounds[attribute].max = max;
              }
              this.rangeFacetBounds[attribute].isFiltered = false;
              this.log(`Updated range bounds for ${attribute}: ${this.rangeFacetBounds[attribute].min} - ${this.rangeFacetBounds[attribute].max}`);
            }
          }
        }

        // Trigger a re-render of facets to update the sliders with correct bounds
        // Only if we actually fetched new bounds
        if (Object.keys(this.rangeFacetBounds).length > 0) {
          this.log('Re-rendering facets with pre-fetched range bounds');
          // Get current results from the search helper
          if (this.search?.helper?.lastResults) {
            this.updateFacetCounts(this.search.helper.lastResults);
          }
        }
      }
    } catch (error) {
      this.log('Error fetching unfiltered range bounds:', error);
      // Non-fatal - sliders will still work, just with filtered bounds initially
    }
  }

  /**
   * Set up InstantSearch instance
   */
  setupInstantSearch() {
    const indexName = this.getIndexForSort(this.currentSort);
    const self = this;

    this.log('Setting up InstantSearch:');
    this.log('  - Base index:', this.config.indexName);
    this.log('  - Current sort:', this.currentSort);
    this.log('  - Using index:', indexName);

    // Build facet lists from the new facet config (algolia-facet-config.liquid)
    // Falls back to old algoliaConfig.facets if new config not present
    const newFacetConfig = this.getFacetConfig();
    const oldFacetConfig = this.config.facets || {};

    // Extract disjunctive facets from new config (all non-range facets are disjunctive)
    // Also extract range facets for numeric filtering (price, height, width, etc.)
    if (newFacetConfig.facets && newFacetConfig.facets.length > 0) {
      this.disjunctiveFacets = newFacetConfig.facets
        .filter((f) => f.type !== 'range')
        .map((f) => f.attribute);

      // Range facets need to be requested separately to get facet_stats
      this.rangeFacets = newFacetConfig.facets
        .filter((f) => f.type === 'range')
        .map((f) => f.attribute);
    } else {
      // Fallback to old config
      this.disjunctiveFacets = oldFacetConfig.disjunctive || [
        'vendor',
        'product_type',
        'tags',
      ];
      this.rangeFacets = ['price']; // Default range facet
    }

    this.conjunctiveFacets = oldFacetConfig.conjunctive || [
      'inventory_available',
    ];
    this.facetAttributeMapping = oldFacetConfig.attributeMapping || {};
    this.maxValuesPerFacet = oldFacetConfig.maxValuesPerFacet || 100;

    // Build SEO-friendly URL param mapping from facet config
    // Maps: urlParam -> attribute and attribute -> urlParam
    this.urlParamToAttribute = {};
    this.attributeToUrlParam = {};
    if (newFacetConfig.facets) {
      newFacetConfig.facets.forEach((f) => {
        if (f.urlParam && f.attribute) {
          this.urlParamToAttribute[f.urlParam] = f.attribute;
          this.attributeToUrlParam[f.attribute] = f.urlParam;
        }
      });
    }
    this.log('  - URL param mapping:', this.urlParamToAttribute);

    this.log('Facet configuration loaded:');
    this.log('  - Disjunctive (OR):', this.disjunctiveFacets);
    this.log('  - Range (min/max):', this.rangeFacets);
    this.log('  - Conjunctive (AND):', this.conjunctiveFacets);
    this.log('  - Max values per facet:', this.maxValuesPerFacet);

    // Track disjunctive refinements separately
    // Format: { facet: [value1, value2], ... }
    this.disjunctiveRefinements = {};

    // Create search client wrapper to handle redirect rules
    const redirectAwareClient = {
      ...this.searchClient,
      search: async (requests) => {
        // Algolia rejects queries longer than 512 bytes with an uncaught ApiError.
        // The collection search box reads its initial query from the `q` URL param,
        // which bypasses any input maxlength, so clamp every request's query to
        // <= 512 UTF-8 bytes here — the single point all collection search requests
        // pass through. Mirrors algolia-search-page.js. See SHOPIFY-127.
        const enc = new TextEncoder();
        const clampedRequests = Array.isArray(requests)
          ? requests.map((req) => {
              const q = req?.params?.query;
              if (typeof q !== 'string' || enc.encode(q).length <= 512) return req;
              let clamped = q.slice(0, 512);
              while (enc.encode(clamped).length > 512) clamped = clamped.slice(0, -1);
              return { ...req, params: { ...req.params, query: clamped } };
            })
          : requests;
        const results = await self.searchClient.search(clampedRequests);
        // Check for Algolia redirect rule - if matched, redirect immediately
        // Redirect rules are configured in Algolia Dashboard → Rules
        // Visual Editor stores in renderingContent.redirect, Manual Editor in userData
        const firstResult = results.results?.[0];
        const redirectUrl = firstResult?.renderingContent?.redirect?.url
          || firstResult?.userData?.[0]?.redirect
          || firstResult?.redirect?.url;
        // The collection search box searches as the shopper types, so a
        // redirect rule is followed only on Enter (setupCollectionSearchBox).
        const responseQuery = (firstResult?.query || '').trim();
        self.lastResponseQuery = responseQuery.toLowerCase();
        self.searchRedirect = redirectUrl
          ? { query: responseQuery, url: redirectUrl, resultsCount: firstResult.nbHits }
          : null;
        if (self.redirectOnResponse !== null && self.redirectOnResponse === self.lastResponseQuery) {
          self.redirectOnResponse = null;
          if (self.searchRedirect) {
            self.followSearchRedirect(self.searchRedirect);
            // Never settle: any render now (even of emptied results) paints the
            // no-results grid while the browser is leaving
            return new Promise(() => {});
          }
        }
        return results;
      }
    };

    // Use searchFunction to inject dynamic filters before each search
    this.search = instantsearch({
      indexName,
      searchClient: redirectAwareClient,
      routing: false, // We handle URL ourselves
      // Re-enable insights - disabling it was breaking pagination
      // The insights middleware may be involved in state synchronization
      insights: this.config.features?.analytics !== false ? { insightsClient: window.AlgoliaShared.getInsightsClient() } : false,
      searchFunction: function(helper) {
        // Apply collection filter as base filter
        // Note: "all" is a virtual Shopify collection not indexed in Algolia,
        // so we skip the collection filter for /collections/all to show ALL products
        // Other filters (non-disjunctive like availability) added here too
        const baseFilters = self.collectionHandle === 'all'
          ? [...self.currentFilters]
          : [`collections:${self.collectionHandle}`, ...self.currentFilters];
        const filterString = baseFilters.length > 0 ? baseFilters.join(' AND ') : '';

        // CRITICAL: Get page from the MAIN helper, not the derived helper passed to searchFunction
        // The pagination widget updates the main helper (self.search.helper), but searchFunction
        // receives a derived helper whose state.page is always 0.
        // See: https://www.algolia.com/doc/api-reference/widgets/instantsearch/js/#widget-param-searchfunction
        let currentPage = self.search.helper?.state?.page ?? helper.state.page ?? 0;
        if (self.initialPage !== undefined && self.initialPage !== null) {
          currentPage = self.initialPage;
          self.initialPage = null; // Clear after first use
          self.log('Using initial page from URL:', currentPage + 1);
        }

        self.log('searchFunction called:');
        self.log('  - Index:', helper.state.index);
        self.log('  - Page:', currentPage);
        self.log('  - hitsPerPage:', helper.state.hitsPerPage);
        self.log('  - Filters:', filterString);

        // Set base filters (collection + availability)
        helper.setQueryParameter('filters', filterString);

        // Clear all existing numeric refinements before applying new ones
        // This is necessary because the helper accumulates refinements
        self.rangeFacets.forEach((facet) => {
          helper.clearRefinements(facet);
        });

        // Apply numeric range filters using addNumericRefinement for proper Algolia numeric filtering
        // This uses the numericFilters query parameter which Algolia handles correctly
        let hasNumericFilters = false;
        self.numericRangeFilters.forEach((range, attr) => {
          if (range.min !== null) {
            helper.addNumericRefinement(attr, '>=', range.min);
            self.log(`  - Added numeric refinement: ${attr} >= ${range.min}`);
            hasNumericFilters = true;
          }
          if (range.max !== null) {
            helper.addNumericRefinement(attr, '<=', range.max);
            self.log(`  - Added numeric refinement: ${attr} <= ${range.max}`);
            hasNumericFilters = true;
          }
        });

        // CRITICAL: Disable Algolia Dashboard Rules when numeric range filters are active
        // There's a known issue where certain Algolia Rules override/ignore numericFilters.
        // This ensures numeric filtering works correctly at the cost of disabling merchandising rules.
        // TODO: Review Algolia Dashboard rules to find which one conflicts with numericFilters
        if (hasNumericFilters) {
          helper.setQueryParameter('enableRules', false);
          self.log('  - Rules disabled due to active numeric filters');
        }

        // Apply disjunctive facet refinements
        // First clear all existing disjunctive refinements
        self.disjunctiveFacets.forEach((facet) => {
          helper.clearRefinements(facet);
        });

        // Then add the current ones
        Object.entries(self.disjunctiveRefinements).forEach(([facet, values]) => {
          values.forEach((value) => {
            helper.addDisjunctiveFacetRefinement(facet, value);
          });
        });

        self.log('  - Disjunctive refinements:', self.disjunctiveRefinements);
        self.log('  - Numeric range filters:', Object.fromEntries(self.numericRangeFilters));

        // CRITICAL: Set page LAST - clearRefinements/addDisjunctiveFacetRefinement reset page to 0
        // This must be called after all other helper operations to preserve pagination
        helper.setPage(currentPage);

        helper.search();
      },
    });

    // Configure initial search parameters
    // Note: For distinct to work, you must set attributeForDistinct in Algolia Dashboard
    this.search.addWidgets([
      instantsearch.widgets.configure({
        hitsPerPage: this.config.searchParameters?.hitsPerPage || 36,
        distinct: true,
        ...(this.shouldBoostSaleVariants() && {
          optionalFilters: ['meta.custom.on_sale:true<score=500>'],
        }),
        // Declare all facets - disjunctive ones for multi-select, conjunctive for price/availability
        disjunctiveFacets: this.disjunctiveFacets,
        // Include range facets (price, height, width, etc.) to get facet_stats for min/max values
        facets: [...this.rangeFacets, ...this.conjunctiveFacets],
        maxValuesPerFacet: this.maxValuesPerFacet,
        // Enable click analytics to get queryID in responses
        clickAnalytics: this.config.features?.analytics !== false,
        // Enable Algolia Dashboard Rules for merchandising
        // Note: If rules use 'filters' param, they may conflict with facetFilters.
        // Rules should use 'optionalFilters' for boosting to avoid overriding facet selections.
        // See: https://github.com/algolia/instantsearch/issues/5538
        enableRules: true,
      }),
    ]);

    if (this.shouldBoostSaleVariants()) {
      this.log('Sale-variant distinct boost active');
    }

    this.log('InstantSearch configured successfully');
  }

  /**
   * Facet accordion open/close persistence.
   *
   * The facet panel HTML is rebuilt on every result update and on every page
   * load, so a shopper's expand/collapse choices are otherwise lost — most
   * visibly when returning from a product, where every group springs back
   * open. We remember each group the shopper explicitly toggles (keyed by its
   * semantic facet, e.g. "brand") in sessionStorage and honor it on render,
   * so their arrangement survives re-renders and back-navigation for the
   * browsing session.
   */
  _readFacetOpenState() {
    if (this._facetOpenState) return this._facetOpenState;
    try {
      this._facetOpenState = JSON.parse(sessionStorage.getItem('algolia:facetOpen')) || {};
    } catch {
      this._facetOpenState = {};
    }
    return this._facetOpenState;
  }

  _writeFacetOpenState(key, isOpen) {
    if (!key) return;
    const state = this._readFacetOpenState();
    state[key] = isOpen;
    try {
      sessionStorage.setItem('algolia:facetOpen', JSON.stringify(state));
    } catch {
      // sessionStorage unavailable (private mode / quota) — keep the in-memory copy.
    }
  }

  /**
   * Resolve a facet group's open state: an explicit shopper choice wins over
   * the config/active-filter default. alwaysOpen groups are forced open by
   * their callers and never recorded.
   */
  _resolveFacetExpanded(key, defaultExpanded) {
    const stored = this._readFacetOpenState()[key];
    return stored !== undefined ? stored : defaultExpanded;
  }

  /**
   * Capture which facet groups are open right now (by semantic key) before a
   * rebuild, so a group that is open only because it has an active filter does
   * not collapse under the shopper when they deselect that filter's last value.
   * Explicit sessionStorage choices still take precedence in
   * _resolveFacetExpanded — this only raises the floor for the default.
   */
  _captureOpenFacetKeys(containers) {
    this._openFacetKeysAtRender = new Set();
    containers.forEach((container) => {
      container.querySelectorAll('details.facet-accordion-details[open]').forEach((d) => {
        if (d.dataset.facetKey) this._openFacetKeysAtRender.add(d.dataset.facetKey);
      });
    });
  }

  _wasFacetOpenAtRender(key) {
    return !!(this._openFacetKeysAtRender && this._openFacetKeysAtRender.has(key));
  }

  /**
   * Record a shopper's expand/collapse as they click a group's summary. We
   * listen for the summary click rather than the native `toggle` event: the
   * accordion is a customized <details> whose animation-driven open/close does
   * not fire a reliable `toggle` (and its programmatic opens during hydration
   * would be misread as shopper intent). The custom element flips its own open
   * state in its summary handler, which runs before this document-level
   * (bubbling) listener, so the state read here is the post-click state.
   */
  _bindFacetAccordionPersistence() {
    if (this._facetAccordionPersistenceBound) return;
    this._facetAccordionPersistenceBound = true;
    document.addEventListener('click', (e) => {
      const summary = e.target.closest && e.target.closest('summary');
      if (!summary) return;
      const details = summary.closest('details.facet-accordion-details');
      if (!details || details.classList.contains('facet-accordion--always-open')) return;
      const key = details.dataset.facetKey;
      if (key) this._writeFacetOpenState(key, details.open);
    });
  }

  /**
   * Create middleware to expand facet accordions when they have active refinements
   * This ensures users can see which filters are active
   */
  createAccordionExpansionMiddleware() {
    const self = this;

    return () => ({
      onStateChange({ uiState }) {
        // Get the current index state - check both base index and current sorted index
        const currentIndex = self.getIndexForSort(self.currentSort);
        const indexState = uiState[currentIndex] || uiState[self.config.indexName] || {};

        // Get active refinements from state
        const refinementList = indexState.refinementList || {};
        const range = indexState.range || {};

        // Also check disjunctive refinements tracked internally
        const disjunctiveRefinements = self.disjunctiveRefinements || {};

        // Find all facet accordions in all containers
        const containers = document.querySelectorAll('.algolia-facets-container, [data-facets-drawer]');

        containers.forEach(container => {
          const accordions = container.querySelectorAll('details.facet-accordion-details[data-index]');

          accordions.forEach((accordion) => {
            // Get the filter param from data-index (e.g., "vertical-brand" -> "brand")
            const dataIndex = accordion.dataset.index || '';
            const filterParam = dataIndex.replace(/^(vertical|drawer)-/, '');
            if (!filterParam) return;

            // Map filter param back to Algolia attribute for checking refinements
            const attributeMapping = self.getFilterMapping();
            const attribute = attributeMapping[filterParam] || filterParam;

            // Check if this facet has active refinements
            const hasRefinements =
              (refinementList[attribute] && refinementList[attribute].length > 0) ||
              (disjunctiveRefinements[attribute] && disjunctiveRefinements[attribute].length > 0) ||
              (range[attribute] !== undefined);

            // Expand accordion if it has active refinements — unless the
            // shopper explicitly collapsed this group (their choice wins).
            const storedKey = accordion.dataset.facetKey;
            const explicitlyCollapsed = storedKey && self._readFacetOpenState()[storedKey] === false;
            if (hasRefinements && !accordion.open && !explicitlyCollapsed) {
              accordion.open = true;
              self.log(`Expanded facet accordion for ${filterParam} (has active refinements)`);
            }
          });
        });
      },
    });
  }

  /**
   * Create middleware to track pagination state changes for debugging
   * This helps diagnose issues where pagination clicks don't update products
   */
  createPaginationDebugMiddleware() {
    const self = this;
    let previousPage = 0;

    return () => ({
      onStateChange({ uiState }) {
        // Get the current index state
        const currentIndex = self.getIndexForSort(self.currentSort);
        const indexState = uiState[currentIndex] || uiState[self.config.indexName] || {};
        const currentPage = indexState.page || 1;

        // Log page changes
        if (currentPage !== previousPage) {
          self.log('🔄 PAGINATION STATE CHANGE: Page', previousPage, '→', currentPage);
          previousPage = currentPage;

          // Update URL with page parameter (since routing: false disables auto URL sync)
          const url = new URL(window.location.href);
          if (currentPage > 1) {
            url.searchParams.set('page', currentPage);
          } else {
            url.searchParams.delete('page');
          }
          history.replaceState({}, '', url.toString());
          self.log('📍 URL updated with page:', currentPage);
        }
      },
    });
  }

  /**
   * Middleware that dims the grid immediately on any UI state change
   * (pagination click, facet checkbox, sort change, refine clear, etc.),
   * BEFORE the network request goes out. This decouples the INP measurement
   * from the network round-trip and the subsequent grid re-render:
   *
   *   click → onStateChange → showLoadingState → paint (~16ms) ← INP stops here
   *   then network → renderHits → swapGridContent → hideLoadingState
   *
   * Without this, INP includes the full network + render window (400-800ms
   * on pagination per Sentry p75). With this, INP is bounded by the
   * dim-class apply + paint. Target: ~200ms → sub-100ms on pagination/facet
   * clicks. hideLoadingState in renderHits below continues to clear the dim.
   */
  createLoadingStateMiddleware() {
    const self = this;
    return () => ({
      onStateChange() {
        self.showLoadingState();
      },
    });
  }

  /**
   * Get Algolia index name for a sort option
   */
  getIndexForSort(sortValue) {
    const baseIndex = this.config.indexName || 'shopify_products';

    // Check configurable sort indices from config
    const sortIndices = Array.isArray(this.config.sortIndices) ? this.config.sortIndices : [];
    const sortConfig = sortIndices.find((s) => s.value === sortValue);

    if (sortConfig) {
      // Append suffix to base index (empty suffix = base index)
      const fullIndex = sortConfig.index ? `${baseIndex}${sortConfig.index}` : baseIndex;
      this.log('Sort index for', sortValue, ':', fullIndex, '(suffix:', sortConfig.index || 'none', ')');
      return fullIndex;
    }

    // Fallback to base index if no config found
    this.log('No sort config for', sortValue, ', using base index:', baseIndex);
    return baseIndex;
  }

  /**
   * Hijack FacetForms to use Algolia instead of Shopify
   */
  hijackFacetForm() {
    if (!this.dom.facetForms || this.dom.facetForms.length === 0) {
      this.log('No facet forms found, skipping hijack');
      return;
    }

    // Hijack each form (desktop and drawer)
    this.dom.facetForms.forEach((form, index) => {
      this.log('Hijacking form', index, form);

      // Completely disable native FacetForm behavior
      // Override renderSection to use Algolia instead of Shopify fetch
      form.renderSection = (url, _event) => {
        this.log('Intercepted renderSection from form', index, ':', url);

        const urlObj = new URL(url);
        const params = urlObj.searchParams;

        // Apply filters to Algolia
        this.applyFiltersFromParams(params);

        // Update URL without Shopify reload
        const cleanUrl = new URL(window.location.href);
        cleanUrl.search = '';

        // Copy filter params to clean URL
        params.forEach((value, key) => {
          if (key !== 'section_id') {
            cleanUrl.searchParams.append(key, value);
          }
        });

        history.replaceState({}, '', cleanUrl.toString());

        // Reset dirty flag on all forms to prevent further native calls
        this.dom.facetForms.forEach((f) => {
          f.dirty = false;
        });

        // Return a resolved promise to satisfy any code expecting renderSection to return a promise
        return Promise.resolve();
      };

      // Also override renderSectionFromFetch if it exists
      if (form.renderSectionFromFetch) {
        form.renderSectionFromFetch = () => {
          this.log('Blocked renderSectionFromFetch');
          return Promise.resolve();
        };
      }

      // Override onSubmitHandler
      if (form.onSubmitHandler) {
        form.onSubmitHandler = (e) => {
          this.log('Blocked onSubmitHandler');
          e.preventDefault();
          return false;
        };
      }

      // Intercept form submit with capture to run before native handlers
      form.addEventListener(
        'submit',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.log('Blocked form submit');
          return false;
        },
        { capture: true }
      );

      // Listen for filter input changes directly
      form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
        input.addEventListener('change', (e) => {
          // Prevent native handlers from running
          e.stopPropagation();
          
          this.log('Filter input changed:', input.name, '=', input.value, 'checked:', input.checked);
          
          // Small delay to ensure we're not racing with native handlers
          setTimeout(() => {
            this.handleFilterChange(form);
          }, 10);
        }, { capture: true });
      });

      // Also handle range inputs (price, height, width, etc.)
      form.querySelectorAll('input[type="number"], input[type="range"]').forEach((input) => {
        // Handle price inputs and any .min/.max range inputs
        if (input.name?.includes('price') || input.name?.endsWith('.min') || input.name?.endsWith('.max')) {
          input.addEventListener('change', (e) => {
            e.stopPropagation();
            this.log('Range input changed:', input.name, '=', input.value);
            setTimeout(() => {
              this.handleFilterChange(form);
            }, 10);
          }, { capture: true });
        }
      });
    });

    this.log('FacetForms hijacked:', this.dom.facetForms.length);
  }

  /**
   * Hijack filter removal buttons (×, Clear All)
   */
  hijackFilterRemoval() {
    // Use event delegation on the document to catch dynamically added elements
    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[is="facet-remove"]');
      if (!removeBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const href = removeBtn.getAttribute('href');
      this.log('Filter remove clicked:', href);

      // Check if this is "Clear All" (no filter params in href)
      const url = new URL(href, window.location.origin);
      const params = url.searchParams;

      // Check for filter params
      const hasFilters = Array.from(params.keys()).some(
        (key) => key.startsWith('filter.') || key === 'sort_by'
      );

      if (!hasFilters || removeBtn.classList.contains('facet-clear-all')) {
        // Clear All - reset to no filters
        this.log('Clearing all filters');
        this.clearAllFilters();
      } else {
        // Single filter removal - apply the new filter state from URL
        this.log('Removing single filter, new params:', params.toString());
        this.applyFiltersFromParams(params);
      }

      // Update URL
      history.replaceState({}, '', href);

    }, { capture: true });

    this.log('Filter removal hijacked');
  }

  /**
   * Clear all filters and reset to base collection
   */
  clearAllFilters() {
    // Reset filter state
    this.currentFilters = [];
    this.disjunctiveRefinements = {};
    this.numericRangeFilters = new Map();

    // Clear search query and trigger search with cleared state
    if (this.search?.helper) {
      this.search.helper
        .setQuery('')
        .setPage(0)
        .search();
    }

    // Update URL to base collection
    const baseUrl = new URL(window.location.href);
    // Remove all filter params but keep sort
    const sortBy = baseUrl.searchParams.get('sort_by');
    baseUrl.search = '';
    if (sortBy) {
      baseUrl.searchParams.set('sort_by', sortBy);
    }
    history.replaceState({}, '', baseUrl.toString());

    // Uncheck all filter checkboxes
    this.dom.facetForms?.forEach((form) => {
      form.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
        cb.checked = false;
      });
      // Reset price and other range inputs (height, width, etc.)
      form.querySelectorAll('input[name*="price"], input[name$=".min"], input[name$=".max"]').forEach((input) => {
        if (input.type === 'number' || input.type === 'range') {
          input.value = input.defaultValue || '';
        }
      });
    });

    // Clear search input
    const searchInput = document.getElementById('AlgoliaCollectionSearchInput');
    if (searchInput) {
      searchInput.value = '';
    }

    this.log('All filters cleared');
  }

  /**
   * Handle filter change from direct input listeners
   */
  handleFilterChange(form) {
    // Build URL from form data
    const formData = new FormData(form);
    const params = new URLSearchParams();

    for (const [key, value] of formData.entries()) {
      if (key !== 'section_id' && value) {
        params.append(key, value);
      }
    }

    this.log('Applying filters from form change:', params.toString());

    // Apply filters to Algolia
    this.applyFiltersFromParams(params);

    // Update URL
    const cleanUrl = new URL(window.location.href);
    cleanUrl.search = params.toString();
    history.replaceState({}, '', cleanUrl.toString());

    // Reset dirty flag on all forms
    this.dom.facetForms.forEach((f) => {
      f.dirty = false;
    });
  }

  /**
   * Parse initial filters from URL (before search.start())
   */
  parseInitialFilters() {
    const params = new URLSearchParams(window.location.search);
    if (params.toString()) {
      // Don't trigger search - just populate currentFilters for the initial search
      this.applyFiltersFromParams(params, false);
    }

    // Parse initial page from URL (since routing: false disables auto URL parsing)
    const pageParam = params.get('page');
    if (pageParam) {
      const initialPage = parseInt(pageParam, 10);
      if (initialPage > 1) {
        // Store for use in searchFunction on first search
        this.initialPage = initialPage - 1; // Algolia uses 0-indexed pages
        this.log('Parsed initial page from URL:', initialPage);
      }
    }
  }

  /**
   * Apply filters from URL params to Algolia
   * @param {URLSearchParams} params - The filter parameters
   * @param {boolean} triggerSearch - Whether to trigger a search (false during initialization)
   */
  applyFiltersFromParams(params, triggerSearch = true) {
    this.log('applyFiltersFromParams called, triggerSearch:', triggerSearch);

    // Reset filters - collection filter is added in searchFunction
    // currentFilters is for non-disjunctive filters (availability, etc.)
    this.currentFilters = [];
    // disjunctiveRefinements is for facets that should use OR logic
    this.disjunctiveRefinements = {};
    // numericRangeFilters is for numeric range filters (price, width, height, etc.)
    // These MUST be applied via helper.addNumericRefinement() for Algolia to filter correctly
    this.numericRangeFilters = new Map();

    // Group filter values by attribute
    const filterGroups = new Map();

    params.forEach((value, key) => {
      if (key === 'sort_by') {
        // Handle sort separately
        this.currentSort = value;
        if (this.search?.helper) {
          const newIndex = this.getIndexForSort(value);
          this.search.helper.setIndex(newIndex);
        }
        return;
      }

      // Handle price range params
      if (key === 'price.min' || key === 'price.max') {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return;

        const isMin = key === 'price.min';
        if (!this.numericRangeFilters.has('price')) {
          this.numericRangeFilters.set('price', { min: null, max: null });
        }
        if (isMin) {
          this.numericRangeFilters.get('price').min = numValue;
        } else {
          this.numericRangeFilters.get('price').max = numValue;
        }
        return;
      }

      // Check for range facet params (e.g., height.min, height.max, width.min, width.max)
      if (key.endsWith('.min') || key.endsWith('.max')) {
        const baseParam = key.replace(/\.(min|max)$/, '');
        const isMin = key.endsWith('.min');

        // Map the base param to Algolia attribute
        // Avoid double-prefix if baseParam already contains meta.custom.
        const algoliaAttr = this.urlParamToAttribute?.[baseParam]
          || (baseParam.startsWith('meta.custom.') ? baseParam : `meta.custom.${baseParam}`);

        const numValue = parseFloat(value);
        if (isNaN(numValue)) return;

        if (!this.numericRangeFilters.has(algoliaAttr)) {
          this.numericRangeFilters.set(algoliaAttr, { min: null, max: null });
        }

        if (isMin) {
          this.numericRangeFilters.get(algoliaAttr).min = numValue;
        } else {
          this.numericRangeFilters.get(algoliaAttr).max = numValue;
        }
        return;
      }

      // Map Shopify param to Algolia attribute
      const algoliaAttr = this.mapFilterParam(key);
      if (algoliaAttr) {
        // Group values by attribute
        if (!filterGroups.has(algoliaAttr)) {
          filterGroups.set(algoliaAttr, []);
        }
        filterGroups.get(algoliaAttr).push(value);
      }
    });

    // Process filter groups - separate disjunctive from regular filters
    filterGroups.forEach((values, attr) => {
      // Check if this is a disjunctive facet
      const isDisjunctive = this.disjunctiveFacets && this.disjunctiveFacets.includes(attr);

      if (isDisjunctive) {
        // Use disjunctive refinements for proper facet count calculation
        this.disjunctiveRefinements[attr] = values;
      } else if (attr === 'inventory_available') {
        // Special case: inventory_available is boolean, use regular filter
        if (values.includes('1') || values.includes('true')) {
          this.currentFilters.push(`${attr}:true`);
        }
      } else {
        // Non-disjunctive facet - add to regular filters
        // Escape double quotes in values to prevent malformed Algolia filter strings
        const escapeFilterValue = (v) => v.replace(/"/g, '\\"');
        if (values.length === 1) {
          this.currentFilters.push(`${attr}:"${escapeFilterValue(values[0])}"`);
        } else {
          // Multiple values with AND logic
          const filters = values.map((v) => `${attr}:"${escapeFilterValue(v)}"`).join(' AND ');
          this.currentFilters.push(`(${filters})`);
        }
      }
    });

    // Note: Numeric range filters (price, width, height, etc.) are stored in this.numericRangeFilters
    // and applied via helper.addNumericRefinement() in the searchFunction for proper Algolia numeric filtering

    this.log('Applied filters:', this.currentFilters);
    this.log('Disjunctive refinements:', this.disjunctiveRefinements);
    this.log('Numeric range filters:', Object.fromEntries(this.numericRangeFilters));

    // Trigger search if requested
    if (triggerSearch && this.search?.helper) {
      this.log('Triggering search with helper.search()...');
      // Don't reset page - let Algolia's pagination widget manage page state
      // Resetting here causes double-search bug where pagination clicks get reset to page 0
      // this.search.helper.setPage(0);
      // Call helper.search() which goes through searchFunction
      this.search.helper.search();
    } else if (triggerSearch) {
      this.log('WARNING: search.helper is not available!');
    }
  }

  /**
   * Get merged filter mapping (defaults + config overrides)
   */
  getFilterMapping() {
    return {
      ...AlgoliaCollectionPage.DEFAULT_FILTER_MAPPING,
      ...this.facetAttributeMapping,
    };
  }

  /**
   * Map URL param name to Algolia attribute
   * Supports SEO-friendly params (e.g., style, brand) and legacy Shopify params (filter.p.*)
   */
  mapFilterParam(paramName) {
    // Check SEO-friendly URL param mapping first
    if (this.urlParamToAttribute && this.urlParamToAttribute[paramName]) {
      return this.urlParamToAttribute[paramName];
    }

    const mapping = this.getFilterMapping();

    // Check direct mapping (defaults merged with config)
    if (mapping[paramName]) {
      return mapping[paramName];
    }

    // Check dynamic option pattern (filter.v.option.X -> options.X)
    const optionMatch = paramName.match(/^filter\.v\.option\.(.+)$/);
    if (optionMatch) {
      return `options.${optionMatch[1]}`;
    }

    // Check metafield pattern (filter.p.meta.custom.X -> meta.custom.X)
    const metaMatch = paramName.match(/^filter\.p\.(meta\.custom\..+)$/);
    if (metaMatch) {
      return metaMatch[1];
    }

    return null;
  }

  /**
   * Set up hits connector for product rendering
   */
  setupHitsConnector() {
    const hits = instantsearch.connectors.connectHits((renderOptions, isFirstRender) => {
      const { hits, results } = renderOptions;

      this.log('Hits connector called, isFirstRender:', isFirstRender, 'hits count:', hits?.length);

      // Track query for SKU detection
      this.currentQuery = results?.query || '';

      if (!this.dom.productGrid) {
        this.log('WARNING: productGrid DOM element not found, trying to find it again...');
        this.dom.productGrid = document.getElementById('ProductGridContainer');
        if (!this.dom.productGrid) {
          console.error('[Algolia Collection] ProductGridContainer not found in DOM');
          return;
        }
      }

      // Log render
      if (window.algoliaLogger) {
        window.algoliaLogger.logInstantSearchRender('CollectionPage Hits', results);
      }

      // Store queryID and hits for analytics tracking
      this.lastQueryID = results?.queryID || null;
      this.lastHits = hits || [];
      this.log('Stored queryID for analytics:', this.lastQueryID);
      if (results) window.AlgoliaShared?.observeSearch(results, 'collection');

      // Show loading state
      this.showLoadingState();

      // Find the product list container
      const productsList =
        this.dom.productsList ||
        this.dom.productGrid.querySelector('#ProductsList') ||
        this.dom.productGrid.querySelector('.products-list') ||
        this.dom.productGrid;

      this.log('Rendering to productsList:', productsList?.id || productsList?.className);

      // Render products - pass array for chunked rendering on large sets
      if (hits.length > 0) {
        this.swapGridContent(productsList, hits.map((hit, i) => this.renderProductCard(hit, i)));
        window.AlgoliaShared?.restoreScroll(productsList);
        this.log('Rendered', hits.length, 'products');
        // Sync ItemList JSON-LD with the rendered hits so faceted /
        // sorted views serve schema that matches the visible cards.
        if (window.AlgoliaShared?.updateCollectionSchema) {
          window.AlgoliaShared.updateCollectionSchema(hits);
        }
      } else if (!isFirstRender) {
        // Only show "no results" after initial render - keeps skeleton visible during first load
        // On first render, hits is empty because API response hasn't arrived yet
        this.swapGridContent(productsList, this.renderNoResults());
        window.AlgoliaShared?.restoreScroll(productsList);
        this.log('Rendered no results message');
      } else {
        this.log('First render with no hits - keeping skeleton visible');
      }

      // Update grid class based on layout
      this.updateGridLayout(productsList);

      // Update product count display
      this.updateProductCount(results?.nbHits || 0);

      // Update active filter badges (visible above the grid, must be in sync with the
      // products the user sees right now; can't defer)
      this.updateActiveFilters();

      // Wire click tracking + wishlist for the FRESHLY-rendered cards
      // synchronously. These attach event handlers to the new DOM. If they
      // were deferred, a user clicking a product card or wishlist heart in
      // the brief window before the deferred handler fires would either miss
      // the click (analytics) or hit a stale-state handler (wishlist). Cost
      // is small: setupClickTracking is one removeEventListener +
      // addEventListener (event delegation, fast); reinitializeWishlist
      // already wraps its body in setTimeout internally.
      this.reinitializeWishlist();
      this.setupClickTracking(hits);

      // Hide loading state — flip the dim off immediately so the next paint shows
      // the new grid at full opacity.
      this.hideLoadingState();

      // === DEFERRED POST-RENDER WORK ===
      //
      // The work below isn't required for the next paint to be correct from the
      // user's POV: facet sidebar count badges live offscreen in a drawer on
      // mobile (and behind the fold on desktop), and reinitializeThemeFeatures
      // touches non-grid components. Running these synchronously here was the
      // dominant contributor to the 136 ms long task observed in inp-probe
      // traces (updateFacetCounts in particular does an innerHTML rebuild of
      // the entire facet sidebar — ~396 checkboxes on /collections/lamps).
      // Deferring frees the main thread to present the new grid + count +
      // chips without waiting on this work.
      //
      // setupClickTracking and reinitializeWishlist are intentionally NOT in
      // this deferred block — they wire interaction handlers to the new
      // cards and a deferred path leaves a window where clicks on freshly-
      // rendered cards might miss tracking or wishlist actions.
      //
      // requestIdleCallback when available; setTimeout(...,0) fallback for Safari.
      const runIdle = window.requestIdleCallback || ((cb) => setTimeout(cb, 0));
      runIdle(() => {
        this.updateFacetCounts(results);
      }, { timeout: 500 });
      runIdle(() => {
        this.reinitializeThemeFeatures();
      }, { timeout: 1000 });
      // Refresh cached scroll measurements off the input task — the active-filter
      // chip strip and any header-height changes settle after this render, so the
      // next facet/sort/pagination tap reads cached values instead of forcing layout.
      runIdle(() => {
        this._cacheScrollMeasurements();
      }, { timeout: 1000 });

      // Enrich with B2B prices (runs async, updates DOM when complete)
      if (this.config?.b2b?.enabled) {
        this.enrichWithB2BPrices(hits);
      }

      // Handle scroll-to-grid after render. Two sources of intent:
      //   - pagination: detected here via _lastRenderedPage change
      //   - filter apply / search input: caller sets _scrollOnNextRender before triggering the search
      // Both paths land here so the scroll runs *after* updateActiveFilters() — meaning
      // the chip strip is settled and grid measurements are accurate.
      // The first render comes before any results, so it doesn't count as a page: a
      // ?page=3 URL would otherwise scroll to the grid on load and on back.
      const currentPage = results?.page ?? 0;
      const pageChanged =
        !isFirstRender &&
        this._lastRenderedPage !== undefined &&
        this._lastRenderedPage !== currentPage;
      if (results) this._lastRenderedPage = currentPage;

      // Skip the deferred scroll if a capture-phase click handler (in
      // applyFacetFilters, wireSorting, or setupClassicPagination) already
      // scrolled within the input event window. The deferred path here runs
      // >500 ms after the user input, which puts the resulting position
      // changes outside the layout-shift API's hadRecentInput exclusion and
      // counts them as CLS. The immediate-scroll handlers cover real-user
      // clicks; this fallback covers programmatic state changes (URL
      // navigation, bfcache restores, etc.) where no user input occurred.
      if ((this._scrollOnNextRender || pageChanged) && !this._scrollAlreadyDone) {
        this._scrollOnNextRender = false;
        const self = this;
        requestAnimationFrame(() => {
          self.scrollToProductGrid();
        });
      }
      this._scrollAlreadyDone = false;
    });

    // Only add regular hits widget if not using infinite hits
    // (infinite hits handles its own rendering)
    if (!this.useInfiniteHits) {
      this.search.addWidgets([hits({})]);
    }
  }

  /**
   * Render a product card matching theme structure
   */
  renderProductCard(product, index) {
    // Use adapter's rendering if available
    if (window.algoliaAdapter?.renderProductCard) {
      let cardHtml = window.algoliaAdapter.renderProductCard(product);
      // The adapter returns with grid__item wrapper, replace with correct class
      cardHtml = cardHtml.replace('<div class="grid__item">', '<div class="f-column product-column">');
      return cardHtml;
    }

    // Fallback rendering with correct wrapper
    return `<div class="f-column product-column">${this.renderProductCardInner(product, index)}</div>`;
  }


  /**
   * Get price from Algolia hit, checking for B2B trade pricing first
   */
  getProductPrice(product) {
    const b2bConfig = this.config?.b2b;

    // Check if B2B pricing is enabled and customer is B2B
    if (b2bConfig?.enabled && b2bConfig?.priceListId) {
      const tradePriceField = `${b2bConfig.tradePricePrefix}${b2bConfig.priceListId}`;
      const tradePrice = product[tradePriceField];

      if (tradePrice !== undefined && tradePrice !== null) {
        this.log(`Using B2B trade price from ${tradePriceField}:`, tradePrice);
        return {
          priceMin: tradePrice,
          priceMax: tradePrice,
          isTradePrice: true
        };
      }
      this.log(`B2B price field ${tradePriceField} not found, using standard price`);
    }

    // Fallback to standard pricing
    const priceMin = product.variants_min_price ?? product.price_min ?? product.price ?? 0;
    const priceMax = product.variants_max_price ?? product.price_max ?? product.price ?? priceMin;
    return {
      priceMin,
      priceMax,
      isTradePrice: false
    };
  }

  /**
   * Render product card inner HTML (without wrapper div)
   * Matches the native theme card-product.liquid structure exactly
   * Now respects ALL pcard_* settings from theme configuration
   */
  renderProductCardInner(product, index) {
    // Get variant price range from Algolia data (with B2B support)
    const pricing = this.getProductPrice(product);
    const priceMin = pricing.priceMin;
    const priceMax = pricing.priceMax;
    const compareAtPriceMin = product.compare_at_price_min ?? product.compare_at_price ?? 0;
    const compareAtPriceMax = product.compare_at_price_max ?? product.compare_at_price ?? 0;

    // Determine if product has a price range (multiple variant prices) - not for B2B single price
    const hasRange = !pricing.isTradePrice && (priceMax > priceMin);

    // Product is on sale if any compare_at_price is higher than the corresponding price
    const onSale = (compareAtPriceMin > 0 && compareAtPriceMin > priceMin) ||
                   (compareAtPriceMax > 0 && compareAtPriceMax > priceMax);

    const available = product.inventory_available !== false;

    // Get image URLs - prefer media images from meta.custom.media, fallback to standard image fields
    const mediaImages = product.meta?.custom?.media?.[0]?.images;
    const imageUrl = mediaImages?.[0]?.url || product.image || product.featured_image || '';
    const secondImageUrl = mediaImages?.[1]?.url || (product.images && product.images[1]) || '';

    // A card stands for the whole product, so it shows parent_name (the un-sized
    // product name). short_name is size-specific and belongs on the PDP, where the
    // customer has committed to one variant. Matches card-product.liquid.
    const displayTitle = product.meta?.custom?.parent_name
      || product.meta?.custom?.short_name
      || product.title;

    // Get product ID for wishlist integration
    const productId = this.getNumericProductId(product);

    // Get variant ID for URL - check various possible fields
    // Algolia may index at variant level with variant_id, or product level with first variant
    const variantId = product.variant_id || product.objectID?.split('_').pop() || product.variants?.[0]?.id || '';

    // Build product URL with variant parameter if available
    let productUrl = `/products/${product.handle}`;
    if (variantId && String(variantId) !== productId) {
      // Only add variant param if it's different from product ID (meaning it's actually a variant ID)
      productUrl += `?variant=${variantId}`;
    }

    // Get brand name from meta.custom.brand_name, fall back to vendor
    // Handle array values (extract first element) or use string value as-is
    const brandNameValue = product.meta?.custom?.brand_name;
    const brandName = Array.isArray(brandNameValue) 
      ? (brandNameValue[0] || product.vendor || '')
      : (brandNameValue || product.vendor || '');

    // ========================================
    // READ ALL PCARD SETTINGS FROM THEME CONFIG
    // ========================================
    const pcardStyle = this.themeConfig.pcardStyle || 'standard';
    const pcardColorScheme = this.themeConfig.pcardColorScheme || 'scheme-2';
    const contentAlignment = this.themeConfig.pcardContentAlignment || 'left';
    const showVendor = this.themeConfig.pcardShowVendor !== false;
    const showPrice = this.themeConfig.pcardShowPrice !== false;
    const showSecondImg = this.themeConfig.pcardShowSecondImg !== false;
    const showQuickAdd = this.themeConfig.pcardShowCartButton !== false;
    const showQuickView = this.themeConfig.pcardShowQuickviewButton !== false;
    const mobileHideQuickAdd = this.themeConfig.pcardMobileHideQuickAdd === true;
    const quickAddButtonStyle = this.themeConfig.pcardButtonStyle || 'btn--white';
    const _quickViewButtonStyle = this.themeConfig.quickviewButtonStyle || 'btn--white';
    const chooseOptionsAction = this.themeConfig.pcardChooseOptionsAction || 'open_popup';

    // Image ratio - convert setting to CSS aspect ratio
    const imageRatioSetting = this.themeConfig.pcardImageRatio || 'adapt';
    const imageRatio = this.getAspectRatio(imageRatioSetting, product);

    // Mobile-responsive font sizes
    const isMobile = window.innerWidth < 768;
    const _vendorFontSize = isMobile
      ? (this.themeConfig.pcardVendorFontSizeMobile || 11)
      : (this.themeConfig.pcardVendorFontSize || 12);
    const _titleFontSize = isMobile
      ? (this.themeConfig.pcardTitleFontSizeMobile || 15)
      : (this.themeConfig.pcardTitleFontSize || 16);

    // Get variant count for "X options" display
    const variantCount = product.variant_count || product.variants_count || 1;
    const hasVariants = variantCount > 1;

    // Build card classes
    const cardClasses = [
      'product-card',
      `product-card-style-${pcardStyle}`,
      pcardStyle === 'card' ? `color-${pcardColorScheme}` : 'color-inherit'
    ];
    if (mobileHideQuickAdd) cardClasses.push('mobile-hide-quick-add');

    // Build image wrapper classes
    const imageWrapperClasses = ['product-card__image-wrapper'];
    if (!secondImageUrl || !showSecondImg) {
      imageWrapperClasses.push('product-card__image-wrapper--main-only');
    }

    // Sale percentage for experiment pcard-v2 (B bucket). Node is always
    // rendered when on sale; CSS gates visibility per bucket.
    let salePctBadge = '';
    if (onSale && compareAtPriceMin > 0 && compareAtPriceMin > priceMin) {
      const pct = Math.round(((compareAtPriceMin - priceMin) / compareAtPriceMin) * 100);
      if (pct >= 5) {
        const label = hasRange ? `Up to ${pct}% off` : `${pct}% off`;
        salePctBadge = `<div class="product-card__sale-badge" aria-hidden="true">${label}</div>`;
      }
    }

    // Sale-range mismatch: sale prices vary across variants but compare-at
    // is a single value. The mixed format ("$184 - $250.20  $278") confuses
    // shoppers because they can't tell which variant the $278 originally
    // applied to. When this is the case, B-scoped CSS hides the struck
    // compare price; the range + sale-% badge carry the savings signal.
    const saleRangeMismatch = onSale && hasRange &&
      (!compareAtPriceMax || compareAtPriceMin === compareAtPriceMax);

    return `
      <div class="${cardClasses.join(' ')}" data-product-id="${productId}" data-product-handle="${product.handle}" data-product-url="/products/${product.handle}" data-variant-id="${variantId}" data-sale-range-mismatch="${saleRangeMismatch}">
        <div class="product-card__wrapper h-full">
          <div class="${imageWrapperClasses.join(' ')}">
            ${salePctBadge}
            <a href="${productUrl}" aria-label="${this.escapeAttr(displayTitle)}" tabindex="-1">
                ${this.renderProductImage(imageUrl, displayTitle, imageRatio, index)}
                ${showSecondImg && secondImageUrl ? this.renderSecondImage(secondImageUrl, displayTitle, imageRatio) : ''}
            </a>
            ${showQuickView || hasVariants ? `
              <div class="product-card__options-wrapper">
                <a
                  href="${productUrl}"
                  class="product-card__options-badge${showQuickView ? ' product-card__quickview no-js-hidden' : ''}"
                  ${showQuickView ? `data-product-handle="${product.handle}"` : ''}
                  aria-label="${showQuickView ? 'Quick view' : 'View'} ${this.escapeAttr(displayTitle)}"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-eye icon--small" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5Z"/><circle cx="12" cy="12" r="3"/></svg>
                </a>
              </div>
            ` : ''}
            <div class="product-card__wishlist-wrapper">
              <button
                type="button"
                class="wishlist-btn-custom wishlist-btn-custom--small"
                data-wishlist-btn
                data-product-handle="${product.handle}"
                data-variant-id="${variantId}"
                data-product-id="${productId}"
                aria-label="Add to wishlist"
                aria-pressed="false"
              >
                <span class="wishlist-btn-custom__icon wishlist-btn-custom__icon--empty">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                </span>
                <span class="wishlist-btn-custom__icon wishlist-btn-custom__icon--filled">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                </span>
                <span class="wishlist-btn-custom__loading">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" stroke-opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/></svg>
                </span>
              </button>
            </div>
            ${showQuickAdd ? this.renderQuickAddButton(product, productUrl, hasVariants, available, quickAddButtonStyle, chooseOptionsAction) : ''}
          </div>
          ${this.renderDivider(hasVariants ? variantCount : null, product.handle, productId)}
          <div class="product-card__info text-${contentAlignment}">
            <h3 class="product-card__title text-pcard-title text-left">
              <a class="reversed-link block" href="${productUrl}">
                <span class="reversed-link__text">${this.escapeHtml(displayTitle)}</span>
              </a>
            </h3>
            ${showVendor && brandName ? `
              <p class="product-card__vendor">
                by ${this.escapeHtml(brandName)}
              </p>
            ` : ''}
            ${showPrice ? this.renderPrice(priceMin, priceMax, compareAtPriceMin, compareAtPriceMax, onSale, available, hasRange) : ''}
          </div>
          ${this.renderMobileActions(product, productUrl, productId, hasVariants, variantCount, showQuickView)}
        </div>
      </div>
    `;
  }

  /**
   * Convert image ratio setting to CSS aspect ratio value
   */
  getAspectRatio(setting, product) {
    switch (setting) {
      case 'adapt': {
        // Use product image's natural ratio, default to 3/4 if not available
        // (matches skeleton loader default to prevent CLS)
        const img = product.meta?.custom?.media?.[0]?.images?.[0];
        if (img && img.width && img.height) {
          return (img.width / img.height).toFixed(4);
        }
        return '0.75'; // 3/4 ratio - common for product photos
      }
      case '1/1':
        return '1';
      case '3/4':
        return '0.75';
      case '4/3':
        return '1.333';
      default:
        return '1';
    }
  }

  /**
   * Render main product image
   */
  renderProductImage(imageUrl, alt, aspectRatio, index) {
    const isAboveFold = typeof index === 'number' && index < 6;
    if (!imageUrl) {
      return `
        <div class="product-card__image product-card__image--main" style="--aspect-ratio: ${aspectRatio}">
          <svg class="placeholder-svg" viewBox="0 0 525 525" xmlns="http://www.w3.org/2000/svg">
            <rect fill="#f0f0f0" width="525" height="525"/>
          </svg>
        </div>
      `;
    }
    return `
      <div class="media-wrapper product-card__image product-card__image--main" style="--aspect-ratio: ${aspectRatio}">
        <img
          src="${this.getOptimizedImage(imageUrl, 450)}"
          srcset="${this.getOptimizedImageSrcset(imageUrl, [200, 300, 450, 620, 900])}"
          sizes="${this.getGridImageSizes()}"
          alt="${this.escapeAttr(alt)}"
          loading="${isAboveFold ? 'eager' : 'lazy'}"
          ${isAboveFold ? 'fetchpriority="high"' : ''}
          class="motion-reduce"
          width="450"
          height="${Math.round(450 / (aspectRatio || 1))}"
        >
        ${this.getLightbulbLoaderHtml()}
      </div>
    `;
  }

  /**
   * Get the lightbulb loader HTML for image placeholders
   * Simple gray bulb outline matching lightbulb-loader.liquid
   * Includes inline styles since CSS may not be loaded on Algolia pages
   */
  getLightbulbLoaderHtml() {
    return `<div class="lightbulb-loader" aria-hidden="true" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:#fff;z-index:1;opacity:0;visibility:hidden;pointer-events:none;">
      <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" style="width:25%;max-width:60px;min-width:30px;">
        <path class="bulb-glass" fill="none" stroke="#bbb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M32 8 C18 8 12 20 12 28 C12 38 20 44 22 48 L42 48 C44 44 52 38 52 28 C52 20 46 8 32 8Z"/>
        <path class="bulb-base" fill="none" stroke="#bbb" stroke-width="2" stroke-linecap="round" d="M22 48 L22 54 L42 54 L42 48 M24 54 L24 58 L40 58 L40 54"/>
        <path class="bulb-filament" fill="none" stroke="#bbb" stroke-width="2" stroke-linecap="round" d="M28 36 L28 28 L32 32 L36 28 L36 36"/>
      </svg>
    </div>`;
  }

  /**
   * Render secondary hover image
   */
  renderSecondImage(imageUrl, alt, aspectRatio) {
    return `
      <div class="media-wrapper product-card__image product-card__image--second inset-full hidden md:block" style="--aspect-ratio: ${aspectRatio}">
        <img
          src="${this.getOptimizedImage(imageUrl, 450)}"
          srcset="${this.getOptimizedImageSrcset(imageUrl, [200, 300, 450, 620, 900])}"
          sizes="${this.getGridImageSizes()}"
          alt="${this.escapeAttr(alt)}"
          loading="lazy"
          class="motion-reduce"
          width="450"
          height="${Math.round(450 / (aspectRatio || 1))}"
        >
      </div>
    `;
  }

  /**
   * Render Quick View button
   */
  renderQuickViewButton(product, productId, buttonStyle) {
    const quickViewType = this.themeConfig.quickViewType || 'drawer';
    const iconType = this.themeConfig.quickviewButtonIcon || 'search';

    // Get the appropriate icon SVG based on setting
    const iconSvg = this.getQuickViewIcon(iconType);

    // Note: aria-controls removed as the modal doesn't exist until loaded dynamically
    // aria-haspopup="dialog" is sufficient for accessibility
    return `
      <button
        class="product-card__quickview btn btn--icon-circle ${buttonStyle} no-js-hidden"
        aria-label="Quick view"
        aria-haspopup="dialog"
        data-product-handle="${product.handle}"
        data-quick-view-type="${quickViewType}"
      >
        ${iconSvg}
      </button>
    `;
  }

  /**
   * Get Quick View icon SVG based on icon type setting
   */
  getQuickViewIcon(iconType) {
    const icons = {
      search: `<svg class="icon icon-search icon--large" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M10.875 18.75C15.2242 18.75 18.75 15.2242 18.75 10.875C18.75 6.52576 15.2242 3 10.875 3C6.52576 3 3 6.52576 3 10.875C3 15.2242 6.52576 18.75 10.875 18.75Z"/>
        <path d="M16.4434 16.4453L20.9997 21.0016"/>
      </svg>`,
      eye: `<svg class="icon icon-eye icon--large" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5Z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>`,
      zoom: `<svg class="icon icon-zoom icon--large" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M10.875 18.75C15.2242 18.75 18.75 15.2242 18.75 10.875C18.75 6.52576 15.2242 3 10.875 3C6.52576 3 3 6.52576 3 10.875C3 15.2242 6.52576 18.75 10.875 18.75Z"/>
        <path d="M16.4434 16.4453L20.9997 21.0016"/>
      </svg>`
    };

    return icons[iconType] || icons.search;
  }

  /**
   * Render divider line between image and info sections
   * @param {number|null} variantCount - Number of variants to display, or null to hide
   * @param {string} productHandle - Product handle for quickview
   * @param {string} productId - Product ID for modal
   */
  renderDivider(variantCount = null, productHandle = '', productId = '') {
    if (!this.themeConfig.pcardDividerEnabled) {
      return '';
    }

    const color = this.themeConfig.pcardDividerColor || '#e5e5e5';
    const width = this.themeConfig.pcardDividerWidth || 100;
    const thickness = this.themeConfig.pcardDividerThickness || 1;
    const marginTop = this.themeConfig.pcardInfoPaddingTop || 10;
    const modalId = `QuickView-${productId}-${this.sectionId || 'algolia'}`;

    const optionsCount = variantCount ? `
      <button class="product-card__options-count" data-quickview-trigger data-product-handle="${productHandle}" aria-haspopup="dialog">${variantCount} options</button>
    ` : '';

    return `
      <div class="product-card__divider${variantCount ? ' product-card__divider--with-count' : ''}" style="
        --divider-color: ${color};
        --divider-width: ${width}%;
        --divider-thickness: ${thickness}px;
        margin-top: ${marginTop}px;
      ">${optionsCount}</div>
    `;
  }

  /**
   * Render mobile action icons - REMOVED
   */
  renderMobileActions(_product, _productUrl, _productId, _hasVariants, _variantCount, _showQuickView) {
    return '';
  }

  /**
   * Render Quick Add button/form. Mirrors card-product.liquid's enable_quick_add
   * block: single-variant products get a real Add to Cart action, multi-variant
   * products get "Choose options" (either a link to the PDP or an aria-controls
   * trigger that opens the quick-add variant modal, per chooseOptionsAction).
   */
  renderQuickAddButton(product, productUrl, hasVariants, available, buttonStyle, chooseOptionsAction) {
    if (!available) {
      return `
        <div class="product-card__actions product-card__main-actions">
          <button class="btn ${buttonStyle} w-full" disabled>
            Sold out
          </button>
        </div>
      `;
    }

    if (hasVariants) {
      // Multiple variants - show "Choose options" or link to product page
      if (chooseOptionsAction === 'product_page') {
        return `
          <div class="product-card__actions product-card__main-actions">
            <a href="${productUrl}" class="btn ${buttonStyle} w-full">
              Choose options
            </a>
          </div>
        `;
      }
      // Open popup/drawer for variant selection
      return `
        <div class="product-card__actions product-card__main-actions no-js-hidden">
          <button
            class="btn ${buttonStyle} w-full"
            data-product-handle="${product.handle}"
            data-action="quick-add-variants"
            aria-label="Choose options"
          >
            Choose options
          </button>
        </div>
      `;
    }

    // Single variant - show Add to Cart
    const variantId = product.variant_id || product.objectID?.split('_').pop() || product.variants?.[0]?.id || '';
    return `
      <div class="product-card__actions product-card__main-actions no-js-hidden">
        <button
          class="btn ${buttonStyle} w-full"
          data-product-handle="${product.handle}"
          data-variant-id="${variantId}"
          data-action="quick-add"
          aria-label="Add to cart"
        >
          Add to cart
        </button>
      </div>
    `;
  }

  /**
   * Render badges (Sale and Sold out)
   * Now respects pcard badge settings
   * @param {boolean} hasRange - Whether product has price range (for percentage display)
   */
  renderBadges(onSale, available, price, compareAtPrice, hasRange = false) {
    const badges = [];
    const showSaleBadge = this.themeConfig.pcardShowSaleBadge !== false;
    const showSoldoutBadge = this.themeConfig.pcardShowBadgeSoldout !== false;
    const saleBadgeType = this.themeConfig.pcardSaleBadgeType || 'show_text';

    // Sold out badge takes priority
    if (!available && showSoldoutBadge) {
      badges.push('<span class="f-badge f-badge--soldout" aria-hidden="true">Sold out</span>');
    } else if (onSale && showSaleBadge) {
      let saleBadgeText = 'Sale';

      // Only show percentage/amount for single prices, not ranges (percentage is ambiguous for ranges)
      if (!hasRange && saleBadgeType === 'show_percentage' && compareAtPrice > 0 && price > 0) {
        const percentage = Math.round((1 - price / compareAtPrice) * 100);
        saleBadgeText = `-${percentage}%`;
      } else if (!hasRange && saleBadgeType === 'show_amount' && compareAtPrice > 0 && price > 0) {
        const savings = this.formatMoney((compareAtPrice - price) * 100);
        saleBadgeText = `Save ${savings}`;
      }

      badges.push(`<span class="f-badge f-badge--sale" aria-hidden="true">${saleBadgeText}</span>`);
    }

    if (badges.length === 0) return '';
    return `<div class="product-card__badge">${badges.join('')}</div>`;
  }

  /**
   * Render price HTML matching theme f-price structure
   * Supports price ranges (min/max variant prices) to avoid misleading consumers
   * @param {number} priceMin - Minimum variant price
   * @param {number} priceMax - Maximum variant price
   * @param {number} compareAtPriceMin - Minimum compare at price
   * @param {number} compareAtPriceMax - Maximum compare at price
   * @param {boolean} onSale - Whether product has sale pricing
   * @param {boolean} available - Whether product is available
   * @param {boolean} hasRange - Whether product has multiple variant prices
   */
  renderPrice(priceMin, priceMax, compareAtPriceMin, compareAtPriceMax, onSale, available, hasRange) {
    const priceClasses = ['f-price'];
    if (!available) priceClasses.push('f-price--sold-out');

    // Apply sale styling for all products on sale (including ranges)
    const showAsSale = onSale;
    if (showAsSale) priceClasses.push('f-price--on-sale');

    // Format price with split dollars/cents.
    // Issue #1296 — emit `price-cents--zero` modifier class when cents are
    // exactly `00` so CSS can hide the noise on PLP cards while keeping
    // meaningful non-zero cents (.50, .99) visible. Mirrors the same
    // convention in `theme/snippets/price.liquid`.
    const formatPriceSplit = (priceInDollars) => {
      const formatted = this.formatMoney(priceInDollars * 100);
      // Match currency symbol, dollars (with commas), and cents from formatted price
      const match = formatted.match(/^([^\d]*)([\d,]+)\.(\d{2})(.*)$/);
      if (match) {
        const [, symbol, dollars, cents, suffix] = match;
        const centsClass = cents === '00' ? 'price-cents price-cents--zero' : 'price-cents';
        return `<span class="price-currency">${symbol}</span><span class="price-dollars">${dollars}</span><span class="${centsClass}">.${cents}</span>${suffix}`;
      }
      // Fallback for prices without cents (e.g., $100) — always zero cents
      const matchNoCents = formatted.match(/^([^\d]*)([\d,]+)(.*)$/);
      if (matchNoCents) {
        const [, symbol, dollars, suffix] = matchNoCents;
        return `<span class="price-currency">${symbol}</span><span class="price-dollars">${dollars}</span><span class="price-cents price-cents--zero">.00</span>${suffix}`;
      }
      return formatted;
    };

    // Build price display based on whether there's a range
    let priceDisplay;
    let comparePriceDisplay = null;

    if (hasRange) {
      // Show price range: "$X - $Y"
      priceDisplay = `${formatPriceSplit(priceMin)} - ${formatPriceSplit(priceMax)}`;
      // Show compare-at price range for ranged products on sale
      if (onSale) {
        if (compareAtPriceMin && compareAtPriceMax && compareAtPriceMin !== compareAtPriceMax) {
          comparePriceDisplay = `${this.formatMoney(compareAtPriceMin * 100)} - ${this.formatMoney(compareAtPriceMax * 100)}`;
        } else if (compareAtPriceMin) {
          comparePriceDisplay = this.formatMoney(compareAtPriceMin * 100);
        }
      }
    } else {
      // Single price (no range)
      priceDisplay = formatPriceSplit(priceMin);

      if (onSale && compareAtPriceMin) {
        comparePriceDisplay = this.formatMoney(compareAtPriceMin * 100);
      }
    }

    // Use CSS classes for typography - no inline styles
    return `
      <div class="${priceClasses.join(' ')}">
        <div class="f-price__regular">
          <span class="f-price-item f-price-item--regular">${priceDisplay}</span>
        </div>
        ${showAsSale && comparePriceDisplay ? `
          <div class="f-price__sale">
            <span class="f-price-item f-price-item--sale">${priceDisplay}</span>
            <span class="f-price-item f-price-item--compare"><s>${comparePriceDisplay}</s></span>
          </div>
        ` : ''}
      </div>
    `;
  }

  /**
   * Swap grid content with chunked rendering to avoid blocking main thread.
   * FIRST chunk is intentionally small (4 cards) so the initial paint after a
   * pagination/facet refine happens within ~16-30ms instead of 300-500ms from
   * a 12-card innerHTML blast (the previous hot path flagged by Sentry INP).
   * Subsequent chunks are larger (8 cards) to finish filling the grid quickly.
   * Every chunk — including the first — renders inside requestAnimationFrame so
   * the browser can paint and process input between chunks. Pins grid height
   * to prevent CLS while chunks arrive.
   *
   * @param {HTMLElement} grid - The product list container
   * @param {string|string[]} html - HTML string or array of card HTML strings
   */
  swapGridContent(grid, html) {
    // Cancel any in-progress chunked render
    if (this._renderAbort) this._renderAbort.abort();

    const cards = Array.isArray(html) ? html : null;
    const FIRST_CHUNK = 4;
    const CHUNK_SIZE = 8;

    // Non-array HTML (no-results message, etc.) — single swap, no chunking
    if (!cards) {
      grid.style.minHeight = `${grid.offsetHeight}px`;
      grid.innerHTML = html;
      requestAnimationFrame(() => { grid.style.minHeight = ''; });
      return;
    }

    // Small card set — no chunking needed
    if (cards.length <= FIRST_CHUNK) {
      grid.style.minHeight = `${grid.offsetHeight}px`;
      grid.innerHTML = cards.join('');
      requestAnimationFrame(() => { grid.style.minHeight = ''; });
      return;
    }

    const controller = new AbortController();
    this._renderAbort = controller;

    grid.style.minHeight = `${grid.offsetHeight}px`;
    // Clear grid synchronously — replaceChildren is faster than innerHTML=''
    // and lets the first paint happen with just the dim loading-state applied,
    // before any new card HTML is parsed. Falls back to innerHTML='' for old
    // browsers without Element.replaceChildren (added 2020+) — fixes
    // SHOPIFY-1Z0 ("grid.replaceChildren is not a function") for the long-tail
    // pre-2020 user agents that still hit collection pages.
    if (typeof grid.replaceChildren === 'function') {
      grid.replaceChildren();
    } else {
      grid.innerHTML = '';
    }

    let i = 0;
    const renderNextChunk = () => {
      if (controller.signal.aborted) return; // new render owns minHeight now
      if (i >= cards.length) {
        requestAnimationFrame(() => { grid.style.minHeight = ''; });
        return;
      }
      const size = i === 0 ? FIRST_CHUNK : CHUNK_SIZE;
      const chunk = cards.slice(i, i + size).join('');
      grid.insertAdjacentHTML('beforeend', chunk);
      i += size;
      requestAnimationFrame(renderNextChunk);
    };
    requestAnimationFrame(renderNextChunk);
  }

  /**
   * Render no results message
   */
  renderNoResults() {
    // The CTA needs to distinguish two different dead ends:
    // - Filters narrowed an otherwise-populated collection to zero results:
    //   "Continue Shopping" sent the customer to the homepage, away from the
    //   very collection they were browsing, when what they actually needed
    //   was their own filter selections cleared (.algolia-reset-filters is
    //   already wired to clearAllFilters() via delegated click handler).
    // - The collection itself has no products at all (no filters active):
    //   there's nothing to clear, so keep the homepage link for that case.
    const hasActiveFilters = this.currentFilters?.length > 0
      || Object.keys(this.disjunctiveRefinements || {}).length > 0
      || this.numericRangeFilters?.size > 0;
    const cta = hasActiveFilters
      ? '<button type="button" class="btn btn--primary algolia-reset-filters" style="margin-top: var(--space-2);">Clear all filters</button>'
      : '<a href="/" class="btn btn--primary" style="margin-top: var(--space-2);">Continue Shopping</a>';
    return `
      <div class="collection-no-results" style="display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: var(--space-12) var(--space-4); gap: var(--space-4); grid-column: 1 / -1;">
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" style="opacity: 0.3;"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>
        <div>
          <p class="h4" style="margin: 0 0 var(--space-2) 0;">No products found</p>
          <p style="margin: 0; opacity: 0.7;">Try adjusting your filters or browse our other collections.</p>
        </div>
        ${cta}
      </div>
    `;
  }

  /**
   * Set up stats connector for product count
   */
  setupStatsConnector() {
    const stats = instantsearch.connectors.connectStats((renderOptions) => {
      const { nbHits } = renderOptions;

      if (!this.dom.productCount) return;

      // Update product count text
      if (nbHits === 0) {
        this.dom.productCount.textContent = 'No products found';
      } else if (nbHits === 1) {
        this.dom.productCount.textContent = '1 product';
      } else {
        this.dom.productCount.textContent = `${nbHits.toLocaleString()} products`;
      }
    });

    this.search.addWidgets([stats({})]);
  }

  /**
   * Set up pagination/infinite scroll based on theme settings
   * Supports: 'number' (classic), 'infinite', 'load_more'
   */
  setupPaginationConnector() {
    const _self = this;

    // Detect pagination type from existing DOM
    const _existingPagination = this.dom.pagination;
    const loadMoreButton = document.querySelector('button[is="load-more-button"]');

    let paginationType = 'number'; // default
    if (loadMoreButton) {
      paginationType = loadMoreButton.getAttribute('type') || 'load_more';
    }

    this.log('Pagination type detected:', paginationType);
    this.paginationType = paginationType;

    if (paginationType === 'infinite' || paginationType === 'load_more') {
      // Use infiniteHits widget for infinite scroll / load more
      this.setupInfiniteHits(paginationType);
    } else {
      // Use standard pagination widget for classic numbered pagination
      this.setupClassicPagination();
    }
  }

  /**
   * Set up infinite scroll / load more pagination
   */
  setupInfiniteHits(type) {
    const self = this;

    // Only set up infinite scroll if there's a products list
    const productsList = this.dom.productsList;
    if (!productsList) {
      this.log('No products list found - skipping infinite hits setup');
      return;
    }

    // Find or create container for load more button
    let loadMoreContainer = document.querySelector('.pagination');
    if (!loadMoreContainer) {
      loadMoreContainer = document.createElement('div');
      loadMoreContainer.className = 'pagination algolia-pagination text-center';
      if (productsList.parentNode) {
        productsList.parentNode.insertBefore(loadMoreContainer, productsList.nextSibling);
      }
    } else {
      // Drop Liquid's SSR contents — in load_more/infinite mode that's a live
      // <button is="load-more-button">, and show-more.js would fetch and append
      // Shopify HTML into #ProductsList alongside the Algolia hits if it were
      // clicked before the first Algolia render.
      loadMoreContainer.innerHTML = '';
      loadMoreContainer.classList.add('algolia-pagination');
    }

    // Store reference
    this.dom.pagination = loadMoreContainer;

    // Use infiniteHits connector to handle load more / infinite scroll
    const infiniteHits = instantsearch.connectors.connectInfiniteHits((renderOptions, isFirstRender) => {
      const { hits, results, showMore, isLastPage } = renderOptions;

      self.log('InfiniteHits render:', {
        hitsCount: hits?.length,
        isFirstRender,
        isLastPage,
        totalHits: results?.nbHits
      });

      // Render products
      if (hits && hits.length > 0) {
        // Determine if this is a new search or a "load more" action
        // New search: isFirstRender OR hits.length <= lastHitsCount (results reset)
        // Load more: hits.length > lastHitsCount (more results appended)
        const isNewSearch = isFirstRender || !self.lastHitsCount || hits.length <= self.lastHitsCount;

        if (isNewSearch) {
          // New search - replace content entirely (pin height to prevent CLS)
          self.swapGridContent(self.dom.productsList, hits.map((hit, i) => self.renderProductCard(hit, i)));
          window.AlgoliaShared?.restoreScroll(self.dom.productsList);
          self.log('Replaced content with', hits.length, 'products (new search)');
        } else {
          // Load more - append only new content
          const newHtml = hits.slice(self.lastHitsCount).map((hit) => self.renderProductCard(hit)).join('');
          self.dom.productsList.insertAdjacentHTML('beforeend', newHtml);
          self.log('Appended', hits.length - self.lastHitsCount, 'products (load more)');
        }
        // Sync ItemList JSON-LD with the cumulative visible hits.
        if (window.AlgoliaShared?.updateCollectionSchema) {
          window.AlgoliaShared.updateCollectionSchema(hits);
        }
        self.lastHitsCount = hits.length;
        self.log('Total rendered:', hits.length, 'products');
      } else if (hits && hits.length === 0 && !isFirstRender) {
        // No results - clear the grid and show empty message
        // Only show on subsequent renders - keeps skeleton visible during first load
        self.swapGridContent(self.dom.productsList, self.renderNoResults());
        window.AlgoliaShared?.restoreScroll(self.dom.productsList);
        self.lastHitsCount = 0;
        self.log('No products to display');
      } else if (isFirstRender && hits && hits.length === 0) {
        // First render with no hits - keep skeleton visible
        self.log('First render with no hits - keeping skeleton visible');
      }

      // Update grid layout
      self.updateGridLayout(self.dom.productsList);

      // Update product count
      self.updateProductCount(results?.nbHits || 0);

      // Update active filter chips synchronously — visible above the grid
      self.updateActiveFilters();

      // Defer facet sidebar rebuild past the next paint — it's offscreen on
      // mobile (drawer) and behind the fold on desktop, and its innerHTML
      // rebuild dominates INP on collections with many checkboxes. See the
      // matching deferral in renderHits (~line 1490) for full rationale.
      const _runIdleIH = window.requestIdleCallback || ((cb) => setTimeout(cb, 0));
      _runIdleIH(() => {
        self.updateFacetCounts(results);
      }, { timeout: 500 });

      // Render load more button or set up infinite scroll
      if (isLastPage) {
        loadMoreContainer.innerHTML = '';
        loadMoreContainer.style.display = 'none';
      } else {
        loadMoreContainer.style.display = '';

        if (type === 'load_more') {
          // Show "Load More" button
          loadMoreContainer.innerHTML = `
            <button class="btn btn--primary algolia-load-more" type="button">
              <span>${window.theme?.strings?.show_more || 'Show more'}</span>
            </button>
          `;
          const btn = loadMoreContainer.querySelector('.algolia-load-more');
          btn.addEventListener('click', () => {
            btn.classList.add('loading');
            btn.disabled = true;
            showMore();
          });
        } else {
          // Infinite scroll - set up intersection observer
          loadMoreContainer.innerHTML = '<div class="algolia-infinite-trigger" style="height: 1px;"></div>';
          const trigger = loadMoreContainer.querySelector('.algolia-infinite-trigger');

          if (self.infiniteObserver) {
            self.infiniteObserver.disconnect();
          }

          self.infiniteObserver = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !isLastPage) {
              self.log('Infinite scroll triggered');
              showMore();
            }
          }, { rootMargin: '200px' });

          self.infiniteObserver.observe(trigger);
        }
      }

      // setupClickTracking and reinitializeWishlist run synchronously to
      // wire interaction handlers to the freshly-rendered cards. See the
      // matching block in renderHits (~line 1490) for full reasoning —
      // deferring these leaves a window where clicks on new cards might
      // miss analytics or hit a stale-state wishlist handler.
      self.reinitializeWishlist();
      self.setupClickTracking(hits);

      // Defer the heavy non-critical work (facet sidebar rebuild, theme
      // feature reinit) past the next paint.
      const _runIdleIH2 = window.requestIdleCallback || ((cb) => setTimeout(cb, 0));
      _runIdleIH2(() => {
        self.reinitializeThemeFeatures();
      }, { timeout: 1000 });
    });

    // Remove the regular hits widget since infiniteHits handles rendering
    // We need to NOT add the regular hits widget when using infinite
    this.useInfiniteHits = true;

    this.search.addWidgets([infiniteHits({})]);
    this.log('Infinite hits widget added (type:', type, ')');
  }

  /**
   * Set up classic numbered pagination
   */
  setupClassicPagination() {
    const _self = this;

    // Create or find pagination container
    let paginationContainer = this.dom.pagination;

    if (!paginationContainer) {
      // Try multiple insertion points to ensure container is in DOM
      const productsList = this.dom.productsList;
      const productGrid = this.dom.productGrid;

      // Only create pagination if there's a product grid - don't add to pages without one
      if (!productsList && !productGrid) {
        this.log('No product grid found - skipping pagination setup');
        return;
      }

      paginationContainer = document.createElement('div');
      paginationContainer.id = 'AlgoliaPagination';
      paginationContainer.className = 'pagination algolia-pagination';

      if (productsList && productsList.parentNode) {
        productsList.parentNode.insertBefore(paginationContainer, productsList.nextSibling);
        this.log('Created Algolia pagination container after ProductsList');
      } else if (productGrid) {
        // Fallback: append to product grid container
        productGrid.appendChild(paginationContainer);
        this.log('Created Algolia pagination container inside ProductGridContainer (fallback)');
      }
    } else {
      // Reusing the server-rendered <nav class="pagination">. Empty it first: it
      // arrives holding Liquid's real ?page=N links (kept for crawlers, see
      // main-collection-product-grid.liquid), and the InstantSearch widget renders
      // via preact, which would otherwise diff against those existing children.
      paginationContainer.innerHTML = '';
      paginationContainer.classList.add('algolia-pagination');
      this.log('Using existing pagination container');
    }

    // Verify container is in DOM
    if (!document.body.contains(paginationContainer)) {
      console.error('[Algolia] Pagination container is not in DOM! Widget will not function correctly.');
    }

    this.dom.pagination = paginationContainer;

    // Use the standard pagination widget
    this.search.addWidgets([
      instantsearch.widgets.pagination({
        container: paginationContainer,
        padding: 2,
        showFirst: false,
        showLast: false,
        scrollTo: false, // Disabled - using custom scroll in hits connector for proper timing
        templates: {
          previous: `<svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 1L1 7L7 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
          next: `<svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
        },
        cssClasses: {
          root: 'ais-Pagination--theme',
          list: 'pagination__list flex items-center justify-center gap-1',
          item: 'pagination__item',
          selectedItem: 'pagination__item--current',
          disabledItem: 'pagination__item--disabled',
          link: 'pagination__link',
        },
      }),
    ]);

    this.log('Classic pagination widget added');

    // Capture-phase click handler: scroll IMMEDIATELY when user clicks a
    // pagination link, so the scroll happens within the layout-shift observer's
    // hadRecentInput window (500 ms post-input). The deferred render-callback
    // scroll triggered by `pageChanged` runs after Algolia's network round-trip
    // (~300-500 ms), well outside that window. Set _scrollAlreadyDone so the
    // deferred path skips its scrollTo and we don't double-scroll.
    paginationContainer.addEventListener('click', (e) => {
      if (e.target.closest('a, button')) {
        this.scrollToProductGrid();
        this._scrollAlreadyDone = true;
      }
    }, { capture: true });

    // Fix accessibility: Remove aria-label from non-interactive span elements
    // Algolia's pagination widget adds aria-label to disabled span elements, which is invalid HTML
    const fixPaginationA11y = () => {
      paginationContainer.querySelectorAll('span.ais-Pagination-link[aria-label]').forEach((span) => {
        span.removeAttribute('aria-label');
      });
    };

    // Run fix after each search completes
    this.search.on('render', fixPaginationA11y);

    // Also run immediately in case pagination is already rendered
    fixPaginationA11y();

    // routing is off, so the widget writes href="#" on its page links. Point them at
    // the real ?page=N URL so a middle-click opens that page and a crawler that
    // renders JavaScript can follow them, as it can the server-rendered links. Every
    // render rewrites them all: the widget reuses the previous and next anchors.
    const fixPaginationLinks = () => {
      const selected = paginationContainer.querySelector('.ais-Pagination-item--selected a');
      const current = Number(selected?.textContent) || 1;
      const hrefFor = (page) => {
        const url = new URL(window.location.href);
        if (page > 1) {
          url.searchParams.set('page', page);
        } else {
          url.searchParams.delete('page');
        }
        return url.pathname + url.search;
      };
      paginationContainer.querySelectorAll('a.ais-Pagination-link').forEach((link) => {
        const item = link.closest('.ais-Pagination-item');
        let page = Number((link.getAttribute('aria-label') || '').replace(/\D/g, ''));
        if (item?.classList.contains('ais-Pagination-item--previousPage')) page = current - 1;
        if (item?.classList.contains('ais-Pagination-item--nextPage')) page = current + 1;
        if (page >= 1) link.setAttribute('href', hrefFor(page));
      });
    };
    this.search.on('render', fixPaginationLinks);
    fixPaginationLinks();
  }

  /**
   * Go to a redirect rule's page, recording the search as redirected.
   * @param {{query: string, url: string, resultsCount: number}} redirect
   */
  followSearchRedirect(redirect) {
    this.log('Redirect rule matched, navigating to:', redirect.url);
    window.AlgoliaShared?.reportRedirect(redirect.query, redirect.resultsCount, 'collection');
    window.location.href = redirect.url;
  }

  /**
   * Set up collection search box connector
   * Allows searching within the current collection
   */
  setupCollectionSearchBox() {
    const searchInput = document.getElementById('AlgoliaCollectionSearchInput');
    if (!searchInput) {
      this.log('Collection search input not found, skipping');
      return;
    }

    const self = this;

    // Read initial search query from URL
    const urlParams = new URLSearchParams(window.location.search);
    const initialQuery = urlParams.get('q') || '';
    if (initialQuery) {
      searchInput.value = initialQuery;
      this.log('Initial search query from URL:', initialQuery);
    }

    // Helper to update URL with search query
    const updateSearchQueryInURL = (query) => {
      const url = new URL(window.location.href);
      if (query && query.trim()) {
        url.searchParams.set('q', query.trim());
      } else {
        url.searchParams.delete('q');
      }
      history.replaceState({}, '', url.toString());
      self.log('URL updated with search query:', query || '(cleared)');
    };

    // Create a custom search box using the connectSearchBox connector
    const searchBox = instantsearch.connectors.connectSearchBox((renderOptions, isFirstRender) => {
      const { query, refine } = renderOptions;

      if (isFirstRender) {
        // Apply initial query from URL if present
        if (initialQuery) {
          refine(initialQuery);
        }

        // Set up input event listener
        searchInput.addEventListener('input', (e) => {
          // Debounce the search - use instance property for cleanup
          clearTimeout(self._searchDebounceTimer);
          self._searchDebounceTimer = setTimeout(() => {
            const searchValue = e.target.value;
            // Defer scroll to the render callback — chip strip height changes
            // when the search resolves, so measuring before refine() is stale.
            self._scrollOnNextRender = true;
            refine(searchValue);
            updateSearchQueryInURL(searchValue);
            self.log('Collection search query:', searchValue || '(empty)');
          }, 300);
        });

        // Handle clear on escape
        searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            searchInput.value = '';
            refine('');
            updateSearchQueryInURL('');
          }
        });

        // Enter follows a redirect rule for the query (e.g. "fan" to Ceiling
        // Fans). If the pause-search for this text hasn't answered yet, search
        // now and follow a redirect if one comes back. The box has no form.
        searchInput.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' || e.isComposing) return;
          e.preventDefault();
          const value = searchInput.value.trim();
          if (!value) return;
          const key = value.toLowerCase();
          if (self.searchRedirect && self.searchRedirect.query.toLowerCase() === key) {
            self.followSearchRedirect(self.searchRedirect);
            return;
          }
          if (self.lastResponseQuery === key) return;
          self.redirectOnResponse = key;
          if ((self.search.helper.state.query || '').trim().toLowerCase() !== key) {
            clearTimeout(self._searchDebounceTimer);
            refine(searchInput.value);
            updateSearchQueryInURL(searchInput.value);
          }
        });
      }

      // Sync input value with current query state
      if (searchInput.value !== query) {
        searchInput.value = query || '';
      }
    });

    this.search.addWidgets([searchBox({})]);
    this.log('Collection search box set up');
  }

  /**
   * Wire sorting dropdown
   */
  wireSorting() {
    const wireSelect = (select) => {
      if (!select) return;

      this.log('Wiring sort select:', select.id || select.name);

      // Set initial value
      if (this.currentSort && select.value !== this.currentSort) {
        select.value = this.currentSort;
        this.log('Set initial sort value to:', this.currentSort);
      }

      // Add change listener with capture to intercept before native handlers
      select.addEventListener('change', (e) => {
        e.stopPropagation();
        
        this.currentSort = e.target.value;
        const newIndex = this.getIndexForSort(this.currentSort);

        this.log('Sort changed to:', this.currentSort, '-> index:', newIndex);

        // Update URL
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', this.currentSort);
        history.replaceState({}, '', url.toString());

        // Switch index and search
        this.search.helper.setIndex(newIndex).search();

        // Sync other sort selects
        this.syncSortSelects(e.target);

        // Scroll IMMEDIATELY within the input event window. See applyFacetFilters
        // (~line 4525) for full rationale — TL;DR: deferring the scroll to the
        // render callback puts it >500ms after user input, where the layout-shift
        // observer counts the resulting position changes as CLS. Calling
        // scrollToProductGrid here keeps it input-attributed and excluded.
        this.scrollToProductGrid();
        this._scrollAlreadyDone = true;
      }, { capture: true });
    };

    wireSelect(this.dom.sortSelect);
    wireSelect(this.dom.sortSelectMobile);
    
    // Log available sort indices
    this.log('Sort indices configured:', this.config.sortIndices);
  }

  /**
   * Sync sort selects (desktop and mobile)
   */
  syncSortSelects(changedSelect) {
    const allSelects = document.querySelectorAll('select[is="facet-short"]');
    allSelects.forEach((select) => {
      if (select !== changedSelect && select.value !== this.currentSort) {
        select.value = this.currentSort;
      }
    });
  }

  /**
   * Detect and handle layout switching
   */
  detectLayout() {
    const layoutSwitcher = document.querySelector('layout-switcher');
    if (!layoutSwitcher) return;

    // Detect current layout
    const listBtn = layoutSwitcher.querySelector('[data-layout-mode="list"]');
    if (listBtn?.classList.contains('btn--active')) {
      this.currentLayout = 'list';
    }

    // Listen for layout changes
    layoutSwitcher.querySelectorAll('button').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.currentLayout = btn.dataset.layoutMode || 'grid';
        this.log('Layout changed to:', this.currentLayout);

        // Update grid immediately without re-searching
        const productsList =
          this.dom.productsList || this.dom.productGrid?.querySelector('#ProductsList, .products-list');
        if (productsList) {
          this.updateGridLayout(productsList);
        }
      });
    });
  }

  /**
   * Update grid layout classes
   */
  updateGridLayout(container) {
    if (!container) return;

    if (this.currentLayout === 'list') {
      container.classList.remove('products-list--list-grid');
      container.classList.add('products-list--list-list');
    } else {
      container.classList.remove('products-list--list-list');
      container.classList.add('products-list--list-grid');
    }
  }

  /**
   * Show loading state
   */
  showLoadingState() {
    const loadings = document.querySelectorAll('[data-facet-loading]');
    loadings.forEach((el) => el.classList.add('btn--loading'));

    if (this.dom.productGrid) {
      this.dom.productGrid.style.opacity = '0.5';
      this.dom.productGrid.style.pointerEvents = 'none';
    }
  }

  /**
   * Hide loading state
   */
  hideLoadingState() {
    const loadings = document.querySelectorAll('[data-facet-loading]');
    loadings.forEach((el) => el.classList.remove('btn--loading'));

    if (this.dom.productGrid) {
      this.dom.productGrid.style.opacity = '1';
      this.dom.productGrid.style.pointerEvents = 'auto';
    }
  }

  /**
   * Reinitialize wishlist buttons after dynamic rendering
   * Supports common wishlist apps: Swym, Wishlist King, Growave, etc.
   */
  reinitializeWishlist() {
    setTimeout(() => {
      // Swym/Swat.io wishlist
      if (window._swat?.initializeActionButtons) {
        window._swat.initializeActionButtons();
        this.log('Swym wishlist reinitialized');
      }

      // Wishlist King (wlk)
      if (window.WishlistKing?.toolkit?.refresh) {
        window.WishlistKing.toolkit.refresh();
        this.log('Wishlist King reinitialized');
      } else if (window.wk?.update) {
        window.wk.update();
        this.log('Wishlist King (wk) reinitialized');
      }

      // Growave wishlist
      if (window.Growave?.wishlist?.init) {
        window.Growave.wishlist.init();
        this.log('Growave wishlist reinitialized');
      }

      // Dispatch custom event for other wishlist apps
      document.dispatchEvent(new CustomEvent('wishlist:refresh'));
      document.dispatchEvent(new CustomEvent('wlk:refresh'));

    }, 150);
  }

  /**
   * Reinitialize theme features after rendering
   */
  reinitializeThemeFeatures() {
    setTimeout(() => {
      // Lazy loading
      if (window.lazySizes?.autoSizer) {
        lazySizes.autoSizer.checkElems();
      }

      // Color swatches
      if (window.theme?.initSwatches) {
        window.theme.initSwatches();
      }

      // Product compare
      if (window.theme?.initCompare) {
        window.theme.initCompare();
      }

      // Currency converter
      if (window.Currency?.convertAll) {
        window.Currency.convertAll(window.Shopify?.currency?.active, this.themeConfig.moneyFormat);
      }

      // Note: FoxTheme.Motion.inView() requires (element, callback) args.
      // Product card animations are handled by their own custom elements.

      // Mobile wishlist button handlers
      this.initMobileWishlistButtons();

      // Dispatch events for third-party apps
      document.dispatchEvent(new CustomEvent('collection:rerendered'));
      document.dispatchEvent(new CustomEvent('algolia:collection:rendered'));
      document.dispatchEvent(new CustomEvent('algolia:hits:rendered'));

      this.log('Theme features reinitialized');
    }, 100);
  }

  /**
   * Initialize mobile wishlist button click handlers
   * Clicks find and trigger the corresponding Wishlist King button
   */
  initMobileWishlistButtons() {
    const mobileWishlistBtns = document.querySelectorAll('.product-card__mobile-wishlist:not([data-initialized])');

    mobileWishlistBtns.forEach(btn => {
      btn.setAttribute('data-initialized', 'true');
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const productCard = btn.closest('.product-card');
        if (!productCard) return;

        // Find the Wishlist King button in the same card
        const wkButton = productCard.querySelector('.wishlist-button-block button, .wishlist-button-block a, [data-wk-button], .wk-button');

        if (wkButton) {
          wkButton.click();
          // Update our button state after a short delay
          setTimeout(() => {
            const isAdded = wkButton.closest('.wishlist-button-block')?.classList.contains('wk-added') ||
                           wkButton.classList.contains('wk-added');
            btn.classList.toggle('is-in-wishlist', isAdded);
          }, 300);
        } else {
          // Fallback: try using WishlistKing API directly
          const productId = btn.dataset.productId;
          if (productId && window.WishlistKing?.toolkit) {
            window.WishlistKing.toolkit.addProduct({ id: productId });
            btn.classList.add('is-in-wishlist');
          }
        }
      });
    });
  }


  /**
   * Get merged facet configuration from window.algoliaFacetConfig
   * Applies collection overrides and filters hidden facets
   */
  getFacetConfig() {
    const config = window.algoliaFacetConfig || {};
    let facets = [...(config.facets || [])];

    // Apply collection overrides if present
    if (config.collectionOverrides && Array.isArray(config.collectionOverrides)) {
      config.collectionOverrides.forEach((override) => {
        const existingIndex = facets.findIndex((f) => f.attribute === override.attribute);
        if (existingIndex >= 0) {
          // Merge override into existing facet
          facets[existingIndex] = { ...facets[existingIndex], ...override };
        } else {
          // Add new facet from override
          facets.push(override);
        }
      });
    }

    // Auto-show blade filters and hide finish/color on fan collections and search pages
    if (config.isFanContext) {
      const showOnFan = [
        'meta.custom.nav_blade_span',
        'meta.custom.nav_number_blades',
        'meta.custom.nav_blade_finish',
        'meta.custom.nav_housing_finish',
      ];
      const hideOnFan = [
        'meta.custom.nav_finish',
        'meta.custom.search_color_1',
      ];
      // Move blade-related filters higher (near where color would be)
      const reorderOnFan = {
        'meta.custom.nav_blade_span': 11,
        'meta.custom.nav_blade_finish': 12,
        'meta.custom.nav_housing_finish': 13,
      };
      facets.forEach((f) => {
        if (showOnFan.includes(f.attribute)) {
          f.hidden = false;
        }
        if (hideOnFan.includes(f.attribute)) {
          f.hidden = true;
        }
        if (reorderOnFan[f.attribute] !== undefined) {
          f.order = reorderOnFan[f.attribute];
        }
      });
    }

    // Filter out hidden facets (from config or collection metafield)
    const hiddenFacets = (config.collectionHiddenFacets || '').toLowerCase().split(',').map((s) => s.trim()).filter(Boolean);
    const pageContext = config.pageContextType || 'default';

    facets = facets.filter((f) => {
      if (f.hidden) return false;
      if (hiddenFacets.includes(f.attribute.toLowerCase())) return false;
      // Filter out searchOnly facets when on collection pages (except "all" collection which acts like a browse page)
      if (f.searchOnly && pageContext === 'collection' && this.collectionHandle !== 'all') return false;
      return true;
    });

    // Sort by order (use nullish coalescing to handle order: 0 correctly)
    facets.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

    return {
      facets,
      settings: config.settings || {},
    };
  }

  /**
   * Render facets entirely from Algolia data
   * This replaces Shopify's server-rendered facets with Algolia-powered ones
   * @param {Object} results - Algolia search results containing facets
   */
  updateFacetCounts(results) {
    // Find ALL facet containers (there may be multiple: drawer, sidebar, etc.)
    const containers = document.querySelectorAll('.algolia-facets-container');
    if (containers.length === 0) {
      this.log('algolia-facets-container not found - Algolia facets not enabled in theme');
      return;
    }

    this.log(`Found ${containers.length} facet container(s) to update`);

    // Floor for the render default: keep groups open that are open right now
    // (e.g. because of an active filter) so deselecting a filter's last value
    // doesn't collapse the group. Explicit collapses still win via storage.
    this._captureOpenFacetKeys(containers);

    if (!results) {
      this.log('No results available for facets');
      containers.forEach(container => {
        container.innerHTML = '<div class="text-subtext">No filters available</div>';
      });
      return;
    }

    // Extract all facet data from Algolia results
    const allFacets = this.extractFacetData(results);
    this.log('Rendering facets from Algolia:', Object.keys(allFacets));

    // Get current URL params to determine active filters
    const urlParams = new URLSearchParams(window.location.search);
    const activeFilters = this.getActiveFiltersFromURL(urlParams);

    // Get facet configuration (with collection overrides applied)
    const facetConfig = this.getFacetConfig();
    const configuredFacets = facetConfig.facets || [];
    const settings = facetConfig.settings || {};

    this.log('Facet config loaded:', configuredFacets.map((f) => f.attribute));

    // Get all facet stats for range facets (price, height, width, etc.)
    const allFacetStats = results._rawResults?.[0]?.facets_stats || {};

    // Update EACH container with its own unique IDs to avoid duplicates.
    // Rendering a container is the expensive part (HTML string build +
    // innerHTML parse + listener wiring, ~100ms+ on mid mobile with many
    // facets), and there are usually two containers (desktop sidebar +
    // mobile drawer) of which only one is visible. Render the visible
    // container(s) in this task; push each hidden one to its own idle
    // slice so the combined work never forms a single long task that
    // delays the user's next tap. A token guards against a newer render
    // overwriting deferred work with stale results.
    const updateToken = (this._facetUpdateToken = (this._facetUpdateToken || 0) + 1);
    const runIdle = window.requestIdleCallback || ((cb) => setTimeout(cb, 0));
    const renderContainer = (container) => {
      const context = container.getAttribute('data-context') || 'vertical';
      let html = '';

      // Render each configured facet that has data (in configured order)
      configuredFacets.forEach((facetDef) => {
        // Handle range facets (price, height, width, etc.)
        if (facetDef.type === 'range') {
          // Get stats for this range attribute
          const stats = allFacetStats[facetDef.attribute];
          if (stats) {
            html += this.renderRangeFacet(facetDef, stats, urlParams, settings, context);
          } else {
            this.log(`Skipping range facet ${facetDef.attribute} - no stats available`);
          }
          return;
        }

        const facetValues = allFacets[facetDef.attribute];
        if (!facetValues || Object.keys(facetValues).length === 0) {
          this.log(`Skipping facet ${facetDef.attribute} - no values`);
          return;
        }

        // Skip facets with only one option (except availability/in-stock facet)
        const valueCount = Object.keys(facetValues).length;
        if (valueCount === 1 && facetDef.attribute !== 'meta.custom.stock_filter' && facetDef.attribute !== 'meta.custom.discount_group_tag') {
          this.log(`Skipping facet ${facetDef.attribute} - only one option`);
          return;
        }

        // Use SEO-friendly urlParam if defined, otherwise fall back to shopifyParam or auto-generated
        const urlParam = facetDef.urlParam || facetDef.shopifyParam || this.getShopifyParamForAlgoliaAttr(facetDef.attribute);
        const activeValues = activeFilters[urlParam] || [];

        html += this.renderFacetGroup(facetDef, urlParam, facetValues, activeValues, settings, context);
      });

      if (html) {
        container.innerHTML = html;
        this.initializeFacetInteractions(container);
        this.log(`Updated facets in container (context: ${context})`);
      } else {
        container.innerHTML = '<div class="text-subtext">No filters available</div>';
      }
    };

    const visible = [];
    const hidden = [];
    containers.forEach((container) => {
      // checkVisibility covers display:none subtrees and the [hidden]
      // attribute the facet drawer uses; fall back to offsetParent on
      // older engines.
      const isVisible = container.checkVisibility
        ? container.checkVisibility()
        : container.offsetParent !== null;
      (isVisible ? visible : hidden).push(container);
    });
    visible.forEach(renderContainer);
    this._pendingFacetRenders = [];
    hidden.forEach((container) => {
      const job = { token: updateToken, done: false, container, run: () => renderContainer(container) };
      this._pendingFacetRenders.push(job);
      runIdle(() => this._runFacetRenderJob(job), { timeout: 500 });
    });
    this._bindFacetDrawerFlush();
  }

  /**
   * Run a deferred hidden-container facet render exactly once, dropping it
   * if a newer updateFacetCounts call has superseded its results.
   */
  _runFacetRenderJob(job) {
    if (job.done) return;
    job.done = true;
    if (this._facetUpdateToken !== job.token) return;
    job.run();
  }

  /**
   * A drawer can open before the idle callback for its deferred facet render
   * fires, which would briefly show pre-filter counts. Flush pending renders
   * the moment a drawer containing a facet container finishes opening.
   */
  _bindFacetDrawerFlush() {
    if (this._facetDrawerFlushBound) return;
    this._facetDrawerFlushBound = true;
    this._facetDrawerFlushHandler = (e) => {
      if (!e.target?.querySelector?.('.algolia-facets-container')) return;
      // Only flush jobs for containers inside the drawer that just opened —
      // flushing everything would render the off-screen desktop sidebar in
      // the same tick and recreate the long task this defer exists to avoid.
      this._pendingFacetRenders = (this._pendingFacetRenders || []).filter((job) => {
        if (e.target.contains(job.container)) {
          this._runFacetRenderJob(job);
          return false;
        }
        return !job.done;
      });
    };
    document.addEventListener('drawer:handleAfterShow', this._facetDrawerFlushHandler);
  }

  /**
   * Extract facet data from Algolia results
   */
  extractFacetData(results) {
    const allFacets = {};

    // Get regular facets
    const rawFacets = results._rawResults?.[0]?.facets || {};
    Object.assign(allFacets, rawFacets);

    // Get disjunctive facets (array-like structure)
    if (results.disjunctiveFacets && results.disjunctiveFacets.length > 0) {
      results.disjunctiveFacets.forEach((facetData) => {
        if (facetData.name && facetData.data) {
          allFacets[facetData.name] = facetData.data;
        }
      });
    }

    // Also check raw disjunctive facets
    const rawDisjunctiveFacets = results._rawResults?.[0]?.disjunctiveFacets || {};
    Object.entries(rawDisjunctiveFacets).forEach(([facetName, facetData]) => {
      if (!allFacets[facetName]) {
        allFacets[facetName] = facetData;
      }
    });

    return allFacets;
  }

  /**
   * Get active filters from URL parameters
   * Supports both SEO-friendly params (e.g., ?style=Modern) and legacy Shopify params
   */
  getActiveFiltersFromURL(urlParams) {
    const activeFilters = {};
    for (const [key, value] of urlParams.entries()) {
      // Check if this is a SEO-friendly param (mapped in urlParamToAttribute)
      if (this.urlParamToAttribute && this.urlParamToAttribute[key]) {
        // Use the urlParam as the key (for matching with rendered facets)
        if (!activeFilters[key]) {
          activeFilters[key] = [];
        }
        activeFilters[key].push(value);
      }
      // Also support legacy filter.* params for backwards compatibility
      else if (key.startsWith('filter.')) {
        if (!activeFilters[key]) {
          activeFilters[key] = [];
        }
        activeFilters[key].push(value);
      }
    }
    return activeFilters;
  }

  /**
   * Get Shopify param name for an Algolia attribute
   */
  getShopifyParamForAlgoliaAttr(algoliaAttr) {
    const mapping = this.getFilterMapping();
    for (const [shopifyParam, attr] of Object.entries(mapping)) {
      if (attr === algoliaAttr) {
        return shopifyParam;
      }
    }
    // Fallback: convert options.X to filter.v.option.X
    if (algoliaAttr.startsWith('options.')) {
      return `filter.v.option.${algoliaAttr.replace('options.', '')}`;
    }
    return `filter.p.${algoliaAttr}`;
  }

  /**
   * Get human-readable label for a facet
   */
  getFacetLabel(algoliaAttr) {
    const labels = {
      'vendor': 'Brand',
      'product_type': 'Product Type',
      'tags': 'Tags',
      'options.color': 'Color',
      'options.size': 'Size',
      'options.material': 'Material',
      'inventory_available': 'Availability',
    };
    return labels[algoliaAttr] || algoliaAttr.replace('options.', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  /**
   * Get facet ordering configuration from Algolia renderingContent
   * This respects the facetOrdering settings configured in Algolia dashboard
   * @param {string} attribute - The Algolia attribute name
   * @returns {Object|null} - Ordering config with order, hide, sortRemainingBy
   */
  getFacetOrdering(attribute) {
    try {
      const helper = this.search?.helper;
      if (!helper || !helper.lastResults) {
        return null;
      }

      const rawResults = helper.lastResults._rawResults?.[0];
      const facetOrdering = rawResults?.renderingContent?.facetOrdering;

      if (!facetOrdering?.values) {
        return null;
      }

      // Check for exact attribute match first
      if (facetOrdering.values[attribute]) {
        return facetOrdering.values[attribute];
      }

      // Also check common attribute mappings (e.g., vendor -> meta.custom.brand_name)
      const attributeMappings = {
        'vendor': 'meta.custom.brand_name',
        'meta.custom.brand_name': 'vendor',
      };

      const mappedAttr = attributeMappings[attribute];
      if (mappedAttr && facetOrdering.values[mappedAttr]) {
        return facetOrdering.values[mappedAttr];
      }

      return null;
    } catch (err) {
      this.log('Error getting facet ordering:', err);
      return null;
    }
  }

  /**
   * Render a single facet group - matches native Shopify facets.liquid structure
   * @param {Object} facetDef - Facet definition from config
   * @param {string} shopifyParam - Shopify filter param name
   * @param {Object} facetValues - Object of {value: count} from Algolia
   * @param {Array} activeValues - Currently selected values
   * @param {Object} settings - Global facet settings
   * @param {string} context - Container context for unique IDs (e.g., 'drawer', 'vertical')
   */
  renderFacetGroup(facetDef, shopifyParam, facetValues, activeValues, settings = {}, context = 'vertical') {
    const filterId = `${context}-${shopifyParam}`;
    const hasActiveValues = activeValues.length > 0;
    const isExpanded = this._resolveFacetExpanded(shopifyParam, facetDef.expanded || hasActiveValues || this._wasFacetOpenAtRender(shopifyParam));
    const label = facetDef.label || this.getFacetLabel(facetDef.attribute);
    const limit = facetDef.limit || settings.defaultLimit || 14;
    const isSwatch = facetDef.type === 'swatch';
    const showCounts = facetDef.showCount !== false && settings.showCounts !== false;

    // Sort facet values based on settings and Algolia facetOrdering
    let sortedValues = Object.entries(facetValues)
      .map(([value, count]) => ({
        value,
        count,
        isActive: activeValues.includes(value),
      }));

    // Get facetOrdering from Algolia renderingContent (if available)
    const facetOrdering = this.getFacetOrdering(facetDef.attribute);
    const pinnedValues = facetOrdering?.order || [];
    const hiddenValues = facetOrdering?.hide || [];
    const sortRemainingBy = facetOrdering?.sortRemainingBy || facetDef.sortValues || settings.sortValues || 'count';

    // Filter out hidden values (unless they're active - user explicitly selected them)
    if (hiddenValues.length > 0) {
      sortedValues = sortedValues.filter(item => !hiddenValues.includes(item.value) || item.isActive);
    }

    // Apply sorting: pinned first (in order), then active, then by configured sort method
    sortedValues.sort((a, b) => {
      const aIsPinned = pinnedValues.includes(a.value);
      const bIsPinned = pinnedValues.includes(b.value);

      // Pinned values come first, in the order specified
      if (aIsPinned && bIsPinned) {
        return pinnedValues.indexOf(a.value) - pinnedValues.indexOf(b.value);
      }
      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;

      // Active items next (for non-pinned items)
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;

      // Then by configured method (from Algolia sortRemainingBy or local settings)
      if (sortRemainingBy === 'numeric') {
        // Values like "Number of Lights" (1, 2, 3...), "Blade Span" (42, 44, 52...),
        // "Voltage" (12V, 120V) or "Color Temperature" (2700K, 3000K) are numeric
        // facts with unit suffixes, not names — a plain string sort puts "10" before
        // "2". parseFloat reads the leading numeric portion and ignores the suffix.
        const aNum = parseFloat(a.value);
        const bNum = parseFloat(b.value);
        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
          return aNum - bNum;
        }
        return a.value.localeCompare(b.value);
      }
      if (sortRemainingBy === 'alpha') {
        return a.value.localeCompare(b.value);
      }
      // Default: by count descending
      return b.count - a.count;
    });

    const hasMore = sortedValues.length > limit;

    // Render values based on type (swatch, tree, or list)
    let valuesHtml;
    const isTree = facetDef.type === 'tree';
    if (isSwatch) {
      valuesHtml = this.renderSwatchFacetValues(sortedValues, shopifyParam, filterId, limit, showCounts);
    } else if (isTree) {
      const separator = facetDef.separator || ' > ';
      // On collection pages (except 'all'), filter tree to show only current collection's branch
      const shouldFilterTree = this.collectionHandle && this.collectionHandle !== 'all';
      valuesHtml = this.renderTreeFacetValues(sortedValues, shopifyParam, filterId, limit, showCounts, separator, activeValues, shouldFilterTree);
    } else {
      valuesHtml = this.renderListFacetValues(sortedValues, shopifyParam, filterId, limit, showCounts);
    }

    // Show more disabled for now - show all filter values
    const showMoreHtml = '';

    // Use the exact same icon structure as the native theme (icon-caret-toggle.liquid)
    // Tree facets use a different container structure
    const listClass = isTree ? 'facet-tree' : (isSwatch ? 'swatches grid gap-2 md:gap-3' : 'grid gap-2 md:gap-3');

    // Build clear button for this facet if it has active values
    const clearFacetUrl = hasActiveValues ? this.buildClearFacetUrl(shopifyParam) : '';
    const clearButton = hasActiveValues ? `
      <a href="${clearFacetUrl}" class="facet-clear-single btn--link text-sm" is="facet-remove" aria-label="Clear ${this.escapeAttr(label)} filters">
        Clear
      </a>
    ` : '';

    // Check for alwaysOpen and hideHeader options
    const alwaysOpen = facetDef.alwaysOpen === true;
    const hideHeader = facetDef.hideHeader === true;

    // If hideHeader is true, render without accordion wrapper
    if (hideHeader) {
      return `
        <div
          class="facet-group facet-group--no-header${hasActiveValues ? ' has-active-filters' : ''}"
          data-filter
          data-index="${filterId}"
          data-facet-type="${facetDef.type || 'list'}"
        >
          <div class="facet-group__content flex flex-col gap-2 md:gap-3">
            <ul class="${listClass}">
              ${valuesHtml}
            </ul>
            ${isTree ? '' : showMoreHtml}
          </div>
        </div>
      `;
    }

    // Build CSS classes for the accordion
    const accordionClasses = [
      'accordion-details',
      'facet-accordion-details',
      hasActiveValues ? 'has-active-filters' : '',
      alwaysOpen ? 'facet-accordion--always-open' : ''
    ].filter(Boolean).join(' ');

    return `
      <details
        class="${accordionClasses}"
        is="accordion-details"
        data-filter
        data-index="${filterId}"
        data-facet-key="${shopifyParam}"
        data-facet-type="${facetDef.type || 'list'}"
        ${isExpanded || alwaysOpen ? 'open' : ''}
      >
        <summary class="accordion-details__summary flex items-center justify-between"${alwaysOpen ? ' style="pointer-events: none;"' : ''}>
          <span class="facet-header-label flex items-center gap-2">
            <span class="h6">${this.escapeHtml(label)}</span>
            ${clearButton}
          </span>
          ${alwaysOpen ? '' : `
          <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-caret-down icon--small icon--thick" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
            <path d="M16.25 7.5L10 13.75L3.75 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          `}
        </summary>
        <div class="accordion-details__content parent-display flex flex-col gap-2 md:gap-3">
          <ul class="${listClass}">
            ${valuesHtml}
          </ul>
          ${isTree ? '' : showMoreHtml}
        </div>
      </details>
    `;
  }

  /**
   * Render list-style facet values (checkboxes)
   */
  renderListFacetValues(sortedValues, shopifyParam, filterId, limit, showCounts) {
    return sortedValues.map((item, index) => {
      const valueId = `${filterId}-${index}`;
      // Show more disabled - don't hide any items
      const isDisabled = item.count === 0 && !item.isActive;

      return `
        <li class="flex items-center gap-2 md:gap-3">
          <input
            name="${shopifyParam}"
            value="${this.escapeAttr(item.value)}"
            class="checkbox"
            id="${valueId}"
            type="checkbox"
            ${item.isActive ? 'checked' : ''}
            ${isDisabled ? 'disabled' : ''}
          >
          <label
            for="${valueId}"
            class="flex flex-grow justify-between items-center gap-2 text-subtext text-sm reversed-link"
          >
            <span class="reversed-link__text">${this.escapeHtml(item.value)}</span>
            ${showCounts ? `<span class="count text-sm">${item.count}</span>` : ''}
          </label>
        </li>
      `;
    }).join('');
  }

  /**
   * Render swatch-style facet values (color swatches)
   */
  renderSwatchFacetValues(sortedValues, shopifyParam, filterId, limit, showCounts) {
    return sortedValues.map((item, index) => {
      const valueId = `${filterId}-${index}`;
      // Show more disabled - don't hide any items
      const isDisabled = item.count === 0 && !item.isActive;

      // Generate swatch background from color name
      const colorHandle = item.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const swatchStyle = this.getSwatchStyle(item.value, colorHandle);

      return `
        <li data-tooltip class="filter-item">
          <input
            class="visually-hidden"
            name="${shopifyParam}"
            value="${this.escapeAttr(item.value)}"
            type="checkbox"
            id="${valueId}"
            ${isDisabled ? 'disabled' : ''}
            ${item.isActive ? 'checked' : ''}
          >
          <label
            class="flex items-center gap-2 reversed-link"
            for="${valueId}"
          >
            <div
              class="swatch-color block relative no-empty"
              style="${swatchStyle}"
              title="${this.escapeAttr(item.value)}"
            ></div>
            <div class="flex flex-grow justify-between items-center gap-2 text-subtext text-sm">
              <span class="reversed-link__text">${this.escapeHtml(item.value)}</span>
              ${showCounts ? `<span class="count text-sm">${item.count}</span>` : ''}
            </div>
          </label>
        </li>
      `;
    }).join('');
  }

  /**
   * Render tree-style facet values (hierarchical categories)
   * Parses values like "Lamps > Table Lamps" into a collapsible tree
   * @param {boolean} filterToCollection - If true, only show the branch matching current collection
   */
  renderTreeFacetValues(sortedValues, shopifyParam, filterId, limit, showCounts, separator, activeValues, filterToCollection = false) {
    // Filter out "Shop By" items - these are navigation categories, not product categories
    const filteredValues = sortedValues.filter(item => !item.value.startsWith('Shop By'));

    // Build tree structure from flat values
    const tree = this.buildFacetTree(filteredValues, separator, activeValues);

    // If filtering to current collection, use categoryFullPath from collection metafield
    // This gives us the exact tree path like "Wall Lights > Picture Lights"
    if (filterToCollection) {
      const categoryFullPath = this.config?.pageContext?.categoryFullPath;

      if (categoryFullPath) {
        // Find the branch matching the exact category path
        const matchingBranch = this.findTreeBranchByPath(tree, categoryFullPath, separator);
        if (matchingBranch) {
          // Check if branch has children - if not, go up one level to show siblings
          const hasChildren = matchingBranch.children && Object.keys(matchingBranch.children).length > 0;
          if (hasChildren) {
            // Render the matching branch's children as the root level
            return this.renderTreeNodes(matchingBranch.children, shopifyParam, filterId, showCounts, 0, limit);
          } else {
            // Leaf node - go up one level to show siblings with current pre-selected
            const parentBranch = this.findParentBranch(tree, categoryFullPath, separator);
            if (parentBranch && parentBranch.children) {
              // Mark the current category as active/selected
              const currentCategory = categoryFullPath.split(separator).pop();
              if (parentBranch.children[currentCategory]) {
                parentBranch.children[currentCategory].isActive = true;
              }
              return this.renderTreeNodes(parentBranch.children, shopifyParam, filterId, showCounts, 0, limit, currentCategory);
            }
          }
        }
        this.log('No matching branch found for categoryFullPath:', categoryFullPath);
      }

      // Fallback to handle-based matching if no categoryFullPath
      if (this.collectionHandle) {
        const matchingBranch = this.findMatchingTreeBranch(tree, this.collectionHandle);
        if (matchingBranch) {
          // Check if branch has children - if not, go up one level to show siblings
          const hasChildren = matchingBranch.children && Object.keys(matchingBranch.children).length > 0;
          if (hasChildren) {
            return this.renderTreeNodes(matchingBranch.children, shopifyParam, filterId, showCounts, 0, limit);
          } else {
            // Leaf node - find parent and show siblings
            const parentBranch = this.findParentBranchByHandle(tree, this.collectionHandle);
            if (parentBranch && parentBranch.children) {
              return this.renderTreeNodes(parentBranch.children, shopifyParam, filterId, showCounts, 0, limit, matchingBranch.label);
            }
          }
        }
      }
      // If no match found, fall through to show full tree
    }

    // Render tree as nested HTML
    return this.renderTreeNodes(tree.children, shopifyParam, filterId, showCounts, 0, limit);
  }

  /**
   * Find the parent branch of a given path
   * @param {Object} tree - The tree structure
   * @param {string} fullPath - The full path like "Wall Lights > Picture Lights"
   * @param {string} separator - The path separator
   * @returns {Object|null} The parent branch or null
   */
  findParentBranch(tree, fullPath, separator = ' > ') {
    const parts = fullPath.split(separator);
    if (parts.length <= 1) {
      // Already at root level, return tree itself
      return tree;
    }
    // Remove the last part to get parent path
    const parentPath = parts.slice(0, -1).join(separator);
    return this.findTreeBranchByPath(tree, parentPath, separator);
  }

  /**
   * Find the parent branch of a node matched by collection handle
   */
  findParentBranchByHandle(tree, collectionHandle) {
    const normalizedHandle = collectionHandle.replace(/-/g, ' ').toLowerCase();

    // Search through tree to find parent of matching node
    const findParent = (node, parent = null) => {
      for (const [key, child] of Object.entries(node.children || {})) {
        const normalizedKey = key.toLowerCase();
        if (normalizedKey === normalizedHandle) {
          return parent || tree; // Return parent, or tree if at root
        }
        // Check if child's fullValue matches
        if (child.fullValue) {
          const pathParts = child.fullValue.toLowerCase().split(' > ');
          const lastPart = pathParts[pathParts.length - 1].replace(/ /g, '-');
          if (lastPart === collectionHandle || pathParts[pathParts.length - 1] === normalizedHandle) {
            return parent || tree;
          }
        }
        // Recursively search children
        const found = findParent(child, child);
        if (found) return found;
      }
      return null;
    };

    return findParent(tree);
  }

  /**
   * Find a tree branch by its exact full path
   * @param {Object} tree - The tree structure
   * @param {string} fullPath - The full path like "Wall Lights > Picture Lights"
   * @param {string} separator - The path separator (default " > ")
   * @returns {Object|null} The matching branch node or null
   */
  findTreeBranchByPath(tree, fullPath, separator = ' > ') {
    const parts = fullPath.split(separator);
    let current = tree;

    for (const part of parts) {
      if (!current.children || !current.children[part]) {
        // Try case-insensitive match
        const matchingKey = Object.keys(current.children || {}).find(
          key => key.toLowerCase() === part.toLowerCase()
        );
        if (matchingKey) {
          current = current.children[matchingKey];
        } else {
          this.log('Path part not found in tree:', part, 'Available:', Object.keys(current.children || {}));
          return null;
        }
      } else {
        current = current.children[part];
      }
    }

    return current;
  }

  /**
   * Find a tree branch that matches the collection handle
   * Matches by comparing collection handle to tree node keys (case-insensitive, handle-normalized)
   */
  findMatchingTreeBranch(tree, collectionHandle) {
    // Normalize handle for comparison (e.g., "ceiling-fans" -> "ceiling fans")
    const normalizedHandle = collectionHandle.replace(/-/g, ' ').toLowerCase();

    // Check root level first
    for (const [key, node] of Object.entries(tree.children)) {
      const normalizedKey = key.toLowerCase();
      // Check if the key matches the handle (e.g., "Fans" matches "fans")
      if (normalizedKey === normalizedHandle) {
        return node;
      }
      // Check if any child matches (e.g., "Ceiling Fans" matches "ceiling-fans")
      for (const [childKey, childNode] of Object.entries(node.children || {})) {
        const normalizedChildKey = childKey.toLowerCase();
        if (normalizedChildKey === normalizedHandle) {
          return childNode;
        }
        // Check grandchildren for deeper matches
        for (const [grandchildKey, grandchildNode] of Object.entries(childNode.children || {})) {
          const normalizedGrandchildKey = grandchildKey.toLowerCase();
          if (normalizedGrandchildKey === normalizedHandle) {
            return grandchildNode;
          }
        }
      }
    }

    // Also try matching against fullValue paths
    // e.g., collection "ceiling-fans" might match "Fans > Ceiling Fans"
    const findByFullValue = (node) => {
      if (node.fullValue) {
        const pathParts = node.fullValue.toLowerCase().split(' > ');
        const lastPart = pathParts[pathParts.length - 1].replace(/ /g, '-');
        if (lastPart === collectionHandle || pathParts[pathParts.length - 1] === normalizedHandle) {
          return node;
        }
      }
      for (const child of Object.values(node.children || {})) {
        const found = findByFullValue(child);
        if (found) return found;
      }
      return null;
    };

    return findByFullValue(tree);
  }

  /**
   * Build a tree structure from flat facet values with separators
   * Input: [{value: "Lamps > Table Lamps", count: 50, isActive: false}, ...]
   * Output: { children: { "Lamps": { children: { "Table Lamps": { ... } }, ... } } }
   */
  buildFacetTree(items, separator, activeValues) {
    const root = { children: {}, count: 0, leafValues: [] };

    items.forEach(item => {
      const parts = item.value.split(separator);
      let current = root;

      parts.forEach((part, index) => {
        if (!current.children[part]) {
          current.children[part] = {
            children: {},
            count: 0,
            label: part,
            leafValues: [],
            isActive: false,
          };
        }
        current = current.children[part];

        // Store the full item at its leaf position
        if (index === parts.length - 1) {
          current.count = item.count;
          current.isActive = activeValues.includes(item.value);
          current.fullValue = item.value;
        }
      });
    });

    // Second pass: collect all leaf values for parent nodes
    this.collectTreeLeafValues(root);

    return root;
  }

  /**
   * Recursively collect all leaf fullValues into parent nodes
   * This allows parent checkboxes to select all children
   */
  collectTreeLeafValues(node) {
    const leafValues = [];
    let hasActiveChild = false;

    Object.values(node.children).forEach(child => {
      // First, recursively collect from children
      this.collectTreeLeafValues(child);

      // If this child has its own fullValue (it's a leaf), add it
      if (child.fullValue) {
        leafValues.push(child.fullValue);
        if (child.isActive) hasActiveChild = true;
      }

      // Also add any leaf values collected from its children
      if (child.leafValues && child.leafValues.length > 0) {
        leafValues.push(...child.leafValues);
        if (child.hasActiveChild) hasActiveChild = true;
      }
    });

    node.leafValues = leafValues;
    node.hasActiveChild = hasActiveChild;
  }

  /**
   * Render tree nodes as nested HTML lists
   * @param {string} activeCategory - Optional category name to pre-select (for leaf nodes showing siblings)
   */
  renderTreeNodes(children, shopifyParam, filterId, showCounts, depth, limit, activeCategory = null) {
    const childKeys = Object.keys(children);
    if (childKeys.length === 0) return '';

    // Sort by count descending, then alphabetically
    const sortedKeys = childKeys.sort((a, b) => {
      const childA = children[a];
      const childB = children[b];
      // Active items first (including pre-selected category)
      const aIsActive = childA.isActive || a === activeCategory;
      const bIsActive = childB.isActive || b === activeCategory;
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;
      // Then by total count
      const countA = this.getTreeNodeTotalCount(childA);
      const countB = this.getTreeNodeTotalCount(childB);
      if (countB !== countA) return countB - countA;
      return a.localeCompare(b);
    });

    // Apply limit only to root level
    const visibleKeys = depth === 0 ? sortedKeys.slice(0, limit) : sortedKeys;

    // Auto-expand if there's only ONE first-level item with children
    const autoExpandSingleRoot = depth === 0 && visibleKeys.length === 1;

    return visibleKeys.map((key, index) => {
      const child = children[key];
      const hasChildren = Object.keys(child.children).length > 0;
      const totalCount = this.getTreeNodeTotalCount(child);
      // Check if this is the pre-selected current category
      const isCurrentCategory = activeCategory && key === activeCategory;
      // Expand if: has active selection, is current category, OR is the only root item with children
      const isExpanded = child.isActive || child.hasActiveChild || isCurrentCategory || (autoExpandSingleRoot && hasChildren);
      const _valueId = `${filterId}-${depth}-${index}`;

      // For parent nodes, use leafValues JSON; for leaf nodes, use fullValue
      const filterValue = child.fullValue || '';
      // IMPORTANT: Use single quotes for data-leaf-values attribute to preserve JSON double quotes
      // Only escape single quotes and HTML special chars (not double quotes which JSON needs)
      const leafValuesAttr = hasChildren && child.leafValues?.length > 0
        ? `data-leaf-values='${JSON.stringify(child.leafValues).replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}'`
        : '';

      // Parent is considered "checked" if all its leaf values are active
      const allChildrenActive = hasChildren && child.leafValues?.length > 0
        ? this.areAllTreeValuesActive(child)
        : false;
      // Include isCurrentCategory to pre-check the current collection's category
      const isChecked = child.isActive || allChildrenActive || isCurrentCategory;

      const childrenHtml = hasChildren
        ? `<ul class="facet-tree facet-tree--nested" data-depth="${depth + 1}">${this.renderTreeNodes(child.children, shopifyParam, filterId, showCounts, depth + 1, 100)}</ul>`
        : '';

      return `
        <li class="facet-tree__item${hasChildren ? ' facet-tree__item--parent' : ''}${isExpanded ? ' facet-tree__item--expanded' : ''}${isChecked ? ' facet-tree__item--selected' : ''}">
          <div class="facet-tree__row">
            ${hasChildren ? `
              <button type="button" class="facet-tree__toggle" aria-label="Toggle ${this.escapeAttr(key)}">
                <svg class="facet-tree__arrow" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 2L7 5L3 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            ` : '<span class="facet-tree__spacer"></span>'}
            <label class="facet-tree__label">
              <input
                type="checkbox"
                class="facet-tree__checkbox"
                name="${shopifyParam}"
                value="${this.escapeAttr(filterValue)}"
                ${leafValuesAttr}
                ${isChecked ? 'checked' : ''}
              >
              <span class="facet-tree__text">${this.escapeHtml(key)}</span>
              ${showCounts ? `<span class="facet-tree__count">${totalCount}</span>` : ''}
            </label>
          </div>
          ${childrenHtml}
        </li>
      `;
    }).join('');
  }

  /**
   * Get total count for a tree node (including all descendants)
   */
  getTreeNodeTotalCount(node) {
    let total = node.count || 0;
    Object.values(node.children).forEach(child => {
      total += this.getTreeNodeTotalCount(child);
    });
    return total;
  }

  /**
   * Check if all leaf values under a node are active
   */
  areAllTreeValuesActive(node) {
    // If this is a leaf with a fullValue, just check if it's active
    if (node.fullValue) {
      return node.isActive === true;
    }

    // For parent nodes, check all children recursively
    const childKeys = Object.keys(node.children);
    if (childKeys.length === 0) return false;

    return childKeys.every(key => this.areAllTreeValuesActive(node.children[key]));
  }

  /**
   * Get swatch style for a color value
   * Attempts to map color names to CSS colors
   */
  getSwatchStyle(colorName, colorHandle) {
    // Common color mappings
    const colorMap = {
      'black': '#000000',
      'white': '#ffffff',
      'red': '#cc0000',
      'blue': '#0066cc',
      'green': '#006633',
      'yellow': '#ffcc00',
      'orange': '#ff6600',
      'purple': '#660099',
      'pink': '#ff6699',
      'brown': '#663300',
      'grey': '#999999',
      'gray': '#999999',
      'silver': '#c0c0c0',
      'gold': '#ffd700',
      'navy': '#000080',
      'beige': '#f5f5dc',
      'cream': '#fffdd0',
      'tan': '#d2b48c',
      'olive': '#808000',
      'maroon': '#800000',
      'teal': '#008080',
      'coral': '#ff7f50',
      'turquoise': '#40e0d0',
      'ivory': '#fffff0',
      'charcoal': '#36454f',
      'burgundy': '#800020',
      'mint': '#98ff98',
      'lavender': '#e6e6fa',
      'rose': '#ff007f',
      'salmon': '#fa8072',
      'khaki': '#c3b091',
      'natural': '#f5f5dc',
      'clear': 'transparent',
      'multi': 'linear-gradient(135deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #8b00ff)',
      'multicolor': 'linear-gradient(135deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #8b00ff)',
    };

    // Try exact match first
    const lowerName = colorName.toLowerCase();
    if (colorMap[lowerName]) {
      const color = colorMap[lowerName];
      if (color.includes('gradient')) {
        return `background: ${color};`;
      }
      return `--swatch-background: ${color};`;
    }

    // Try to find a partial match
    for (const [key, value] of Object.entries(colorMap)) {
      if (lowerName.includes(key) || key.includes(lowerName)) {
        if (value.includes('gradient')) {
          return `background: ${value};`;
        }
        return `--swatch-background: ${value};`;
      }
    }

    // Fallback: use the color name directly (works for CSS color names)
    return `--swatch-background: ${colorHandle};`;
  }

  /**
   * Render a generic range facet (price, height, width, etc.)
   * @param {Object} facetDef - Facet definition from config
   * @param {Object} stats - Facet statistics from Algolia (min, max, avg, sum)
   * @param {URLSearchParams} urlParams - Current URL parameters
   * @param {Object} settings - Global facet settings
   * @param {string} context - Container context for unique IDs (e.g., 'drawer', 'vertical')
   */
  renderRangeFacet(facetDef, stats, urlParams, settings = {}, context = 'vertical') {
    const attribute = facetDef.attribute;
    const urlParam = facetDef.urlParam || attribute;
    const label = facetDef.label || urlParam;
    const isPrice = attribute === 'price';

    // Initialize range cache if not exists
    if (!this.rangeFacetBounds) {
      this.rangeFacetBounds = {};
    }

    // Determine URL parameter names based on attribute type
    // Use SEO-friendly price.min/price.max for all range facets
    const minParamName = `${urlParam}.min`;
    const maxParamName = `${urlParam}.max`;

    // Check if THIS attribute currently has an active range filter
    const hasActiveRangeFilter = urlParams.has(minParamName) || urlParams.has(maxParamName);

    // Get min/max from stats - but CACHE the original full range
    // When filters are applied, Algolia returns stats for filtered results only
    // We want to keep showing the original full range so users can adjust their selection
    const statsMin = Math.floor(stats.min || 0);
    const statsMax = Math.ceil(stats.max || 1000);

    // Cache the bounds ONLY when no filter is active for this attribute
    // This ensures we capture the full unfiltered range
    // Also expand bounds if we see a WIDER range (user cleared filters)
    if (!this.rangeFacetBounds[attribute]) {
      // First time seeing this attribute - only cache if no active filter
      // If there's an active filter, we'll need to wait for unfiltered data
      if (!hasActiveRangeFilter) {
        this.rangeFacetBounds[attribute] = { min: statsMin, max: statsMax };
        this.log(`Cached range bounds for ${attribute}: ${statsMin} - ${statsMax}`);
      } else {
        // Page loaded with filter already set - use stats as temporary bounds
        // These will be expanded when user clears the filter
        this.rangeFacetBounds[attribute] = { min: statsMin, max: statsMax, isFiltered: true };
        this.log(`Temporary filtered bounds for ${attribute}: ${statsMin} - ${statsMax} (will expand when filter cleared)`);
      }
    } else {
      // Already have cached bounds - expand if we see wider range
      // This happens when user clears a filter and we get the full dataset
      if (statsMin < this.rangeFacetBounds[attribute].min) {
        this.rangeFacetBounds[attribute].min = statsMin;
        this.rangeFacetBounds[attribute].isFiltered = false;
        this.log(`Expanded ${attribute} min bound to ${statsMin}`);
      }
      if (statsMax > this.rangeFacetBounds[attribute].max) {
        this.rangeFacetBounds[attribute].max = statsMax;
        this.rangeFacetBounds[attribute].isFiltered = false;
        this.log(`Expanded ${attribute} max bound to ${statsMax}`);
      }
    }

    // Use cached bounds for the slider range
    const minValue = this.rangeFacetBounds[attribute].min;
    const maxValue = this.rangeFacetBounds[attribute].max;

    const currentMin = parseFloat(urlParams.get(minParamName)) || minValue;
    const currentMax = parseFloat(urlParams.get(maxParamName)) || maxValue;

    // Get unit/symbol for display
    let unit = '';
    let unitPosition = 'suffix'; // 'prefix' or 'suffix'
    if (isPrice) {
      const priceSettings = settings.priceRange || {};
      unit = priceSettings.currencySymbol || this.config.theme?.currency || '$';
      unitPosition = 'prefix';
    } else if (attribute.includes('height') || attribute.includes('width') || attribute.includes('length')) {
      unit = '"'; // inches
      unitPosition = 'suffix';
    }

    // Check if accordion should be expanded
    const dataIndex = `${urlParam}-range`;
    const hasActiveFilters = urlParams.has(minParamName) || urlParams.has(maxParamName);
    const isExpanded = this._resolveFacetExpanded(dataIndex, facetDef.expanded || hasActiveFilters || this._wasFacetOpenAtRender(dataIndex));

    // Transform prices to slider positions using weighted scale
    // This makes the slider more fluid when there are price outliers
    const minSliderPos = this.priceToSliderPosition(currentMin, minValue, maxValue);
    const maxSliderPos = this.priceToSliderPosition(currentMax, minValue, maxValue);

    // Calculate progress for range slider visual (using transformed positions)
    const lowerProgress = minSliderPos;
    const higherProgress = maxSliderPos;

    // Generate unique IDs for accessibility
    const uniqueId = `algolia-${context}-${urlParam}`;

    // Format display values with unit
    const formatValue = (val) => {
      if (unitPosition === 'prefix') {
        return `${unit}${val.toLocaleString()}`;
      }
      return `${val.toLocaleString()}${unit}`;
    };

    return `
      <details
        is="accordion-details"
        class="accordion-details facet-accordion-details"
        data-filter
        data-index="${dataIndex}"
        data-facet-key="${dataIndex}"
        data-facet-type="range"
        data-range-attribute="${attribute}"
        data-scale="${this.priceSliderScale}"
        ${isExpanded ? 'open' : ''}
      >
        <summary class="accordion-details__summary flex items-center justify-between">
          <span class="h6">${this.escapeHtml(label)}</span>
          <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-caret-down icon--small icon--thick" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
            <path d="M16.25 7.5L10 13.75L3.75 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </summary>
        <div class="accordion-details__content accordion-details__content--range">
          <!-- Use div instead of price-range to avoid native PriceRange custom element conflict -->
          <div class="grid algolia-range-facet" data-attribute="${attribute}" data-price-min="${minValue}" data-price-max="${maxValue}">
            <span class="text-subtext">Range: ${formatValue(minValue)} - ${formatValue(maxValue)}</span>
            <div class="facet-range-wrapper relative" style="--range-min: ${lowerProgress}%; --range-max: ${higherProgress}%;">
              <input class="facet-range facet-range__min w-full pointer-events-none" type="range" min="0" max="100" step="0.1" value="${minSliderPos}" data-price-min="${minValue}" data-price-max="${maxValue}" aria-label="${this.escapeAttr(label)} minimum value">
              <input class="facet-range facet-range__max w-full pointer-events-none absolute" type="range" min="0" max="100" step="0.1" value="${maxSliderPos}" data-price-min="${minValue}" data-price-max="${maxValue}" aria-label="${this.escapeAttr(label)} maximum value">
            </div>
            <div class="facet-input-wrapper flex items-center gap-4">
              <div class="flex items-center flex-grow relative gap-2">
                <label class="visually-hidden" for="${uniqueId}-min">From</label>
                ${unitPosition === 'prefix' ? `<span class="facet-range__symbol">${unit}</span>` : ''}
                <input class="form-control facet-range__input" type="number" inputmode="numeric" name="${minParamName}" id="${uniqueId}-min" autocomplete="off" placeholder="${minValue}" min="${minValue}" max="${maxValue}" value="${currentMin !== minValue ? currentMin : ''}">
                ${unitPosition === 'suffix' ? `<span class="facet-range__symbol">${unit}</span>` : ''}
              </div>
              <div class="flex items-center flex-grow relative gap-2">
                <label class="visually-hidden" for="${uniqueId}-max">To</label>
                ${unitPosition === 'prefix' ? `<span class="facet-range__symbol">${unit}</span>` : ''}
                <input class="form-control facet-range__input" type="number" inputmode="numeric" name="${maxParamName}" id="${uniqueId}-max" autocomplete="off" placeholder="${maxValue}" min="${minValue}" max="${maxValue}" value="${currentMax !== maxValue ? currentMax : ''}">
                ${unitPosition === 'suffix' ? `<span class="facet-range__symbol">${unit}</span>` : ''}
              </div>
            </div>
          </div>
        </div>
      </details>
    `;
  }

  /**
   * Render price range facet - matches native Shopify facets.liquid structure
   * @deprecated Use renderRangeFacet instead - kept for backwards compatibility
   * @param {Object} facetDef - Facet definition from config
   * @param {Object} priceStats - Price statistics from Algolia
   * @param {URLSearchParams} urlParams - Current URL parameters
   * @param {Object} settings - Global facet settings
   */
  renderPriceRangeFacet(facetDef, priceStats, urlParams, settings = {}) {
    // Delegate to the generic range facet renderer
    // NOTE: Original implementation removed - see git history for previous version
    return this.renderRangeFacet(facetDef, priceStats, urlParams, settings);
  }

  /**
   * Initialize interactions for dynamically rendered facets
   */
  initializeFacetInteractions(container) {
    // Initialize show-more buttons
    container.querySelectorAll('show-more-button button').forEach((btn) => {
      btn.addEventListener('click', () => {
        const details = btn.closest('.accordion-details__content');
        const hiddenItems = details.querySelectorAll('.show-more-item');
        const showMoreLabel = btn.querySelector('.label-show-more');
        const showLessLabel = btn.querySelector('.label-show-less');

        hiddenItems.forEach((item) => item.classList.toggle('hidden'));
        showMoreLabel.classList.toggle('hidden');
        showLessLabel.classList.toggle('hidden');
      });
    });

    // NOTE: We intentionally do NOT initialize the native price-range custom element
    // because it expects the slider min/max to match price min/max.
    // Our Algolia sliders use a 0-100 scale with price transformations, which conflicts
    // with the native PriceRange event handlers. Our event delegation in
    // setupFacetEventDelegation() handles the 0-100 scale with proper transformations.

    // NOTE: Checkbox and price input listeners are now handled via event delegation
    // in setupFacetEventDelegation() which is called once during init()

    this.log('Facet interactions initialized for container');

    // After rendering facets, sync tree checkbox states
    // This ensures children of checked parents are also checked
    // Safe to run on every re-render: only syncs if parent is CHECKED
    // (won't re-check children after user unchecks parent)
    this.syncTreeCheckboxStates(container);
  }

  /**
   * Sync tree checkbox states after facets are re-rendered
   * If a parent checkbox is checked, all its children should also be checked
   * Uses DOM traversal to find descendant checkboxes within each parent's tree item
   */
  syncTreeCheckboxStates(container) {
    try {
      // Find all checked parent checkboxes (those inside a parent tree item)
      const checkedParents = container.querySelectorAll('.facet-tree__item--parent > .facet-tree__row input[type="checkbox"]:checked');

      if (checkedParents.length === 0) {
        return; // No checked parents, nothing to sync
      }

      let anyNewlyChecked = false;

      checkedParents.forEach((parentCheckbox) => {
        // Find the parent tree item containing this checkbox
        const treeItem = parentCheckbox.closest('.facet-tree__item');
        if (!treeItem) return;

        // Find ALL descendant checkboxes within this tree item
        const descendantCheckboxes = treeItem.querySelectorAll('.facet-tree--nested input[type="checkbox"]');

        descendantCheckboxes.forEach((cb) => {
          if (!cb.checked) {
            cb.checked = true;
            anyNewlyChecked = true;
            this.log('Synced child:', cb.value, 'to checked (parent was checked)');
          }
        });
      });

      // If we checked any new descendants, update the URL to include them
      // But don't trigger a new search - we're already in a render cycle
      if (anyNewlyChecked) {
        this.log('Updating URL to include newly-checked descendants');
        // Collect all checked values and update URL silently
        this.updateURLWithCurrentFilters();
      }
    } catch (err) {
      console.error('[Algolia Collection] Error syncing tree checkbox states:', err);
    }
  }

  /**
   * Update URL with current filter state without triggering a new search
   * Used to sync URL after tree checkbox state changes
   */
  updateURLWithCurrentFilters() {
    const params = new URLSearchParams();
    const seenFilters = new Set();

    // Collect all checked checkboxes
    document.querySelectorAll('.algolia-facets-container').forEach((container) => {
      container.querySelectorAll('input[type="checkbox"]:checked').forEach((checkbox) => {
        if (checkbox.name && checkbox.value) {
          // Skip parent checkboxes - only include leaf values
          if (checkbox.dataset.leafValues) {
            return;
          }
          const key = `${checkbox.name}:${checkbox.value}`;
          if (!seenFilters.has(key)) {
            seenFilters.add(key);
            params.append(checkbox.name, checkbox.value);
          }
        }
      });
    });

    // Preserve sort_by and search query (q) parameters
    const currentUrlParams = new URLSearchParams(window.location.search);
    const currentSort = currentUrlParams.get('sort_by');
    if (currentSort) {
      params.set('sort_by', currentSort);
    }
    const currentQuery = currentUrlParams.get('q');
    if (currentQuery) {
      params.set('q', currentQuery);
    }

    // Update URL without triggering navigation
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    if (newUrl !== window.location.href) {
      window.history.replaceState({}, '', newUrl);
      this.log('URL updated silently to:', newUrl);
    }
  }

  /**
   * Set up event delegation for facet interactions
   * This is called ONCE during init() and handles all current and future facet elements
   */
  setupFacetEventDelegation() {
    const self = this;

    // Handle reset filters button click
    document.addEventListener('click', (e) => {
      const resetBtn = e.target.closest('.algolia-reset-filters');
      if (resetBtn) {
        e.preventDefault();
        e.stopPropagation();
        self.log('Reset filters clicked');
        self.clearAllFilters();
      }
    });

    // Handle tree facet toggle buttons (expand/collapse)
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('.facet-tree__toggle');
      if (toggle) {
        e.preventDefault();
        e.stopPropagation();
        const item = toggle.closest('.facet-tree__item');
        if (item) {
          item.classList.toggle('facet-tree__item--expanded');
        }
      }
    });

    // Use event delegation on document to handle dynamically rendered facets
    // This ensures clicks work even after facets are re-rendered
    document.addEventListener('change', (e) => {
      const target = e.target;
      // Delegated on document: e.target can be a non-Element (text/document node),
      // which has no .matches(). Guard so the listener no-ops instead of throwing
      // "target.matches is not a function". See SHOPIFY-1ZK.
      if (!(target instanceof Element)) return;

      // Check if this is an Algolia facet checkbox
      if (target.matches('.algolia-facets-container input[type="checkbox"]')) {
        e.preventDefault();
        e.stopPropagation();

        // Check if this is a tree parent checkbox with leaf values
        const leafValuesAttr = target.dataset.leafValues;
        if (leafValuesAttr) {
          // This is a parent node - handle multi-select of all children
          self.handleTreeParentChange(target, leafValuesAttr);
          return;
        }

        self.log('Algolia facet checkbox changed (delegated):', target.name, '=', target.value, 'checked:', target.checked);
        if (target.checked) {
          self.trackFilterClick(target.name, target.value);
        }
        // Pass the changed checkbox so we can sync matching checkboxes across containers first
        self.handleAlgoliaFacetChange(target);
        return;
      }

      // Check if this is an Algolia range input (price, height, width, etc.)
      // Matches: input[name*="price"], input[name$=".min"], input[name$=".max"]
      if (target.matches('.algolia-facets-container input[name*="price"]') ||
          target.matches('.algolia-facets-container input[name$=".min"]') ||
          target.matches('.algolia-facets-container input[name$=".max"]') ||
          target.matches('.algolia-facets-container .facet-range')) {
        e.preventDefault();
        e.stopPropagation();
        self.log('Algolia range input changed (delegated):', target.name, '=', target.value);
        self.handleAlgoliaFacetChange();
        return;
      }
    });

    // Handle range slider input events (for real-time visual feedback while sliding)
    // Throttled to one update per animation frame to avoid per-pixel style recalcs
    let rangeRafPending = false;
    document.addEventListener('input', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      // Check if this is a range slider within Algolia facets
      if (target.matches('.algolia-facets-container input[type="range"].facet-range')) {
        if (rangeRafPending) return;
        rangeRafPending = true;
        requestAnimationFrame(() => {
          rangeRafPending = false;
          updateRangeVisuals(target);
        });
      }
    }, { passive: true });

    function updateRangeVisuals(target) {
      const wrapper = target.closest('.facet-range-wrapper');
      if (!wrapper) return;
      const minSlider = wrapper.querySelector('.facet-range__min');
      const maxSlider = wrapper.querySelector('.facet-range__max');
      if (!minSlider || !maxSlider) return;

      let minPos = parseFloat(minSlider.value);
      let maxPos = parseFloat(maxSlider.value);

      if (target.classList.contains('facet-range__min') && minPos > maxPos) {
        minPos = maxPos;
        minSlider.value = minPos;
      } else if (target.classList.contains('facet-range__max') && maxPos < minPos) {
        maxPos = minPos;
        maxSlider.value = maxPos;
      }

      wrapper.style.setProperty('--range-min', `${minPos}%`);
      wrapper.style.setProperty('--range-max', `${maxPos}%`);

      const priceMin = parseFloat(minSlider.dataset.priceMin) || 0;
      const priceMax = parseFloat(minSlider.dataset.priceMax) || 1000;
      const currentPos = parseFloat(target.value);
      const actualPrice = self.sliderPositionToPrice(currentPos, priceMin, priceMax);

      const container = target.closest('.accordion-details__content');
      if (container) {
        const numberInputs = container.querySelectorAll('input[type="number"].facet-range__input');
        if (numberInputs.length >= 2) {
          if (target.classList.contains('facet-range__min')) {
            numberInputs[0].value = actualPrice;
          } else if (target.classList.contains('facet-range__max')) {
            numberInputs[1].value = actualPrice;
          }
        }
      }
    }

    // Debounced handler for when range slider stops (mouseup/touchend)
    // Store handler reference for cleanup
    this._rangeEndHandler = (e) => {
      const target = e.target;
      if (target.matches('.algolia-facets-container input[type="range"].facet-range')) {
        // Debounce to avoid multiple rapid calls - use instance property for cleanup
        clearTimeout(self._rangeDebounceTimer);
        self._rangeDebounceTimer = setTimeout(() => {
          self.log('Range slider released:', target.className, '=', target.value);
          self.handleAlgoliaFacetChange();
        }, 150);
      }
    };

    document.addEventListener('mouseup', this._rangeEndHandler);
    document.addEventListener('touchend', this._rangeEndHandler);

    // Set up Quick Add and Quick View button handlers
    this.setupProductCardActions();

    this.log('Facet event delegation set up');
  }

  /**
   * Set up event delegation for Quick Add and Quick View buttons on Algolia product cards
   * Uses event delegation since cards are dynamically rendered
   */
  setupProductCardActions() {
    const self = this;
    const productGrid = document.querySelector('.products-list');
    if (!productGrid) return;

    // Quick View button click handler (includes options count trigger)
    productGrid.addEventListener('click', (e) => {
      const quickViewBtn = e.target.closest('.product-card__quickview, [data-quickview-trigger]');
      if (quickViewBtn) {
        e.preventDefault();
        e.stopPropagation();
        const handle = quickViewBtn.dataset.productHandle;
        const quickViewType = quickViewBtn.dataset.quickViewType || 'drawer';
        self.log('Quick View clicked:', handle, 'type:', quickViewType);
        self.openQuickView(handle, quickViewType);
      }
    });

    // Quick Add button click handler (Add to Cart or Choose Options)
    productGrid.addEventListener('click', (e) => {
      const quickAddBtn = e.target.closest('[data-action="quick-add"]');
      if (quickAddBtn) {
        e.preventDefault();
        e.stopPropagation();
        const handle = quickAddBtn.dataset.productHandle;
        const variantId = quickAddBtn.dataset.variantId;
        self.log('Quick Add clicked:', handle, 'variant:', variantId);
        self.addToCart(variantId, quickAddBtn);
      }
    });

    // Choose Options button click handler (opens quick add modal for variants)
    productGrid.addEventListener('click', (e) => {
      const chooseOptionsBtn = e.target.closest('[data-action="quick-add-variants"]');
      if (chooseOptionsBtn) {
        e.preventDefault();
        e.stopPropagation();
        const handle = chooseOptionsBtn.dataset.productHandle;
        self.log('Choose Options clicked:', handle);
        self.openQuickAddModal(handle);
      }
    });

    this.log('Product card action handlers set up');
  }

  /**
   * Open Quick View modal/drawer for a product
   */
  openQuickView(handle, type) {
    const productUrl = `/products/${handle}`;

    // Get or create the shared Algolia quick view modal
    let modal = document.getElementById('AlgoliaQuickViewModal');

    if (!modal) {
      // Create the modal element if it doesn't exist
      const quickViewType = this.themeConfig.quickViewType || type || 'drawer';
      const colorScheme = this.themeConfig.overlayColorScheme || 'scheme-1';
      const isModal = quickViewType === 'modal';

      modal = document.createElement('quick-view-modal');
      modal.id = 'AlgoliaQuickViewModal';
      modal.className = `quick-view ${isModal ? 'modal modal--loading' : 'drawer drawer--right drawer--loading'}`;
      modal.hidden = true;

      // Use appropriate classes based on modal vs drawer mode
      const innerClass = isModal ? 'modal__inner' : 'drawer__inner';
      const closeClass = isModal ? 'modal__close-btn' : 'drawer__close-btn';
      const bodyClass = isModal ? 'modal__body' : 'drawer__body';
      const headerHtml = isModal ? '' : `
          <div class="drawer__header-title md:hidden">
            <h3>${this.themeConfig.quickViewTitle || 'Quick View'}</h3>
          </div>`;

      modal.innerHTML = `
        <div class="fixed-overlay" role="button" tabindex="-1" aria-label="Close" aria-controls="AlgoliaQuickViewModal"></div>
        <div class="${innerClass} flex flex-col color-${colorScheme}">${headerHtml}
          <button class="${closeClass} z-2" aria-controls="AlgoliaQuickViewModal" aria-label="Close">
            <svg class="icon icon--close icon--large" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M18 6L6 18M6 6l12 12"></path>
            </svg>
          </button>
          <div class="loading__spinner">
            <svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
              <circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
            </svg>
          </div>
          <div class="${bodyClass} v-scrollable h-full quick-view__content"></div>
        </div>
      `;
      document.body.appendChild(modal);
      this.log('Created Algolia quick view modal');
    }

    // Update the product URL and show the modal
    modal.dataset.productUrl = productUrl;
    modal.removeAttribute('hidden');

    // Trigger the modal's show method if available
    if (typeof modal.show === 'function') {
      modal.show();
    } else {
      // Fallback: manually trigger visibility
      modal.classList.add('is-visible');
      document.body.classList.add('overflow-hidden');
    }

    this.log('Opening quick view for:', handle);
  }

  /**
   * Open Quick Add modal for product with variants
   */
  openQuickAddModal(handle) {
    // Dispatch event for theme's quick add component to handle
    const event = new CustomEvent('algolia:quick-add', {
      detail: { handle },
      bubbles: true
    });
    document.dispatchEvent(event);

    // Fallback: Try to use theme's built-in quick add if it exists
    if (window.FoxTheme && window.FoxTheme.QuickAdd) {
      window.FoxTheme.QuickAdd.open(handle);
    } else {
      // If no quick add component, navigate to product page
      this.log('No quick add component found, navigating to product page');
      window.location.href = `/products/${handle}`;
    }
  }

  /**
   * Add a single variant to cart
   */
  async addToCart(variantId, button) {
    if (!variantId) {
      this.log('No variant ID provided for add to cart');
      return;
    }

    // Show loading state
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: 1
          }]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const data = await response.json();
      this.log('Added to cart:', data);

      // Show success state briefly
      button.textContent = 'Added!';
      button.classList.add('btn--success');

      // Dispatch cart update event for cart drawer/header to update
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));

      // Reset button after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.classList.remove('btn--success');
      }, 1500);

    } catch (error) {
      this.log('Error adding to cart:', error);
      button.textContent = 'Error';
      button.classList.add('btn--error');

      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.classList.remove('btn--error');
      }, 2000);
    }
  }

  /**
   * Handle tree parent checkbox change - selects/deselects ALL child values
   * Uses DOM traversal to find all descendant checkboxes within the tree item
   */
  handleTreeParentChange(checkbox, leafValuesAttr) {
    try {
      const isChecked = checkbox.checked;
      const name = checkbox.name;

      // Find the parent tree item (li.facet-tree__item) containing this checkbox
      const treeItem = checkbox.closest('.facet-tree__item');
      if (!treeItem) {
        this.log('Could not find parent tree item for checkbox');
        this.handleAlgoliaFacetChange();
        return;
      }

      // Find ALL descendant checkboxes within this tree item (children, grandchildren, etc.)
      const descendantCheckboxes = treeItem.querySelectorAll('.facet-tree--nested input[type="checkbox"]');
      this.log('Tree parent checkbox changed:', name, 'checked:', isChecked, 'descendants:', descendantCheckboxes.length);

      // Check/uncheck all descendants
      descendantCheckboxes.forEach((cb) => {
        cb.checked = isChecked;
      });

      // Also sync the same checkboxes in other containers (desktop/mobile drawer)
      // Collect all values that were changed
      const changedValues = [checkbox.value];
      descendantCheckboxes.forEach((cb) => changedValues.push(cb.value));

      // Sync across all containers
      document.querySelectorAll('.algolia-facets-container').forEach((container) => {
        if (container.contains(checkbox)) return; // Skip the container we just updated
        changedValues.forEach((value) => {
          const matchingCb = container.querySelector(`input[type="checkbox"][name="${name}"][value="${CSS.escape(value)}"]`);
          if (matchingCb) {
            matchingCb.checked = isChecked;
          }
        });
      });

      // Now trigger the facet change to update filters
      this.handleAlgoliaFacetChange();
    } catch (err) {
      console.error('[Algolia Collection] Error handling tree parent change:', err);
    }
  }

  /**
   * Handle facet change from Algolia-rendered facets
   * Collects all filter values from ALL containers and applies them
   * @param {HTMLInputElement} changedCheckbox - The checkbox that was just changed (optional)
   */
  handleAlgoliaFacetChange(changedCheckbox = null) {
    // IMPORTANT: If a checkbox was changed, sync its state to matching checkboxes
    // in other containers BEFORE collecting values. This ensures unchecking works
    // correctly when there are multiple containers (desktop + mobile drawer).
    if (changedCheckbox && changedCheckbox.type === 'checkbox') {
      const name = changedCheckbox.name;
      const value = changedCheckbox.value;
      const isChecked = changedCheckbox.checked;

      // Find and sync all matching checkboxes in other containers
      // Use direct value comparison to avoid CSS selector escaping issues with '>' and other special chars
      document.querySelectorAll(`.algolia-facets-container input[type="checkbox"][name="${name}"]`).forEach((cb) => {
        if (cb !== changedCheckbox && cb.value === value) {
          cb.checked = isChecked;
          this.log('Synced matching checkbox:', name, '=', value, 'to', isChecked);
        }
      });

      // If unchecking a child checkbox, also uncheck any parent checkboxes
      // Parent checkboxes have data-leaf-values containing their children
      // A checked parent implies ALL children are selected, so if any child is unchecked,
      // the parent should also be unchecked
      if (!isChecked) {
        document.querySelectorAll(`.algolia-facets-container input[type="checkbox"][name="${name}"][data-leaf-values]`).forEach((parentCheckbox) => {
          try {
            const leafValues = JSON.parse(parentCheckbox.dataset.leafValues);
            // If this parent's leaf values include the unchecked child, uncheck the parent
            if (leafValues.includes(value)) {
              parentCheckbox.checked = false;
              this.log('Unchecked parent checkbox because child was unchecked:', parentCheckbox.value);
            }
          } catch (_err) {
            // Ignore parse errors
          }
        });
      }
    }

    // Paint-first (INP): the synchronous work above is bounded to the one changed
    // facet (peer + parent sync). Dim the grid NOW so the next paint stops the INP
    // clock, then defer the expensive part — the O(all-facets) rescan of every
    // container, the URL write, the scroll, and the search — out of the
    // input→paint window. One requestAnimationFrame (~16ms) keeps the scroll
    // inside the layout-shift hadRecentInput window, so it stays CLS-excluded
    // (see scrollToProductGrid comment). Do NOT use setTimeout / defer >1 frame.
    this.showLoadingState();
    requestAnimationFrame(() => this._applyFacetChangeDeferred());
  }

  /**
   * Heavy facet-apply work, deferred one frame out of the INP window by
   * handleAlgoliaFacetChange. Rebuilds the filter params from every facet
   * container, updates the URL, scrolls to the grid, and triggers the search.
   */
  _applyFacetChangeDeferred() {
    const params = new URLSearchParams();

    // Track unique filter values to avoid duplicates from multiple containers
    const seenFilters = new Set();

    // Collect all checked checkboxes from ALL facet containers
    // Use Set to deduplicate since desktop and drawer may both have same filter checked
    document.querySelectorAll('.algolia-facets-container').forEach((container) => {
      container.querySelectorAll('input[type="checkbox"]:checked').forEach((checkbox) => {
        if (checkbox.name && checkbox.value) {
          const filterKey = `${checkbox.name}=${checkbox.value}`;
          if (!seenFilters.has(filterKey)) {
            seenFilters.add(filterKey);
            params.append(checkbox.name, checkbox.value);
          }
        }
      });

      // Collect range filter values (price, height, width, etc.)
      // Use set, not append, so no duplicates across containers
      // Collect from number inputs (which have the actual filter names)
      container.querySelectorAll('input[type="number"].facet-range__input').forEach((input) => {
        if (input.value && input.name) {
          params.set(input.name, input.value);
        }
      });

      // Also check legacy price inputs (without facet-range__input class)
      container.querySelectorAll('input[name*="price"]:not(.facet-range__input)').forEach((input) => {
        if (input.value && input.name) {
          params.set(input.name, input.value);
        }
      });
    });

    // Preserve sort_by
    const currentUrlParams = new URLSearchParams(window.location.search);
    const currentSort = currentUrlParams.get('sort_by');
    if (currentSort) {
      params.set('sort_by', currentSort);
    }

    // Preserve search query (q parameter)
    const currentQuery = currentUrlParams.get('q');
    if (currentQuery) {
      params.set('q', currentQuery);
    }

    this.log('Applying Algolia facet filters:', params.toString());

    // IMPORTANT: Update URL BEFORE triggering search
    // This ensures updateFacetCounts() reads the correct state when re-rendering facets
    const cleanUrl = new URL(window.location.href);
    cleanUrl.search = params.toString();
    history.replaceState({}, '', cleanUrl.toString());

    // Scroll IMMEDIATELY within the input event window. Previously this set
    // `_scrollOnNextRender = true` and let renderHits perform the scroll once
    // the chip strip height was accurate — but renderHits runs >500 ms after
    // the user click (network RTT + render), past the layout-shift API's
    // hadRecentInput exclusion window. cls-probe + Lighthouse both attributed
    // a 0.07+ CLS to this scroll because every viewport-relative position
    // changed when the page jumped to the grid top. Scrolling here, while the
    // input event is still considered "recent," makes the resulting position
    // changes input-attributed and excluded from CLS.
    //
    // Trade-off: the chip strip we measure is the OLD strip (pre-filter),
    // so a facet add lands the viewport ~30-50 px low, a facet remove lands
    // ~30-50 px high. That's well below noticeable for most users and a much
    // better deal than 0.07 CLS per click.
    this.scrollToProductGrid();
    // Prevent the deferred scroll in renderHits from double-scrolling when
    // applyFiltersFromParams resets the page (which would trigger pageChanged).
    this._scrollAlreadyDone = true;

    // Apply filters to Algolia (this triggers search and re-renders facets)
    this.applyFiltersFromParams(params);

    // Defer cross-container checkbox sync to the next animation frame.
    // syncFacetCheckboxes() walks ALL .algolia-facets-container checkboxes
    // (~396 on /collections/ceiling-fans) to sync state between desktop and
    // mobile drawer containers — but the sync isn't user-visible until the
    // user opens the alternate container, so it doesn't need to block this
    // input task. The clicked checkbox is already in its new state via
    // native browser behavior; same-name peer sync was already done a few
    // lines above (line 4430 area). The rAF deferral moves the ~40 ms walk
    // out of the input → next-paint window that INP measures, while still
    // completing well before any user could open the mobile drawer.
    //
    // INP probe on /collections/ceiling-fans (2026-05-06) attributed
    // ~40 ms of long-task time to algolia-collection-page.js during a
    // facet click. The dominant cost was this sync walk.
    requestAnimationFrame(() => this.syncFacetCheckboxes());
  }

  /**
   * Calculate dynamic scroll offset based on current header state
   * Measures actual header element heights for accurate positioning
   * This ensures scroll positioning works correctly across all devices and header states
   *
   * @returns {number} The calculated offset in pixels
   */
  calculateDynamicScrollOffset() {
    // Live reads (cheap classList lookups, no layout): the dynamic header state.
    const isHeaderPinned = document.body.classList.contains('header-pinned');
    const isNavHidden = document.body.classList.contains('is-hide-nav');

    // Prefer cached header/nav heights (refreshed off the input task by
    // _cacheScrollMeasurements). Fall back to a live read only before the cache
    // is first populated — avoids a getComputedStyle style-flush on every tap.
    let headerHeight = this._cachedHeaderHeight;
    let navHeight = this._cachedNavHeight;
    if (headerHeight == null) {
      const style = getComputedStyle(document.documentElement);
      headerHeight = parseFloat(style.getPropertyValue('--header-height')) || 0;
      navHeight = parseFloat(style.getPropertyValue('--header-navigation-height')) || 0;
    }
    navHeight = isNavHidden ? (navHeight || 0) : 0;

    // Visual buffer for comfortable spacing
    const buffer = 16;

    let offset;

    if (isHeaderPinned || headerHeight > 0) {
      if (isNavHidden && navHeight > 0) {
        // Header visible but nav hidden - subtract nav height
        offset = Math.max(0, headerHeight - navHeight) + buffer;
      } else {
        // Full header visible
        offset = headerHeight + buffer;
      }
    } else {
      // Header not visible - minimal offset needed
      offset = buffer;
    }

    return offset;
  }

  /**
   * Scroll the viewport so the page header, sticky toolbar, active-filter chip strip,
   * and first row of products are stacked at the top — used after pagination, filter
   * apply, sort change, and search input.
   *
   * Math: gridDocY (rect+scrollY, since offsetTop is offsetParent-relative and
   * underestimates Y under positioned ancestors) minus toolbar height minus
   * active-chip-strip height minus header offset (header/nav-state-aware).
   *
   * Uses behavior: 'auto' so the smooth scroll can't be cut short by post-render
   * layout shifts (B2B price enrichment, image lazy-load, etc.).
   */
  scrollToProductGrid() {
    const productGrid = document.getElementById('ProductGridContainer');
    if (!productGrid) return;

    const toolbar = document.getElementById('CollectionToolbar');
    const activeFiltersWrapper = document.querySelector('.facets-active-wrapper');

    const gridDocY = productGrid.getBoundingClientRect().top + window.scrollY;
    const toolbarHeight = toolbar ? (this._cachedToolbarHeight || toolbar.offsetHeight) : 0;
    const chipStripHeight = activeFiltersWrapper
      ? (this._cachedChipStripHeight != null ? this._cachedChipStripHeight : activeFiltersWrapper.offsetHeight)
      : 0;
    const headerOffset = this.calculateDynamicScrollOffset();

    const scrollTarget = Math.max(0, gridDocY - toolbarHeight - chipStripHeight - headerOffset);

    window.scrollTo({ top: scrollTarget, behavior: 'auto' });
    this.log('Scrolled to product grid', { gridDocY, toolbarHeight, chipStripHeight, headerOffset, scrollTarget });
  }

  /**
   * Sync checkbox states across all facet containers
   * Ensures desktop and mobile drawer facets stay in sync
   */
  syncFacetCheckboxes() {
    const params = new URLSearchParams(window.location.search);
    const activeFilters = {};

    // Build map of active filters - support both SEO-friendly and legacy params
    params.forEach((value, key) => {
      // Check if this is a mapped SEO-friendly param or a legacy filter.* param
      if (this.urlParamToAttribute?.[key] || key.startsWith('filter.')) {
        if (!activeFilters[key]) {
          activeFilters[key] = new Set();
        }
        activeFilters[key].add(value);
      }
    });

    // Update all checkboxes to match URL state
    document.querySelectorAll('.algolia-facets-container input[type="checkbox"]').forEach((checkbox) => {
      const values = activeFilters[checkbox.name];
      const shouldBeChecked = values && values.has(checkbox.value);
      if (checkbox.checked !== shouldBeChecked) {
        checkbox.checked = shouldBeChecked;
      }
    });
  }

  /**
   * Transform a price value to slider position (0-100 scale)
   * Uses non-linear scaling to handle price outliers better
   * @param {number} price - The actual price value
   * @param {number} min - Minimum price in range
   * @param {number} max - Maximum price in range
   * @returns {number} Position 0-100
   */
  priceToSliderPosition(price, min, max) {
    if (max === min) return 0;

    // Normalize price to 0-1 range
    const normalized = (price - min) / (max - min);

    switch (this.priceSliderScale) {
      case 'sqrt':
        // Square root scale - compresses high values
        // Makes it easier to select low-to-mid range prices
        return Math.sqrt(normalized) * 100;

      case 'log': {
        // Logarithmic scale - stronger compression
        // Good for extreme outliers (e.g., $10 to $100,000)
        // Add 1 to avoid log(0), then normalize
        const logNorm = Math.log1p(normalized * 100) / Math.log1p(100);
        return logNorm * 100;
      }

      case 'linear':
      default:
        return normalized * 100;
    }
  }

  /**
   * Transform slider position (0-100) back to price value
   * Inverse of priceToSliderPosition
   * @param {number} position - Slider position 0-100
   * @param {number} min - Minimum price in range
   * @param {number} max - Maximum price in range
   * @returns {number} Actual price value
   */
  sliderPositionToPrice(position, min, max) {
    if (max === min) return min;

    // Normalize position to 0-1
    const normalized = position / 100;

    let priceNormalized;
    switch (this.priceSliderScale) {
      case 'sqrt':
        // Inverse of sqrt is square
        priceNormalized = normalized * normalized;
        break;

      case 'log':
        // Inverse of log transformation
        priceNormalized = (Math.expm1(normalized * Math.log1p(100))) / 100;
        break;

      case 'linear':
      default:
        priceNormalized = normalized;
    }

    // Convert back to actual price
    return Math.round(priceNormalized * (max - min) + min);
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Escape string for use in HTML attributes (escapes quotes in addition to HTML entities)
   * Use this for value="..." attributes where double quotes need to be &quot;
   * Also escapes single quotes to &#39; for safety in all attribute contexts
   */
  escapeAttr(str) {
    if (!str) return '';
    return this.escapeHtml(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /**
   * Update price range filter bounds (legacy - kept for non-Algolia facets)
   */
  updatePriceRangeFacet(results) {
    // Get facet stats from raw results
    const stats = results._rawResults?.[0]?.facets_stats?.price;
    if (!stats) {
      this.log('No price facet stats available');
      return;
    }

    this.log('Price range stats:', stats);

    // Update the "highest price" display text if it exists
    const highestPriceEl = document.querySelector('.facet-accordion-details .text-subtext');
    if (highestPriceEl && highestPriceEl.textContent.includes('highest price')) {
      const formattedMax = this.formatMoney(stats.max * 100);
      highestPriceEl.textContent = `The highest price is ${formattedMax}`;
    }

    // Update range slider max values
    document.querySelectorAll('.facet-range').forEach((slider) => {
      if (slider.id?.includes('Price')) {
        slider.max = Math.ceil(stats.max);
      }
    });
  }

  /**
   * Format money value using theme settings
   */
  formatMoney(cents) {
    const moneyFormat = this.themeConfig.moneyFormat || '${{amount}}';
    const dollars = cents / 100;

    // Format with commas for thousands separator
    const amount = dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const amountNoDecimals = Math.round(dollars).toLocaleString('en-US');
    const amountWithCommaSeparator = dollars.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount_with_comma_separator}}', amountWithCommaSeparator);
  }

  /**
   * Update product count display
   * @param {number} count - Number of products
   */
  updateProductCount(count) {
    // Update all product count elements (there may be multiple)
    const countElements = document.querySelectorAll('#ProductCount, [data-product-count]');
    
    countElements.forEach((el) => {
      if (count === 0) {
        el.textContent = 'No products found';
      } else if (count === 1) {
        el.textContent = '1 product';
      } else {
        el.textContent = `${count.toLocaleString()} products`;
      }
    });

    this.log('Product count updated:', count);
  }

  /**
   * Update active filter badges based on current URL params
   */
  updateActiveFilters() {
    const params = new URLSearchParams(window.location.search);
    const activeFilters = [];
    let priceFilterAdded = false;

    // Collect all active filters (both SEO-friendly params and legacy filter.* params)
    params.forEach((value, key) => {
      // Check SEO-friendly params (category, brand, style, etc.) or legacy filter.* params
      const isSeoParam = this.urlParamToAttribute && this.urlParamToAttribute[key];
      const isLegacyParam = key.startsWith('filter.');

      if ((isSeoParam || isLegacyParam) && value) {
        // Handle price filters as a single combined filter (avoid duplicate tags)
        if (key === 'price.min' || key === 'price.max') {
          if (!priceFilterAdded) {
            priceFilterAdded = true;
            // Use 'price.min' as the key for removal (will clear both)
            activeFilters.push({ key: 'price.min', value, label: this.getFilterLabel(key, value) });
          }
        } else {
          activeFilters.push({ key, value, label: this.getFilterLabel(key, value) });
        }
      }
    });

    // Always update reset button visibility first
    this.updateFacetsHeaderVisibility(activeFilters.length > 0);

    // Update active filters badge container if it exists
    const container = this.dom.activeFilters;
    if (!container) {
      this.log('Active filters updated (no badge container):', activeFilters.length, 'filters');
      return;
    }

    // Build the HTML - Clear All button comes FIRST
    let html = '';

    // Add Clear All first if more than 1 filter
    if (activeFilters.length > 1) {
      const clearAllUrl = this.buildClearAllUrl();
      html += `<a href="${clearAllUrl}" class="facet-clear-all btn--link flex-shrink-0 whitespace-nowrap" is="facet-remove">Clear All</a>`;
    }

    activeFilters.forEach((filter) => {
      const removeUrl = this.buildRemoveFilterUrl(filter.key, filter.value);
      html += `
        <a class="facet-remove btn btn--secondary btn--small text-xs flex relative whitespace-nowrap flex-shrink-0" href="${removeUrl}" aria-label="Remove filter: ${this.escapeAttr(filter.label.valueLabel)}" is="facet-remove">
          <div class="btn__text flex items-center justify-between gap-2">
            <div class="facet-title flex items-center gap-2 whitespace-nowrap">
              <strong>${this.escapeHtml(filter.label.categoryName)}:</strong> ${this.escapeHtml(filter.label.valueLabel)}
            </div>
            <svg class="icon icon--extra-small flex-shrink-0" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.75 3.75L11.25 11.25M11.25 3.75L3.75 11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </a>`;
    });

    container.innerHTML = html;
    this.log('Active filters updated:', activeFilters.length, 'filters');
  }

  /**
   * Show/hide the reset button based on active filters
   */
  updateFacetsHeaderVisibility(hasActiveFilters) {
    const headers = document.querySelectorAll('.algolia-facets-header');
    headers.forEach(header => {
      header.style.display = hasActiveFilters ? '' : 'none';
    });
  }

  /**
   * Get human-readable label for a filter, including the filter category name
   * Returns object with { categoryName, valueLabel, fullLabel }
   */
  getFilterLabel(key, value) {
    // Price range special handling
    if (key === 'price.min' || key === 'price.max') {
      const params = new URLSearchParams(window.location.search);
      const min = params.get('price.min') || '0';
      const max = params.get('price.max');
      const valueLabel = max ? `$${min} - $${max}` : `$${min}+`;
      return { categoryName: 'Price', valueLabel, fullLabel: `Price: ${valueLabel}` };
    }

    // Get the category name from the key
    const categoryName = this.getFilterCategoryName(key);

    // Category/hierarchical paths - show only the last segment
    // e.g., "Architectural Lights > Functional Lights > Recessed Lights" becomes "Recessed Lights"
    let valueLabel = value;
    if (key === 'category' || key.includes('category') || value.includes(' > ')) {
      const parts = value.split(' > ');
      valueLabel = parts[parts.length - 1];
    }

    return { categoryName, valueLabel, fullLabel: `${categoryName}: ${valueLabel}` };
  }

  /**
   * Get the human-readable category name from a filter key
   */
  getFilterCategoryName(key) {
    // FIRST: Check the facet config for the label (this is the source of truth)
    const facetConfig = window.algoliaFacetConfig?.facets;
    if (facetConfig) {
      // Try to find facet by urlParam
      const facetByUrlParam = facetConfig.find(f => f.urlParam === key);
      if (facetByUrlParam?.label) {
        return facetByUrlParam.label;
      }
      // Try to find facet by attribute (for legacy filter.* params)
      if (key.startsWith('filter.')) {
        const parts = key.split('.');
        const lastPart = parts[parts.length - 1];
        const facetByAttr = facetConfig.find(f =>
          f.attribute?.includes(lastPart) ||
          f.urlParam === lastPart
        );
        if (facetByAttr?.label) {
          return facetByAttr.label;
        }
      }
    }

    // Human-readable label mappings for common filter types (fallback)
    const labelMappings = {
      'category': 'Category',
      'categories': 'Category',
      'categories_flat': 'Category',
      'color': 'Color',
      'size': 'Size',
      'brand': 'Brand',
      'vendor': 'Brand',
      'material': 'Material',
      'style': 'Style',
      'price': 'Price',
      'product_type': 'Type',
      'availability': 'Availability',
      'num-lights': 'Number of Lights',
      'num_lights': 'Number of Lights',
      'nav_num_lights': 'Number of Lights',
    };

    // SEO-friendly params: check if the URL param itself has a nice label first
    if (this.urlParamToAttribute && this.urlParamToAttribute[key]) {
      // Check if the URL param name has a direct mapping (e.g., "category" -> "Category")
      if (labelMappings[key]) {
        return labelMappings[key];
      }
      // Fall back to getFacetLabel with the attribute
      const attribute = this.urlParamToAttribute[key];
      return this.getFacetLabel(attribute);
    }

    // Legacy filter.* params: extract the attribute name
    if (key.startsWith('filter.')) {
      // Handle patterns like filter.v.option.color, filter.p.vendor, etc.
      const parts = key.split('.');
      const lastPart = parts[parts.length - 1];
      // Check label mappings first
      if (labelMappings[lastPart]) {
        return labelMappings[lastPart];
      }
      // Use getFacetLabel to get a nice display name
      return this.getFacetLabel(`options.${lastPart}`) || lastPart.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    // Handle meta.custom.* attributes (e.g., meta.custom.categories_flat)
    if (key.includes('meta.custom.') || key.includes('meta_custom_')) {
      const cleanKey = key.replace(/meta[._]custom[._]/gi, '').replace(/_/g, ' ').toLowerCase();
      // Check if we have a mapping for the cleaned key
      for (const [mapKey, label] of Object.entries(labelMappings)) {
        if (cleanKey.includes(mapKey.replace(/_/g, ' '))) {
          return label;
        }
      }
    }

    // Check direct key match in labelMappings
    const lowerKey = key.toLowerCase().replace(/_/g, ' ');
    for (const [mapKey, label] of Object.entries(labelMappings)) {
      if (lowerKey.includes(mapKey)) {
        return label;
      }
    }

    // Fallback: capitalize the key, but clean up ugly patterns
    return key
      .replace(/meta[._]custom[._]/gi, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  }

  /**
   * Build URL with a specific filter removed
   */
  buildRemoveFilterUrl(keyToRemove, valueToRemove) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    // For multi-value params, we need to check all values
    const allValues = params.getAll(keyToRemove);
    params.delete(keyToRemove);

    // Re-add all values except the one to remove
    allValues.forEach((v) => {
      if (v !== valueToRemove) {
        params.append(keyToRemove, v);
      }
    });

    // Special handling for price range - remove both min and max together
    if (keyToRemove.includes('price')) {
      params.delete('price.min');
      params.delete('price.max');
    }

    url.search = params.toString();
    return url.pathname + (url.search ? url.search : '');
  }

  /**
   * Build URL with all filters cleared (keep sort)
   */
  buildClearAllUrl() {
    const url = new URL(window.location.href);
    const sortBy = url.searchParams.get('sort_by');
    url.search = '';
    if (sortBy) {
      url.searchParams.set('sort_by', sortBy);
    }
    return url.pathname + (url.search ? url.search : '');
  }

  /**
   * Build URL with a specific facet's filters cleared
   * @param {string} shopifyParam - The URL parameter name (e.g., 'category', 'brand', 'filter.p.m.custom.style')
   */
  buildClearFacetUrl(shopifyParam) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    // Remove all values for this specific parameter
    params.delete(shopifyParam);

    // Also check for legacy filter.* format if using SEO-friendly params
    if (this.urlParamToAttribute && this.urlParamToAttribute[shopifyParam]) {
      const attribute = this.urlParamToAttribute[shopifyParam];
      // Remove any legacy params that might exist
      const legacyParam = `filter.${attribute}`;
      params.delete(legacyParam);
    }

    url.search = params.toString();
    return url.pathname + (url.search ? url.search : '');
  }

  /**
   * Get optimized image URL
   * Supports Shopify CDN resizing and Cloudflare Image Resizing for external URLs
   */
  getOptimizedImage(url, width) {
    if (!url) return '';

    // Shopify CDN images - use native resizing. Matches an existing
    // `_NNNxNNN` size suffix if present (so re-requesting at a different
    // width replaces it instead of appending a second, unrecognized one —
    // e.g. `_1200x1200_200x.jpg`, which the CDN can't parse) as well as the
    // no-suffix case.
    if (url.includes('cdn.shopify.com')) {
      return url.replace(/(_\d+x\d+)?(\.[^.]+)$/, `_${width}x$2`);
    }

    // External images - use Cloudflare Image Resizing if enabled
    const cfConfig = window.FoxTheme?.cloudflareConfig;
    if (cfConfig?.enabled && url.startsWith('https://')) {
      const quality = cfConfig.quality || 80;
      const format = cfConfig.format || 'auto';
      const options = `width=${width},quality=${quality},format=${format},fit=scale-down`;

      if (cfConfig.proxyDomain) {
        return `https://${cfConfig.proxyDomain}/cdn-cgi/image/${options}/${url}`;
      } else {
        // Use origin domain with /cdn-cgi/image/ path
        try {
          const urlObj = new URL(url);
          return `${urlObj.origin}/cdn-cgi/image/${options}${urlObj.pathname}`;
        } catch (_e) {
          return url;
        }
      }
    }

    return url;
  }

  /**
   * Builds a `srcset` string of `url widthw` pairs from getOptimizedImage.
   */
  getOptimizedImageSrcset(url, widths) {
    if (!url) return '';
    return widths.map((w) => `${this.getOptimizedImage(url, w)} ${w}w`).join(', ');
  }

  /**
   * `sizes` attribute matching the actual rendered card width, computed the
   * same way main-collection-product-grid.liquid derives its own image_sizes
   * (same settings, via window.algoliaConfig.grid) — so this stays correct
   * as merchants change column-count settings instead of drifting from a
   * hardcoded guess.
   */
  getGridImageSizes() {
    const grid = this.config?.grid;
    if (!grid) {
      return '(max-width: 767px) calc((100vw - 38px) / 2), (max-width: 1279px) calc((100vw - 90px) / 3), 450px';
    }
    const desktopSize = grid.enableFiltering
      ? `calc(${grid.pageWidth}px / ${grid.columnsDesktop + 1})`
      : `${Math.floor(grid.pageWidth / grid.columnsDesktop)}px`;
    return `(max-width: 767px) calc((100vw - 38px) / ${grid.columnsMobile}), ` +
      `(max-width: 1279px) calc((100vw - 90px) / ${grid.columnsTablet}), ${desktopSize}`;
  }

  /**
   * Numeric Shopify product ID from an Algolia hit.
   * objectID is namespaced ("shopify_us_123456789"); the numeric tail is what
   * Shopify's `id:` search term expects.
   * @param {Object} hit - Algolia search hit
   * @returns {string} - Numeric product ID, or '' if the hit carries neither field
   */
  getNumericProductId(hit) {
    let productId = hit?.id || hit?.objectID || '';
    if (typeof productId === 'string' && productId.includes('_')) {
      productId = productId.split('_').pop();
    }
    return String(productId);
  }

  /**
   * Enrich Algolia hits with real-time B2B prices from Shopify
   * Uses Section Rendering API to fetch prices that automatically include B2B catalog pricing
   * @param {Array} hits - Algolia search hits
   * @returns {Promise<void>} - Updates DOM after prices are fetched
   */
  async enrichWithB2BPrices(hits) {
    // Only enrich if B2B is enabled
    if (!this.config?.b2b?.enabled) {
      this.log('B2B not enabled, skipping price enrichment');
      return;
    }

    if (!hits || hits.length === 0) {
      return;
    }

    this.log('Starting B2B price enrichment for', hits.length, 'products');

    // Cache configuration
    const cacheKey = 'algolia_b2b_prices';
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

    // Check sessionStorage cache first (with TTL validation)
    let priceCache = {};
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const cacheData = JSON.parse(cached);
        const cacheAge = Date.now() - (cacheData.timestamp || 0);
        if (cacheAge < CACHE_TTL) {
          priceCache = cacheData.prices || {};
          this.log('Using cached prices, age:', Math.round(cacheAge / 1000), 'seconds');
        } else {
          this.log('Cache expired, fetching fresh prices');
        }
      }
    } catch (e) {
      this.log('Cache read error:', e);
    }

    // Filter hits that need price fetching (not in cache)
    const hitsToFetch = hits.filter(hit => !priceCache[hit.handle]);

    if (hitsToFetch.length === 0) {
      this.log('All prices found in cache');
      this.applyB2BPricesToDOM(priceCache);
      return;
    }

    this.log('Fetching B2B prices for', hitsToFetch.length, 'products');

    // Fetch a group of products in a single Section Rendering request.
    // Shopify's search endpoint is the only route that accepts a product set —
    // custom query params on ?section_id= are stripped, so a per-handle
    // product-page fetch is the only other way to reach this data.
    const fetchPriceGroup = async (groupHits) => {
      const terms = groupHits
        .map(hit => this.getNumericProductId(hit))
        .filter(Boolean)
        .map(id => `id:${id}`)
        .join(' OR ');
      if (!terms) return [];

      try {
        const rootUrl = window.Shopify?.routes?.root || '/';
        const url = `${rootUrl}search?section_id=algolia-price-fragment&type=product&q=${encodeURIComponent(terms)}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const html = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        // Key off the handle inside each payload — Shopify does not return
        // results in the order the `id:` terms were given.
        return Array.from(doc.querySelectorAll('script[data-algolia-price]'))
          .map(script => {
            try {
              const data = JSON.parse(script.textContent);
              return data?.handle ? { handle: data.handle, data } : null;
            } catch (e) {
              this.log('Malformed B2B price payload', e);
              return null;
            }
          });
      } catch (error) {
        this.log('Error fetching B2B price group', error);
        return [];
      }
    };

    // 50 per request — matches the `paginate ... by 50` in the section, which is
    // Liquid's documented per-page ceiling for search.results.
    const groupSize = 50;
    const groups = [];
    for (let i = 0; i < hitsToFetch.length; i += groupSize) {
      groups.push(hitsToFetch.slice(i, i + groupSize));
    }
    const results = (await Promise.all(groups.map(group => fetchPriceGroup(group)))).flat();

    // Update cache with new prices
    results.forEach(result => {
      if (result && result.data) {
        priceCache[result.handle] = result.data;
      }
    });

    // Save updated cache with timestamp
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({
        timestamp: Date.now(),
        prices: priceCache
      }));
    } catch (e) {
      this.log('Cache write error:', e);
    }

    // Apply B2B prices to DOM
    this.applyB2BPricesToDOM(priceCache);
  }

  /**
   * Apply B2B prices to already-rendered product cards
   * @param {Object} priceCache - Map of handle to price data
   */
  applyB2BPricesToDOM(priceCache) {
    const productCards = document.querySelectorAll('[data-product-handle]');

    productCards.forEach(card => {
      const handle = card.dataset.productHandle;
      const priceData = priceCache[handle];

      if (!priceData) return;

      // Find price elements within the card
      const priceContainer = card.querySelector('.product-card__price, .pcard__price, [data-product-price]');
      if (!priceContainer) return;

      // Format prices (matching theme's formatMoney)
      const formatPrice = (cents) => {
        if (!cents) return '';
        const amount = (cents / 100).toFixed(2);
        return '$' + amount;
      };

      // Build new price HTML
      let priceHtml = '';

      if (priceData.priceVaries) {
        // Price range
        priceHtml = `<span class="price-item price-item--sale">${formatPrice(priceData.priceMin)} - ${formatPrice(priceData.priceMax)}</span>`;
      } else if (priceData.onSale && priceData.compareAtPriceMin) {
        // Sale price with compare-at
        priceHtml = `
          <span class="price-item price-item--sale">${formatPrice(priceData.priceMin)}</span>
          <s class="price-item price-item--regular">${formatPrice(priceData.compareAtPriceMin)}</s>
        `;
        // Add sale badge if not already present
        const badge = card.querySelector('.pcard__badge-item--sale, .badge--sale');
        if (!badge) {
          const badgeContainer = card.querySelector('.pcard__badges');
          if (badgeContainer) {
            const saleBadge = document.createElement('span');
            saleBadge.className = 'pcard__badge-item pcard__badge-item--sale';
            saleBadge.textContent = `${priceData.savingsPercent}% off`;
            badgeContainer.appendChild(saleBadge);
          }
        }
      } else {
        // Regular price
        priceHtml = `<span class="price-item">${formatPrice(priceData.priceMin)}</span>`;
      }

      // Update the price container
      priceContainer.innerHTML = priceHtml;

      // Add B2B indicator if desired
      if (!card.querySelector('.b2b-price-indicator')) {
        const indicator = document.createElement('span');
        indicator.className = 'b2b-price-indicator text-xs text-subdued';
        indicator.textContent = 'Trade Price';
        indicator.style.display = 'block';
        priceContainer.appendChild(indicator);
      }

      this.log('Updated B2B price for', handle, priceData);
    });

    // Dispatch event for any external listeners
    document.dispatchEvent(new CustomEvent('algolia:b2bPricesApplied', {
      detail: { priceCache }
    }));
  }

  /**
   * Cleanup resources to prevent memory leaks
   * Call this when navigating away or destroying the component
   */
  destroy() {
    // Clear all timers
    clearTimeout(this._searchDebounceTimer);
    clearTimeout(this._rangeDebounceTimer);
    this._searchDebounceTimer = null;
    this._rangeDebounceTimer = null;

    // Disconnect ResizeObserver
    if (this._toolbarResizeObserver) {
      this._toolbarResizeObserver.disconnect();
      this._toolbarResizeObserver = null;
    }

    // Remove document event listeners
    if (this._rangeEndHandler) {
      document.removeEventListener('mouseup', this._rangeEndHandler);
      document.removeEventListener('touchend', this._rangeEndHandler);
      this._rangeEndHandler = null;
    }
    if (this._facetDrawerFlushHandler) {
      document.removeEventListener('drawer:handleAfterShow', this._facetDrawerFlushHandler);
      this._facetDrawerFlushHandler = null;
      this._facetDrawerFlushBound = false;
    }
    this._pendingFacetRenders = [];

    // Dispose InstantSearch instance if exists
    if (this.search) {
      try {
        this.search.dispose();
      } catch (_e) {
        // InstantSearch may already be disposed
      }
      this.search = null;
    }

    this.log('Algolia Collection Page destroyed');
  }

  /**
   * Debug logging
   */
  log(...args) {
    if (this.config?.debug) {
      // eslint-disable-next-line no-console
      console.log('[Algolia Collection]', ...args);
    }
  }
}

// Make class available globally (theme.liquid handles initialization)
window.AlgoliaCollectionPage = AlgoliaCollectionPage;
